import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AlertCircle,
  ArrowUp,
  Building,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Edit3,
  FileText,
  Link2,
  MapPin,
  Package,
  PackageSearch,
  Phone,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings,
  Tags,
  Trash2,
  Unlink,
  UserPlus,
  Wrench,
  X,
  SlidersHorizontal,
} from "lucide-react";

import {
  Alert,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";

import { useLocation, useNavigate } from "react-router-dom";

import NavbarUtility from "./NavbarUtility";

import BackOrder from "./BackOrder";

const API_URL = "https://dashboard.technoparts.it/ws/add_contatti.php";
const CONTATTI_API_URL = "https://dashboard.technoparts.it/ws/contatti.php";

// ─────────────────────────────────────────────────────────────────────────────

// DESIGN SYSTEM

// ─────────────────────────────────────────────────────────────────────────────

const BG_PAGE = "#121419";

const PANEL_BG = "#1d2026";

const FILTER_BOX_BG = "rgba(235, 181, 27, 0.05)";

const FILTER_BOX_BORDER = "rgba(235, 181, 27, 0.25)";

const GOLD = "#ebb51b";

const GOLD_DARK = "#a67a10";

const TEXT_DARK = "#f1f1ee";

const EMPTY_FORM = {
  codice_cliente: "",

  ragsoc: "",

  piva: "",

  tpcontatto: "",

  referente: "",

  email: "",

  pec: "",

  telefono: "",

  cellulare: "",

  indirizzo: "",

  localita: "",

  provincia: "",

  cap: "",

  regione: 0,

  id_nazione: 1,

  id_lingua: 3,

  id_cat_predefinito: "",

  is_cliente: true,

  is_fornitore: false,

  banca: "",

  iban: "",

  mpaga: "",

  sdi: "",

  fcons: "",

  noteCli: "",

  meccanica: false,

  carrozzeria: false,

  vetro: false,

  marchi: [],

  odp_pesare_merce: false,

  odp_film_trasparente: false,

  odp_origine_merci: null,

  odp_palette_fumegate: null,

  odp_carico_sfuso: null,

  odpv_inviare_packing_list: false,

  odpv_inserire_note_ordine: false,

  odpv_inviare_ddt: false,

  tipo_trasporto: "",

  ordina_non_disponibile: true,

  autorizza_ordini_normali: true,

  autorizza_ordini_express: false,

  autorizza_ordini_urgenti: true,

  scarica_stock: true,

  stock_visualizza_disp_attuale: true,

  stock_visualizza_disp_futura: false,

  fido_credit_safe: "0",

  assicurazione_SACE: "0",
};

const EMPTY_RUBRICA = {
  id_telefono_cont: null,

  interlocutore: "",

  qualifica: "",

  qualifica_custom: "",

  tipo: "E",

  num_riferimento: "",

  descrizione: "",

  notifiche: false,

  comunicazioni: false,

  invio_doc: false,

  valido: true,
};

const EMPTY_EXTRA = {
  quantita_targhe: "",

  perc_urgente: "",

  limite_ord_stock: "",

  cod_cli_ar: "",

  id_anagrafica: "",
};

const NOTE_TYPES = [
  {
    value: "UT",
    table: "utility.ut_cont_note",
    label: "UT - nota generale",
  },
  {
    value: "CLI",
    table: "DBA.clienti_base.note",
    label: "CLI - nota cliente",
  },
  { value: "C_1", table: "DBA.note_c", label: "C_1 - nota cliente" },
  { value: "C_s1", table: "DBA.note_c", label: "C_s1 - nota cliente" },
  {
    value: "C_ODP",
    table: "DBA.note_c",
    label: "C_ODP - nota cliente ODP",
  },
  {
    value: "C_ODPV",
    table: "DBA.note_c",
    label: "C_ODPV - nota cliente ODPV",
  },
  { value: "C_RIT", table: "DBA.note_c", label: "C_RIT - nota cliente RIT" },
  { value: "C_CR", table: "DBA.note_c", label: "C_CR - nota cliente CR" },
];

const noteTypeDisplayLabel = (value) => {
  const normalized = textValue(value).trim();
  return (
    NOTE_TYPES.find((item) => item.value === normalized)?.label ||
    normalized ||
    "UT - nota generale"
  );
};

const RUBRICA_QUALIFICA_OTHER = "__OTHER__";

const RUBRICA_QUALIFICA_OPTIONS = [
  "CEO",
  "Owner / Titolare",
  "General Manager",
  "Purchase",
  "Purchase Manager",
  "Buyer / Acquisti",
  "Seller / Commerciale",
  "Sales Manager",
  "Administration / Amministrazione",
  "Accounting / Contabilità",
  "Finance",
  "Logistics / Logistica",
  "Warehouse / Magazzino",
  "Technical / Tecnico",
  "IT",
  "Marketing",
  "HR / Risorse Umane",
  "Customer Service",
];

const toRubricaQualificaDraft = (value) => {
  const normalized = textValue(value).trim();
  if (!normalized) return { qualifica: "", qualifica_custom: "" };

  if (RUBRICA_QUALIFICA_OPTIONS.includes(normalized)) {
    return { qualifica: normalized, qualifica_custom: "" };
  }

  return {
    qualifica: RUBRICA_QUALIFICA_OTHER,
    qualifica_custom: normalized,
  };
};

const rubricaTipoIsWeb = (tipo, rows = []) => {
  const normalizedTipo = textValue(tipo).trim().toLowerCase();
  if (!normalizedTipo) return false;

  const matchingRow = normalizeArray(rows).find((row) =>
    [
      optionValue(row, "telefono_tipo", "tipo", "codice"),
      optionValue(row, "descrizione"),
    ].some((value) => textValue(value).trim().toLowerCase() === normalizedTipo),
  );

  const description = matchingRow
    ? optionValue(matchingRow, "descrizione")
    : "";

  return `${normalizedTipo} ${textValue(description).toLowerCase()}`.includes(
    "web",
  );
};

// ─────────────────────────────────────────────────────────────────────────────

// HELPERS

// ─────────────────────────────────────────────────────────────────────────────

const textValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const pick = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  }

  const lowerMap = Object.keys(obj).reduce((acc, key) => {
    acc[key.toLowerCase()] = key;

    return acc;
  }, {});

  for (const key of keys) {
    const realKey = lowerMap[String(key).toLowerCase()];

    if (realKey !== undefined) return obj[realKey];
  }

  return undefined;
};

const boolValue = (value, fallback = false) => {
  if (value === null || value === undefined || value === "") return fallback;

  if (typeof value === "boolean") return value;

  if (typeof value === "number") return value === 1;

  const normalized = String(value).trim().toLowerCase();

  return ["1", "true", "s", "si", "sì", "yes", "y", "on", "valido"].includes(
    normalized,
  );
};

const nullableBoolValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  return boolValue(value, false);
};

const numberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;

  const n = Number(value);

  return Number.isNaN(n) ? null : n;
};

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const stripRtf = (value) => {
  const raw = textValue(value);

  if (!raw) return "";

  return raw

    .replace(/\\par\b ?/gi, "\n")

    .replace(/\\'[0-9a-fA-F]{2}/g, "")

    .replace(/\\[a-zA-Z]+-?\d* ?/g, "")

    .replace(/[{}]/g, "")

    .replace(/\n{3,}/g, "\n\n")

    .trim();
};

const NOTE_TITLE_PREFIX = "TITOLO: ";
const NOTE_FIERA_PREFIX = "FIERA: ";
const NOTE_FIERA_ID_PREFIX = "FIERA_ID: ";
const NOTE_DONE_PREFIX = "FATTO: ";

const buildStoredNote = (title, body, fiera = "", fieraId = "", done = false) => {
  const cleanTitle = textValue(title).replace(/\r?\n/g, " ").trim();
  const cleanBody = textValue(body).trim();
  const cleanFiera = textValue(fiera).replace(/\r?\n/g, " ").trim();
  const cleanFieraId = textValue(fieraId).replace(/\r?\n/g, " ").trim();

  const metadata = [
    `${NOTE_TITLE_PREFIX}${cleanTitle}`,
    `${NOTE_DONE_PREFIX}${done ? "1" : "0"}`,
  ];
  if (cleanFiera) metadata.push(`${NOTE_FIERA_PREFIX}${cleanFiera}`);
  if (cleanFieraId) metadata.push(`${NOTE_FIERA_ID_PREFIX}${cleanFieraId}`);

  return `${metadata.join("\n")}\n${cleanBody}`;
};

const parseStoredNote = (value) => {
  let raw = stripRtf(value);
  if (!raw) {
    return { title: "Nota senza titolo", fiera: "", fieraId: "", done: false, body: "" };
  }

  // Alcuni campi storici arrivano dal DB come RTF e, dopo la pulizia,
  // possono lasciare davanti al testo il nome del font (es. Microsoft Sans Serif;).
  // Se troviamo il nostro marcatore TITOLO lo consideriamo sempre l'inizio reale.
  const titleMarkerIndex = raw.toUpperCase().indexOf(NOTE_TITLE_PREFIX);
  if (titleMarkerIndex > 0 && titleMarkerIndex < 250) {
    raw = raw.substring(titleMarkerIndex);
  }

  raw = raw
    .replace(
      /^(?:Microsoft Sans Serif|Arial|Calibri|Tahoma|Verdana|Segoe UI|Times New Roman)\s*;\s*/i,
      "",
    )
    // Se il DB/RTF ha appiattito gli a-capo dei metadati, li ricreiamo.
    .replace(/\s+(?=FIERA_ID:\s*)/gi, "\n")
    .replace(/\s+(?=FIERA:\s*)/gi, "\n")
    .replace(/\s+(?=FATTO:\s*)/gi, "\n")
    .trim();

  const lines = raw.split(/\r?\n/);
  const firstLine = textValue(lines[0]).trim();

  if (firstLine.toUpperCase().startsWith(NOTE_TITLE_PREFIX)) {
    const title = firstLine.substring(NOTE_TITLE_PREFIX.length).trim();
    let cursor = 1;
    let fiera = "";
    let fieraId = "";
    let done = false;

    while (cursor < lines.length) {
      const line = textValue(lines[cursor]).trim();
      const upper = line.toUpperCase();

      if (upper.startsWith(NOTE_DONE_PREFIX)) {
        done = line.substring(NOTE_DONE_PREFIX.length).trim() === "1";
        cursor += 1;
        continue;
      }

      // Prima FIERA_ID, così possiamo recuperare anche eventuale testo nota
      // rimasto sulla stessa riga dopo l'id numerico.
      if (upper.startsWith(NOTE_FIERA_ID_PREFIX)) {
        const idPayload = line.substring(NOTE_FIERA_ID_PREFIX.length).trim();
        const idMatch = idPayload.match(/^(\d+)(?:\s+(.*))?$/);
        fieraId = idMatch ? idMatch[1] : idPayload;

        if (idMatch?.[2]) {
          lines[cursor] = idMatch[2].trim();
          break;
        }

        cursor += 1;
        continue;
      }

      if (upper.startsWith(NOTE_FIERA_PREFIX)) {
        fiera = line.substring(NOTE_FIERA_PREFIX.length).trim();
        cursor += 1;
        continue;
      }

      break;
    }

    return {
      title: title || "Nota senza titolo",
      fiera,
      fieraId,
      done,
      body: lines.slice(cursor).join("\n").trim(),
    };
  }

  const rest = lines.slice(1).join("\n").trim();
  const fallbackTitle = firstLine || "Nota senza titolo";
  return {
    title: fallbackTitle,
    fiera: "",
    fieraId: "",
    done: false,
    body: rest || raw,
  };
};

const formatItalianDateTime = (value) => {
  const raw = textValue(value).trim();
  if (!raw) return "—";

  const sqlDate = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/,
  );

  if (sqlDate) {
    const [, year, month, day, hour, minute] = sqlDate;
    return `${day}/${month}/${year}${hour && minute ? ` ${hour}:${minute}` : ""}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;

  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const optionValue = (row, ...keys) => textValue(pick(row, ...keys));

const fieraDisplayLabel = (row) =>
  optionValue(row, "descrizione", "fiera", "nome", "denominazione") ||
  optionValue(row, "id_fiera");

const fieraMatchesYear = (row, year) => {
  const needle = String(year);
  return Object.values(row || {}).some((value) =>
    textValue(value).includes(needle),
  );
};

const api = async (action, payload = {}) => {
  const response = await fetch(API_URL, {
    method: "POST",

    headers: {
      "Content-Type": "application/json",

      Accept: "application/json",
    },

    body: JSON.stringify({ action, ...payload }),
  });

  const raw = await response.text();

  let data;

  try {
    data = raw ? JSON.parse(raw) : {};
  } catch (error) {
    throw new Error(`Il PHP non ha restituito JSON valido: ${raw}`);
  }

  if (!response.ok || data?.success === false) {
    throw new Error(
      data?.message || data?.error || `Errore HTTP ${response.status}`,
    );
  }

  return data;
};

  const moneyValue = (value) => {
  if (value === null || value === undefined || value === "") return "0.00";

  const normalized = String(value).replace(",", ".");
  const number = Number(normalized);

  if (Number.isNaN(number)) return "0.00";

  return number.toFixed(2);
};

const hydrateFormFromContact = (contact) => ({
  ...EMPTY_FORM,

  codice_cliente: textValue(
    pick(contact, "codice_cliente", "cod_cliente", "codice_cli", "id_cliente"),
  ),

  ragsoc: textValue(pick(contact, "ragione_sociale")),

  piva: textValue(pick(contact, "par_iva")),

  tpcontatto: textValue(pick(contact, "tipo_contatto")),

  indirizzo: textValue(pick(contact, "indirizzo")),

  localita: textValue(pick(contact, "localita")),

  provincia: textValue(pick(contact, "provincia")),

  cap: textValue(pick(contact, "cap")),

  regione: numberOrNull(pick(contact, "id_regione")) ?? 0,

  id_nazione: numberOrNull(pick(contact, "id_nazione")) ?? 1,

  id_lingua: numberOrNull(pick(contact, "id_lingua")) ?? 1,

  id_cat_predefinito: textValue(pick(contact, "id_cat_predefinito")),

  is_cliente: boolValue(pick(contact, "is_cliente"), true),

  is_fornitore: boolValue(pick(contact, "is_fornitore"), false),

  meccanica: boolValue(pick(contact, "tratta_meccanica")),

  carrozzeria: boolValue(pick(contact, "tratta_carrozzeria")),

  vetro: boolValue(pick(contact, "tratta_vetro")),

  odp_pesare_merce: boolValue(pick(contact, "odp_pesare_merce")),

  odp_film_trasparente: boolValue(pick(contact, "odp_film_trasparente")),

  odp_origine_merci: nullableBoolValue(pick(contact, "odp_origine_merci")),

  odp_palette_fumegate: nullableBoolValue(
    pick(contact, "odp_palette_fumegate"),
  ),

  odp_carico_sfuso: nullableBoolValue(pick(contact, "odp_carico_sfuso")),

  odpv_inviare_packing_list: boolValue(
    pick(contact, "ODPV_inviare_packing_list", "odpv_inviare_packing_list"),
  ),

  odpv_inserire_note_ordine: boolValue(
    pick(contact, "ODPV_inserire_note_ordine", "odpv_inserire_note_ordine"),
  ),

  odpv_inviare_ddt: boolValue(
    pick(contact, "ODPV_inviareDDT", "odpv_inviareddt", "odpv_inviare_ddt"),
  ),

  tipo_trasporto: textValue(pick(contact, "tipo_trasporto")),

  ordina_non_disponibile: boolValue(
    pick(contact, "ordina_non_disponibile"),

    true,
  ),

  autorizza_ordini_normali: boolValue(
    pick(contact, "autorizza_ordini_normali"),

    true,
  ),

  autorizza_ordini_express: boolValue(
    pick(contact, "autorizza_ordini_express"),
  ),

  autorizza_ordini_urgenti: boolValue(
    pick(contact, "autorizza_ordini_urgenti"),

    true,
  ),

  scarica_stock: boolValue(pick(contact, "scarica_stock"), true),

  stock_visualizza_disp_attuale: boolValue(
    pick(contact, "stock_visualizza_disp_attuale"),

    true,
  ),

  stock_visualizza_disp_futura: boolValue(
    pick(contact, "stock_visualizza_disp_futura"),
  ),

fido_credit_safe: moneyValue(
  pick(contact, "fido_credit_safe")
),

assicurazione_SACE: moneyValue(
  pick(contact, "assicurazione_SACE")
),
});

const extractContactFromApiResult = (result) => {
  const candidates = [
    result?.data,
    result?.contact,
    result?.contatto,
    result?.CONTATTO,
    result,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const first = candidate.find(
        (item) => item && typeof item === "object" && !Array.isArray(item),
      );

      if (first) return first;
      continue;
    }

    if (
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      (pick(candidate, "id_contatto") !== undefined ||
        pick(candidate, "ragione_sociale") !== undefined)
    ) {
      return candidate;
    }
  }

  return null;
};

const buildContactPayload = (formData) => ({
  ...formData,

  regione: Number(formData.regione) || 0,

  id_nazione: numberOrNull(formData.id_nazione),

  id_lingua: numberOrNull(formData.id_lingua),

  id_cat_predefinito: numberOrNull(formData.id_cat_predefinito),

  fido_credit_safe:
    formData.fido_credit_safe === "" ? "0" : formData.fido_credit_safe,

  assicurazione_SACE:
    formData.assicurazione_SACE === "" ? "0" : formData.assicurazione_SACE,
});

const TABS = [
  { id: "anagrafica", label: "Anagrafica", icon: Building },
  { id: "fiere", label: "Fiere", icon: CalendarDays },
  { id: "marchi", label: "Marchi", icon: Tags },
  { id: "rubrica", label: "Rubrica", icon: Phone },
  { id: "note", label: "Note", icon: FileText },
  { id: "backorder", label: "Backorder", icon: PackageSearch },
];

const OE_PRECODICI = new Set([
  "007",

  "009",

  "013",

  "015",

  "017",

  "020",

  "026",

  "031",

  "043",

  "050",

  "051",

  "052",

  "053",

  "054",

  "055",

  "063",

  "069",

  "075",

  "080",

  "090",

  "091",

  "142",

  "143",

  "200",

  "488",
]);

const AddContatti = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState(EMPTY_FORM);

  const [currentId, setCurrentId] = useState(null);

  const [currentContact, setCurrentContact] = useState(null);

  const [activeTab, setActiveTab] = useState("anagrafica");

  const [search, setSearch] = useState("");

  const [contacts, setContacts] = useState([]);

  const [searching, setSearching] = useState(false);

  const [loading, setLoading] = useState(false);

  const [busyAction, setBusyAction] = useState("");

  const [error, setError] = useState("");

  const [successMsg, setSuccessMsg] = useState("");

  const [marchioInput, setMarchioInput] = useState("");

  const [lookups, setLookups] = useState({
    tipiContatto: [],

    nazioni: [],

    lingue: [],

    province: [],

    tipiTrasporto: [],

    fiere: [],

    marchi: [],

    rubricaTipi: [],

    stockPredefiniti: [],
  });

  const [fiere, setFiere] = useState([]);

  const [selectedFiera, setSelectedFiera] = useState("");

  const [newFieraNote, setNewFieraNote] = useState("");

  const [selectedFieraNote, setSelectedFieraNote] = useState(null);

  const [fieraNoteDraft, setFieraNoteDraft] = useState("");

  const [marchiRichiesti, setMarchiRichiesti] = useState([]);

  const [marchiForniti, setMarchiForniti] = useState([]);

  const [marchiFornitiPending, setMarchiFornitiPending] = useState([]);

  const [marchioRichiestoAdd, setMarchioRichiestoAdd] = useState("");

  const [marchioFornitoAdd, setMarchioFornitoAdd] = useState("");

  const [rubrica, setRubrica] = useState([]);

  const [rubricaType, setRubricaType] = useState("contatto");

  const [rubricaDraft, setRubricaDraft] = useState(EMPTY_RUBRICA);

  const [rubricaPending, setRubricaPending] = useState([]);

  const [rubricaPendingEditIndex, setRubricaPendingEditIndex] = useState(null);

  const searchRequestRef = useRef(0);

  // Riferimenti DOM usati per mantenere la lista a destra alta esattamente

  // quanto il relativo editor a sinistra (desktop), lasciando lo scroll interno.

  const fiereEditorRef = useRef(null);

  const fiereListRef = useRef(null);

  const rubricaEditorRef = useRef(null);

  const rubricaListRef = useRef(null);

  const noteEditorRef = useRef(null);

  const noteListRef = useRef(null);

  const [stock, setStock] = useState([]);

  const [note, setNote] = useState([]);

  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [newNoteTitleType, setNewNoteTitleType] = useState("free");

  const [newNoteFiera, setNewNoteFiera] = useState("");

  const [newNote, setNewNote] = useState("");

  const [newNoteType, setNewNoteType] = useState("UT");

  const [noteSearch, setNoteSearch] = useState("");

  const [noteFieraFilter, setNoteFieraFilter] = useState("");

const [noteStatusFilter, setNoteStatusFilter] = useState("all");

  const [editingNote, setEditingNote] = useState(null);
  const [editNoteTitle, setEditNoteTitle] = useState("");
  const [editNoteBody, setEditNoteBody] = useState("");
  const [editNoteFiera, setEditNoteFiera] = useState("");

  const [selectedNote, setSelectedNote] = useState(null);

  const [noteModalOpen, setNoteModalOpen] = useState(false);

  const [fieraCreateModalOpen, setFieraCreateModalOpen] = useState(false);
  const [newFieraDescrizione, setNewFieraDescrizione] = useState("");
  const [fieraCreateBusy, setFieraCreateBusy] = useState(false);

  // Duplicazione di un contatto esistente durante la creazione di un NUOVO contatto.
  // Copia solo i parametri/anagrafica operativa; NON copia l'identità del contatto,
  // collegamenti Infinity, fiere, rubrica, note, stock o altri record collegati.
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateSearch, setDuplicateSearch] = useState("");
  const [duplicateResults, setDuplicateResults] = useState([]);
  const [duplicateSearching, setDuplicateSearching] = useState(false);
  const [duplicateBusy, setDuplicateBusy] = useState(false);
  const duplicateSearchRequestRef = useRef(0);

  // Creazione / collegamento anagrafica Infinity (porting del flusso desktop).
  const [infinityModalOpen, setInfinityModalOpen] = useState(false);
  const [infinityBusy, setInfinityBusy] = useState(false);
  const [infinityStep, setInfinityStep] = useState("setup");
  const [infinityPrecheck, setInfinityPrecheck] = useState(null);
  const [infinityOptions, setInfinityOptions] = useState({
    tipi_cliente: [],
    zone: [],
    pagamenti: [],
  });
  const [infinityForm, setInfinityForm] = useState({
    tipo_cliente: "",
    zona: "",
    id_pagamento: "",
    id_nazione: "",
    provincia: "",
    localita: "",
    cap: "",
    indirizzo: "",
  });
  const [infinityCreatedId, setInfinityCreatedId] = useState(null);
  const [infinityMergeData, setInfinityMergeData] = useState({
    utility: [],
    infinity: [],
  });

  const [extra, setExtra] = useState(EMPTY_EXTRA);

  const isExisting = Boolean(currentId);

  const isBusy = loading || Boolean(busyAction) || infinityBusy || duplicateBusy;

  const currentYear = new Date().getFullYear();

  const availableFiereForDropdown = useMemo(
    () =>
      currentId
        ? lookups.fiere
        : lookups.fiere.filter((row) => fieraMatchesYear(row, currentYear)),
    [currentId, currentYear, lookups.fiere],
  );

  const selectedNoteFieraRow = useMemo(
    () =>
      lookups.fiere.find(
        (row) =>
          textValue(optionValue(row, "id_fiera")) === textValue(newNoteFiera),
      ) || null,
    [lookups.fiere, newNoteFiera],
  );

  const selectedNoteFieraLabel = selectedNoteFieraRow
    ? fieraDisplayLabel(selectedNoteFieraRow)
    : "";

  const resolveRubricaQualifica = (draft) =>
    draft.qualifica === RUBRICA_QUALIFICA_OTHER
      ? textValue(draft.qualifica_custom).trim()
      : textValue(draft.qualifica).trim();

  const resolveRubricaInterlocutore = (draft) => {
    const interlocutore = textValue(draft.interlocutore).trim();
    if (interlocutore) return interlocutore;

    return rubricaTipoIsWeb(draft.tipo, lookups.rubricaTipi)
      ? formData.ragsoc.trim()
      : "";
  };

  const notifyError = useCallback((message) => {
    setError(message || "Errore durante l'operazione.");

    setSuccessMsg("");
  }, []);

  const notifySuccess = useCallback((message) => {
    setSuccessMsg(message || "Operazione completata.");

    setError("");
  }, []);

  useEffect(() => {
    if (!successMsg) return undefined;

    const timer = window.setTimeout(() => {
      setSuccessMsg("");
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [successMsg]);

  const isItalyNation = useMemo(() => {
    const selectedId = textValue(formData.id_nazione).trim();

    const selectedNation = lookups.nazioni.find(
      (row) => textValue(optionValue(row, "id_nazione")).trim() === selectedId,
    );

    const descrizione = textValue(
      optionValue(selectedNation, "descrizione", "nazione"),
    )
      .trim()
      .toLowerCase();

    const iso = textValue(
      optionValue(selectedNation, "codice_iso", "iso", "codice"),
    )
      .trim()
      .toUpperCase();

    return (
      Number(formData.id_nazione) === 1 ||
      descrizione === "italia" ||
      descrizione === "italy" ||
      iso === "IT" ||
      iso === "ITA"
    );
  }, [formData.id_nazione, lookups.nazioni]);

  const infinityNationRow = useMemo(
    () =>
      lookups.nazioni.find(
        (row) =>
          textValue(optionValue(row, "id_nazione")).trim() ===
          textValue(infinityForm.id_nazione).trim(),
      ) || null,
    [infinityForm.id_nazione, lookups.nazioni],
  );

  const infinityNationLabel =
    optionValue(infinityNationRow, "descrizione", "nazione", "nome") || "—";

  const infinityNationIso =
    optionValue(infinityNationRow, "codice_iso", "iso", "codice") || "";

  const noteTitleOptions = [
  {
    value: "meeting",
    label: "Riunione presso Technoparts",
  },
  {
    value: "fair",
    label: "Fiera",
  },
  {
    value: "call",
    label: `Call con ${formData.ragsoc || "cliente"}`,
  },
  {
    value: "free",
    label: "Titolo libero",
  },
];

const getNewNoteTitle = () => {
  switch (newNoteTitleType) {
    case "meeting":
      return "Riunione presso Technoparts";

    case "fair":
      return selectedNoteFieraRow
        ? `Fiera - ${fieraDisplayLabel(selectedNoteFieraRow)}`
        : "";

    case "call":
      return `Call con ${formData.ragsoc || "cliente"}`;

    case "free":
    default:
      return newNoteTitle.trim() || "Nota";
  }
};

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "id_nazione") {
        const selectedNation = lookups.nazioni.find(
          (row) =>
            textValue(optionValue(row, "id_nazione")).trim() ===
            textValue(value).trim(),
        );

        const descrizione = textValue(
          optionValue(selectedNation, "descrizione", "nazione"),
        )
          .trim()
          .toLowerCase();

        const iso = textValue(
          optionValue(selectedNation, "codice_iso", "iso", "codice"),
        )
          .trim()
          .toUpperCase();

        const isItaly =
          Number(value) === 1 ||
          descrizione === "italia" ||
          descrizione === "italy" ||
          iso === "IT" ||
          iso === "ITA";

        if (!isItaly) next.provincia = "";
      }

      return next;
    });
  };

  const handleNullableBool = (name, value) => {
    setFormData((prev) => ({
      ...prev,

      [name]: value === "" ? null : value === "1",
    }));
  };

  const handleAddInitialMarchio = () => {
    const value = marchioInput.trim().toUpperCase();

    if (!value) return;

    setFormData((prev) => ({
      ...prev,

      marchi: prev.marchi.includes(value)
        ? prev.marchi
        : [...prev.marchi, value],
    }));

    setMarchioInput("");
  };

  const handleRemoveInitialMarchio = (value) => {
    setFormData((prev) => ({
      ...prev,

      marchi: prev.marchi.filter((item) => item !== value),
    }));
  };

  const handleInitialMarchioKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      handleAddInitialMarchio();
    }
  };

  const lookupErrors = useMemo(() => [], []);

  const loadLookups = useCallback(async () => {
    const actions = [
      ["tipiContatto", "tipi_contatto"],

      ["nazioni", "nazioni"],

      ["lingue", "lingue"],

      ["province", "province"],

      ["tipiTrasporto", "tipi_trasporto"],

      ["fiere", "fiere_list"],

      ["marchi", "marchi_list"],

      ["rubricaTipi", "rubrica_tipi"],

      ["stockPredefiniti", "stock_predefiniti"],
    ];

    const settled = await Promise.allSettled(
      actions.map(([, action]) => api(action)),
    );

    const next = {};

    settled.forEach((result, index) => {
      const [key] = actions[index];

      next[key] =
        result.status === "fulfilled" ? normalizeArray(result.value?.data) : [];

      if (result.status === "rejected") {
        console.warn(
          `Lookup ${actions[index][1]} non caricato:`,

          result.reason,
        );
      }
    });

    setLookups((prev) => ({ ...prev, ...next }));
  }, [lookupErrors]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const loadContactSections = useCallback(async (id) => {
    const actionMap = [
      ["fiere", "fiere"],

      ["marchiRichiesti", "marchi_richiesti"],

      ["marchiForniti", "marchi_forniti"],

      ["rubrica", "rubrica"],

      ["stock", "stock"],

      ["note", "note"],
    ];

    const settled = await Promise.allSettled(
      actionMap.map(([, action]) => api(action, { id_contatto: id })),
    );

    settled.forEach((result, index) => {
      const [key] = actionMap[index];

      if (result.status === "rejected") {
        console.warn(`Sezione ${key} non caricata:`, result.reason);

        return;
      }

      const data = normalizeArray(result.value?.data);

      if (key === "fiere") setFiere(data);

      if (key === "marchiRichiesti") setMarchiRichiesti(data);

      if (key === "marchiForniti") setMarchiForniti(data);

      if (key === "rubrica") {
        setRubrica(data);

        setRubricaType(result.value?.tipo_rubrica || "contatto");
      }

      if (key === "stock") setStock(data);

      if (key === "note") setNote(data);
    });
  }, []);

  const loadContact = useCallback(
    async (id, { keepTab = false, fallbackContact = null } = {}) => {
      if (!id) return;

      setLoading(true);

      setError("");

      try {
        const result = await api("get", { id_contatto: id });

        const apiContact = extractContactFromApiResult(result);

        // Se arriviamo dalla tabella Contatti, manteniamo sempre i dati già
        // disponibili come fallback. I dati completi restituiti dal PHP hanno
        // precedenza e arricchiscono la preview senza poter svuotare il form.
        const contact = apiContact
          ? { ...(fallbackContact || {}), ...apiContact }
          : fallbackContact;

        if (!contact || typeof contact !== "object") {
          throw new Error("Il server non ha restituito i dati del contatto.");
        }

        setCurrentId(Number(id));

        setCurrentContact(contact);

        setFormData(hydrateFormFromContact(contact));

        setRubricaDraft(EMPTY_RUBRICA);

        setRubricaPending([]);

        setRubricaPendingEditIndex(null);

        setMarchiFornitiPending([]);

        setSelectedFieraNote(null);

        setFieraNoteDraft("");

        setNewNoteFiera("");

        setNoteFieraFilter("");

        setContacts([]);

        setSearch("");

        setMarchioInput("");

        setNewNoteType("UT");

        setExtra({
          quantita_targhe: "",

          perc_urgente: textValue(pick(contact, "perc_urgente")),

          limite_ord_stock: textValue(pick(contact, "limite_ord_stock")),

          cod_cli_ar: textValue(pick(contact, "cod_cli_ar")),

          id_anagrafica: textValue(pick(contact, "id_anagrafica")),
        });

        if (!keepTab) setActiveTab("anagrafica");

        await loadContactSections(id);
      } catch (err) {
        notifyError(err.message);
      } finally {
        setLoading(false);
      }
    },

    [loadContactSections, notifyError],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idContatto = Number(params.get("id_contatto"));

    if (!idContatto) return;

    const preview = location.state?.contactPreview;
    const previewId = Number(pick(preview, "id_contatto"));
    const validPreview =
      preview &&
      typeof preview === "object" &&
      (!previewId || previewId === idContatto)
        ? preview
        : null;

    // Mostra subito ciò che arriva dalla tabella: niente schermata vuota
    // mentre viene richiesto al PHP il record completo.
    if (validPreview) {
      setCurrentId(idContatto);
      setCurrentContact(validPreview);
      setFormData(hydrateFormFromContact(validPreview));
      setActiveTab("anagrafica");
    }

    loadContact(idContatto, { fallbackContact: validPreview });
  }, [location.search, location.state, loadContact]);

  const resetToNew = () => {
    setCurrentId(null);

    setCurrentContact(null);

    setFormData(EMPTY_FORM);

    setActiveTab("anagrafica");

    setFiere([]);

    setMarchiRichiesti([]);

    setMarchiForniti([]);

    setMarchiFornitiPending([]);

    setRubrica([]);

    setRubricaType("contatto");

    setRubricaDraft(EMPTY_RUBRICA);

    setRubricaPending([]);

    setRubricaPendingEditIndex(null);

    setSearch("");

    setContacts([]);

    setStock([]);

    setNote([]);

    setNoteSearch("");

    setNoteFieraFilter("");

    setSelectedNote(null);

    setSelectedFieraNote(null);

    setFieraNoteDraft("");

    setNewNoteTitle("");

    setNewNoteTitleType("free");

    setNewNoteFiera("");

    setNewNote("");

    setNewNoteType("UT");

    setExtra(EMPTY_EXTRA);

    setMarchioInput("");

    setError("");

    setSuccessMsg("");
  };

  useEffect(() => {
    const query = search.trim();

    if (!query) {
      searchRequestRef.current += 1;
      setContacts([]);
      setSearching(false);
      return undefined;
    }

    // Evita chiamate inutili con una sola lettera.
    // Gli ID numerici possono invece essere cercati subito.
    if (query.length < 2 && !/^\d+$/.test(query)) {
      searchRequestRef.current += 1;
      setContacts([]);
      setSearching(false);
      return undefined;
    }

    const requestId = ++searchRequestRef.current;

    const timer = window.setTimeout(async () => {
      if (requestId !== searchRequestRef.current) return;

      setSearching(true);
      setError("");

      try {
        // Una sola chiamata HTTP per ricerca.
        // La action list deve restituire direttamente anche codice_cliente.
        const result = await api("list", { search: query });

        if (requestId !== searchRequestRef.current) return;

        const rows = normalizeArray(result?.data);
        setContacts(rows);
      } catch (err) {
        if (requestId !== searchRequestRef.current) return;

        console.error("Errore ricerca contatti:", err);
        setContacts([]);
        notifyError(err.message || "Errore durante la ricerca.");
      } finally {
        if (requestId === searchRequestRef.current) {
          setSearching(false);
        }
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [search, notifyError]);

  // Ricerca separata usata dal modal "DUPLICA DA...".
  // Non interferisce con la ricerca principale della pagina.
  useEffect(() => {
    const query = duplicateSearch.trim();

    if (!duplicateModalOpen || !query) {
      duplicateSearchRequestRef.current += 1;
      setDuplicateResults([]);
      setDuplicateSearching(false);
      return undefined;
    }

    if (query.length < 2 && !/^\d+$/.test(query)) {
      duplicateSearchRequestRef.current += 1;
      setDuplicateResults([]);
      setDuplicateSearching(false);
      return undefined;
    }

    const requestId = ++duplicateSearchRequestRef.current;

    const timer = window.setTimeout(async () => {
      if (requestId !== duplicateSearchRequestRef.current) return;

      setDuplicateSearching(true);

      try {
        const result = await api("list", { search: query });

        if (requestId !== duplicateSearchRequestRef.current) return;

        setDuplicateResults(normalizeArray(result?.data));
      } catch (err) {
        if (requestId !== duplicateSearchRequestRef.current) return;

        setDuplicateResults([]);
        notifyError(err.message || "Errore durante la ricerca del contatto da duplicare.");
      } finally {
        if (requestId === duplicateSearchRequestRef.current) {
          setDuplicateSearching(false);
        }
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [duplicateModalOpen, duplicateSearch, notifyError]);


  // Sincronizza l'altezza delle tre liste con il pannello editor a sinistra.

  // ResizeObserver copre cambi di contenuto, responsive e modifiche dinamiche.

  useEffect(() => {
    const pairs = [
      [fiereEditorRef, fiereListRef],

      [rubricaEditorRef, rubricaListRef],

      [noteEditorRef, noteListRef],
    ];

    const syncHeights = () => {
      const desktop = window.innerWidth >= 1200;

      pairs.forEach(([editorRef, listRef]) => {
        const editor = editorRef.current;

        const list = listRef.current;

        if (!list) return;

        if (!desktop || !editor) {
          list.style.removeProperty("--uw-list-height");

          return;
        }

        const height = Math.ceil(editor.getBoundingClientRect().height);

        if (height > 0) {
          list.style.setProperty("--uw-list-height", `${height}px`);
        }
      });
    };

    const frame = window.requestAnimationFrame(syncHeights);

    window.addEventListener("resize", syncHeights);

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncHeights)
        : null;

    pairs.forEach(([editorRef]) => {
      if (editorRef.current && observer) observer.observe(editorRef.current);
    });

    return () => {
      window.cancelAnimationFrame(frame);

      window.removeEventListener("resize", syncHeights);

      observer?.disconnect();
    };
  }, [
    activeTab,

    currentId,

    rubricaType,

    fiere.length,

    rubrica.length,

    rubricaPending.length,

    note.length,
  ]);

  const openContactFromSearch = (id) => {
    // Chiude subito il dropdown dei risultati, poi carica il contatto.

    searchRequestRef.current += 1;

    setContacts([]);

    setSearch("");

    setSearching(false);

    loadContact(id);
  };

  const openDuplicateModal = () => {
    // La duplicazione ha senso solo mentre stiamo creando un contatto nuovo.
    if (currentId) {
      notifyError("Per duplicare un contatto premi prima NUOVO.");
      return;
    }

    setDuplicateSearch("");
    setDuplicateResults([]);
    setDuplicateModalOpen(true);
  };

  const closeDuplicateModal = () => {
    if (duplicateBusy) return;

    duplicateSearchRequestRef.current += 1;
    setDuplicateModalOpen(false);
    setDuplicateSearch("");
    setDuplicateResults([]);
    setDuplicateSearching(false);
  };

  const duplicateFromContact = async (sourceId) => {
    const id = Number(sourceId);

    if (!id) {
      notifyError("Contatto sorgente non valido.");
      return;
    }

    setDuplicateBusy(true);
    setError("");
    setSuccessMsg("");

    try {
      const result = await api("get", { id_contatto: id });
      const source = result?.data || {};
      const sourceName = textValue(pick(source, "ragione_sociale")).trim();

      // Partiamo dai parametri dell'anagrafica sorgente...
      const duplicatedForm = hydrateFormFromContact(source);

      // ...ma NON duplichiamo mai l'identità del contatto.
      duplicatedForm.codice_cliente = "";
      duplicatedForm.ragsoc = "";
      duplicatedForm.piva = "";
      duplicatedForm.referente = "";
      duplicatedForm.email = "";
      duplicatedForm.pec = "";
      duplicatedForm.telefono = "";
      duplicatedForm.cellulare = "";
      duplicatedForm.banca = "";
      duplicatedForm.iban = "";
      duplicatedForm.mpaga = "";
      duplicatedForm.sdi = "";
      duplicatedForm.fcons = "";
      duplicatedForm.noteCli = "";
      duplicatedForm.marchi = [];

      // Restiamo in modalità NUOVO: nessun ID del sorgente viene mantenuto.
      setCurrentId(null);
      setCurrentContact(null);
      setFormData(duplicatedForm);
      setActiveTab("anagrafica");

      // Le tabelle/appendici appartengono al contatto sorgente e non vanno duplicate.
      setFiere([]);
      setSelectedFiera("");
      setNewFieraNote("");
      setMarchiRichiesti([]);
      setMarchiForniti([]);
      setMarchiFornitiPending([]);
      setMarchioRichiestoAdd("");
      setMarchioFornitoAdd("");
      setRubrica([]);
      setRubricaType("contatto");
      setRubricaDraft(EMPTY_RUBRICA);
      setRubricaPending([]);
      setRubricaPendingEditIndex(null);
      setStock([]);
      setNote([]);
      setNoteSearch("");
      setNoteFieraFilter("");
      setNoteStatusFilter("all");
      setSelectedNote(null);
      setSelectedFieraNote(null);
      setFieraNoteDraft("");
      setNewNoteTitle("");
      setNewNoteTitleType("free");
      setNewNoteFiera("");
      setNewNote("");
      setNewNoteType("UT");

      // Niente collegamento Infinity / codici o impostazioni extra del contatto sorgente.
      setExtra(EMPTY_EXTRA);

      // Chiudiamo anche eventuali risultati della ricerca principale.
      searchRequestRef.current += 1;
      setContacts([]);
      setSearch("");
      setSearching(false);

      setDuplicateModalOpen(false);
      setDuplicateSearch("");
      setDuplicateResults([]);

      notifySuccess(
        sourceName
          ? `Parametri copiati da ${sourceName}. Inserisci Ragione Sociale e P.IVA del nuovo contatto.`
          : "Parametri copiati. Inserisci Ragione Sociale e P.IVA del nuovo contatto.",
      );
    } catch (err) {
      notifyError(err.message || "Impossibile duplicare il contatto.");
    } finally {
      setDuplicateBusy(false);
    }
  };

  const executeAction = async (
    action,

    payload,

    {
      successText,

      refreshSection,

      reloadContact = false,

      clearSuccess = false,
    } = {},
  ) => {
    setBusyAction(action);

    setError("");

    if (clearSuccess) setSuccessMsg("");

    try {
      const result = await api(action, payload);

      notifySuccess(successText || result?.message || "Operazione completata.");

      if (reloadContact && currentId) {
        await loadContact(currentId, { keepTab: true });
      } else if (refreshSection && currentId) {
        const refreshed = await api(refreshSection, { id_contatto: currentId });

        const rows = normalizeArray(refreshed?.data);

        if (refreshSection === "fiere") setFiere(rows);

        if (refreshSection === "marchi_richiesti") setMarchiRichiesti(rows);

        if (refreshSection === "marchi_forniti") setMarchiForniti(rows);

        if (refreshSection === "rubrica") {
          setRubrica(rows);

          setRubricaType(refreshed?.tipo_rubrica || "contatto");
        }

        if (refreshSection === "stock") setStock(rows);

        if (refreshSection === "note") setNote(rows);
      }

      return result;
    } catch (err) {
      notifyError(err.message);

      throw err;
    } finally {
      setBusyAction("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isBusy) return;

    const requiredFields = [
      {
        invalid: !formData.ragsoc.trim(),

        message: "La Ragione Sociale è obbligatoria.",
      },

      {
        invalid: !textValue(formData.tpcontatto).trim(),

        message: "Il Tipo Contatto è obbligatorio.",
      },

      {
        invalid: numberOrNull(formData.id_lingua) === null,

        message: "La Lingua è obbligatoria.",
      },

      {
        invalid: numberOrNull(formData.id_nazione) === null,

        message: "La Nazione è obbligatoria.",
      },
    ];

    const firstInvalidField = requiredFields.find((field) => field.invalid);

    if (firstInvalidField) {
      notifyError(firstInvalidField.message);

      setActiveTab("anagrafica");

      return;
    }

    if (newNote.trim()) {
      const finalTitle = getNewNoteTitle();

      if (newNoteTitleType === "fair" && !newNoteFiera) {
        notifyError("Per una nota Fiera devi selezionare la fiera.");
        setActiveTab("note");
        return;
      }
    }

    setLoading(true);

    setError("");

    setSuccessMsg("");

    try {
      const payload = buildContactPayload(formData);

      if (currentId) {
        const result = await api("update", {
          ...payload,

          id_contatto: currentId,
        });

        const saveAllErrors = [];

        const runSaveAll = async (operation) => {
          try {
            await operation();
          } catch (operationError) {
            saveAllErrors.push(
              operationError?.message || "Errore in un dato aggiuntivo",
            );
          }
        };

        if (selectedFiera) {
          await runSaveAll(() =>
            api("fiera_add", {
              id_contatto: currentId,

              id_fiera: Number(selectedFiera),

              note: newFieraNote,
            }),
          );
        }

        // Salva nel DB tutti i marchi che l'utente ha preparato in bozza.

        for (const precodice of normalizeArray(formData.marchi)) {
          await runSaveAll(() =>
            api("marchio_richiesto_add", {
              id_contatto: currentId,

              precodice,
            }),
          );
        }

        const marchiFornitiToSave = Array.from(
          new Set(
            [...marchiFornitiPending, marchioFornitoAdd]
              .map((value) => textValue(value).trim().toUpperCase())
              .filter(Boolean),
          ),
        );

        for (const precodice of marchiFornitiToSave) {
          await runSaveAll(() =>
            api("marchio_fornito_add", {
              id_contatto: currentId,
              precodice,
            }),
          );
        }

        if (rubricaDraft.num_riferimento.trim()) {
          const rubricaPayload = {
            id_contatto: currentId,

            interlocutore: resolveRubricaInterlocutore(rubricaDraft),

            qualifica: resolveRubricaQualifica(rubricaDraft),

            tipo: rubricaDraft.tipo || "E",

            num_riferimento: rubricaDraft.num_riferimento,

            descrizione: rubricaDraft.descrizione,

            notifiche: rubricaDraft.notifiche,

            comunicazioni: rubricaDraft.comunicazioni,

            invio_doc: rubricaDraft.invio_doc,

            valido: rubricaDraft.valido,
          };

          if (rubricaDraft.id_telefono_cont) {
            rubricaPayload.id_telefono_cont = rubricaDraft.id_telefono_cont;

            await runSaveAll(() => api("rubrica_update", rubricaPayload));
          } else {
            await runSaveAll(() => api("rubrica_add", rubricaPayload));
          }
        }

        if (newNote.trim()) {
          await runSaveAll(() =>
            api("nota_add", {
              id_contatto: currentId,

              nota: buildStoredNote(
                getNewNoteTitle(),
                newNote,
                newNoteTitleType === "fair"
                  ? selectedNoteFieraLabel
                  : "",
                newNoteTitleType === "fair"
                  ? newNoteFiera
                  : "",
                false,
              ),

              tipo_nota: newNoteType,
            }),
          );
        }

        if (extra.perc_urgente !== "") {
          await runSaveAll(() =>
            api("perc_urgente", {
              id_contatto: currentId,

              perc_urgente: extra.perc_urgente,
            }),
          );
        }

        if (extra.limite_ord_stock !== "") {
          await runSaveAll(() =>
            api("limite_ord_stock", {
              id_contatto: currentId,

              limite_ord_stock: extra.limite_ord_stock,
            }),
          );
        }

        if (extra.cod_cli_ar !== "") {
          await runSaveAll(() =>
            api("cod_cli_ar", {
              id_contatto: currentId,

              cod_cli_ar: extra.cod_cli_ar,
            }),
          );
        }

        const qty = Number(extra.quantita_targhe);

        if (qty > 0) {
          await runSaveAll(() =>
            api("targhe_add", {
              id_contatto: currentId,

              quantita: qty,
            }),
          );
        }

        const idAnagrafica = Number(extra.id_anagrafica);

        if (!currentInfinityId && idAnagrafica > 0) {
          await runSaveAll(() =>
            api("collega_anagrafica", {
              id_contatto: currentId,

              id_anagrafica: idAnagrafica,
            }),
          );
        }

        setSelectedFiera("");

        setNewFieraNote("");

        setMarchioRichiestoAdd("");

        setMarchioFornitoAdd("");

        setMarchiFornitiPending([]);

        setRubricaDraft(EMPTY_RUBRICA);

        setRubricaPending([]);

        setRubricaPendingEditIndex(null);

        setNewNoteTitle("");

        setNewNoteTitleType("free");

        setNewNoteFiera("");

        setNewNote("");

        setNewNoteType("UT");

        setExtra((prev) => ({ ...prev, quantita_targhe: "" }));

        await loadContact(currentId, { keepTab: true });

        if (saveAllErrors.length) {
          setSuccessMsg(
            result?.message || "Contatto aggiornato correttamente.",
          );

          setError(
            `Alcuni dati aggiuntivi non sono stati salvati: ${saveAllErrors.join(" | ")}`,
          );
        } else {
          notifySuccess(
            result?.message || "Contatto aggiornato correttamente.",
          );
        }
      } else {
        // CREATE unico: Ragione Sociale + eventuale nota vengono inviati insieme.

        // Il PHP crea il contatto, ottiene id_contatto e salva la nota nella stessa transazione.

        const result = await api("create", {
          ...payload,

          id_anagrafica: numberOrNull(extra.id_anagrafica),

          ...(newNote.trim()
            ? {
                nota: buildStoredNote(
                  getNewNoteTitle(),
                  newNote,
                  newNoteTitleType === "fair"
                    ? selectedNoteFieraLabel
                    : "",
                  newNoteTitleType === "fair"
                    ? newNoteFiera
                    : "",
                  false,
                ),
                tipo_nota: newNoteType,
              }
            : {}),
        });

        const newId = Number(result?.id_contatto);

        if (!newId) {
          throw new Error(
            "Il PHP non ha restituito id_contatto dopo la creazione.",
          );
        }

        // Alcune action del PHP hanno bisogno di id_contatto.

        // L'utente però può compilarle già nella stessa schermata: dopo il CREATE

        // le applichiamo automaticamente senza wizard / avanti-indietro.

        const deferred = [];

        if (selectedFiera) {
          deferred.push(() =>
            api("fiera_add", {
              id_contatto: newId,

              id_fiera: Number(selectedFiera),

              note: newFieraNote,
            }),
          );
        }

        const marchiFornitiToCreate = Array.from(
          new Set(
            [...marchiFornitiPending, marchioFornitoAdd]
              .map((value) => textValue(value).trim().toUpperCase())
              .filter(Boolean),
          ),
        );

        marchiFornitiToCreate.forEach((precodice) => {
          deferred.push(() =>
            api("marchio_fornito_add", {
              id_contatto: newId,
              precodice,
            }),
          );
        });

        // Tutti i riferimenti preparati prima del primo SALVA vengono creati insieme.

        // Se l'utente ha compilato l'ultimo riferimento senza premere "AGGIUNGI ALLA LISTA",

        // lo includiamo comunque per evitare perdite di dati.

        const rubricaToCreate = [...rubricaPending];

        if (rubricaDraft.num_riferimento.trim()) {
          const draftToSave = {
            ...rubricaDraft,

            id_telefono_cont: null,

            interlocutore: resolveRubricaInterlocutore(rubricaDraft),

            qualifica: resolveRubricaQualifica(rubricaDraft),

            qualifica_custom: "",
          };

          if (rubricaPendingEditIndex !== null) {
            rubricaToCreate[rubricaPendingEditIndex] = draftToSave;
          } else {
            rubricaToCreate.push(draftToSave);
          }
        }

        rubricaToCreate.forEach((riferimento) => {
          deferred.push(() =>
            api("rubrica_add", {
              id_contatto: newId,

              interlocutore: resolveRubricaInterlocutore(riferimento),

              qualifica: resolveRubricaQualifica(riferimento),

              tipo: riferimento.tipo || "E",

              num_riferimento: riferimento.num_riferimento,

              descrizione: riferimento.descrizione,

              notifiche: riferimento.notifiche,

              comunicazioni: riferimento.comunicazioni,

              invio_doc: riferimento.invio_doc,

              valido: riferimento.valido,
            }),
          );
        });

        if (extra.perc_urgente !== "") {
          deferred.push(() =>
            api("perc_urgente", {
              id_contatto: newId,

              perc_urgente: extra.perc_urgente,
            }),
          );
        }

        if (extra.limite_ord_stock !== "") {
          deferred.push(() =>
            api("limite_ord_stock", {
              id_contatto: newId,

              limite_ord_stock: extra.limite_ord_stock,
            }),
          );
        }

        if (extra.cod_cli_ar !== "") {
          deferred.push(() =>
            api("cod_cli_ar", {
              id_contatto: newId,

              cod_cli_ar: extra.cod_cli_ar,
            }),
          );
        }

        const qty = Number(extra.quantita_targhe);

        if (qty > 0) {
          deferred.push(() =>
            api("targhe_add", {
              id_contatto: newId,

              quantita: qty,
            }),
          );
        }

        const deferredErrors = [];

        for (const operation of deferred) {
          try {
            await operation();
          } catch (deferredError) {
            deferredErrors.push(
              deferredError?.message || "Errore in un dato aggiuntivo",
            );
          }
        }

        setSelectedFiera("");

        setNewFieraNote("");

        setMarchioRichiestoAdd("");

        setMarchioFornitoAdd("");

        setMarchiFornitiPending([]);

        setRubricaDraft(EMPTY_RUBRICA);

        setRubricaPending([]);

        setRubricaPendingEditIndex(null);

        setNewNoteTitle("");

        setNewNoteTitleType("free");

        setNewNoteFiera("");

        setNewNote("");

        setNewNoteType("UT");

        await loadContact(newId, { keepTab: true });

        if (deferredErrors.length) {
          setSuccessMsg(`Contatto creato correttamente (ID: ${newId}).`);

          setError(
            `Alcuni dati aggiuntivi non sono stati salvati: ${deferredErrors.join(" | ")}`,
          );
        } else {
          notifySuccess(
            result?.message || `Contatto creato correttamente (ID: ${newId}).`,
          );
        }
      }
    } catch (err) {
      notifyError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async () => {
    if (!currentId) return;

    const name = formData.ragsoc || `ID ${currentId}`;

    if (!window.confirm(`Eliminare definitivamente il contatto "${name}"?`))
      return;

    try {
      await executeAction("delete", { id_contatto: currentId });

      resetToNew();

      notifySuccess("Contatto eliminato correttamente.");

      setContacts((prev) =>
        prev.filter(
          (row) => Number(pick(row, "id_contatto")) !== Number(currentId),
        ),
      );
    } catch {
      // messaggio già gestito da executeAction
    }
  };

  // ───────────────────────────────────────────────────────────────────────────

  // FIERE

  // ───────────────────────────────────────────────────────────────────────────

  const addFiera = async () => {
    if (!currentId || !selectedFiera) return;

    try {
      await executeAction(
        "fiera_add",

        {
          id_contatto: currentId,

          id_fiera: Number(selectedFiera),

          note: newFieraNote,
        },

        { refreshSection: "fiere" },
      );

      setSelectedFiera("");

      setNewFieraNote("");
    } catch {
      // gestito centralmente
    }
  };

  const updateFiera = async (row) => {
    const id = Number(pick(row, "id_partecipazione"));

    if (!id) return;

    try {
      await executeAction(
        "fiera_update",

        { id_partecipazione: id, note: textValue(pick(row, "note")) },

        { refreshSection: "fiere" },
      );
    } catch {
      // gestito centralmente
    }
  };

  const openFieraNote = (row) => {
    setSelectedFieraNote(row);
    setFieraNoteDraft(textValue(pick(row, "note")));
  };

  const saveFieraNoteFromModal = async () => {
    const id = Number(pick(selectedFieraNote, "id_partecipazione"));
    if (!id) return;

    try {
      await executeAction(
        "fiera_update",
        {
          id_partecipazione: id,
          note: fieraNoteDraft,
        },
        { refreshSection: "fiere" },
      );
      setSelectedFieraNote(null);
      setFieraNoteDraft("");
    } catch {
      // gestito centralmente
    }
  };

  const deleteFiera = async (row) => {
    const id = Number(pick(row, "id_partecipazione"));

    if (!id || !window.confirm("Eliminare questa partecipazione alla fiera?"))
      return;

    try {
      await executeAction(
        "fiera_delete",

        { id_partecipazione: id },

        { refreshSection: "fiere" },
      );
    } catch {
      // gestito centralmente
    }
  };

  // ───────────────────────────────────────────────────────────────────────────

  // MARCHI

  // ───────────────────────────────────────────────────────────────────────────

  // I marchi scelti qui vengono messi prima in BOZZA.

  // Sono visibili subito nella tabella e vengono realmente scritti nel DB

  // soltanto quando si preme il pulsante generale SALVA.

  const stageMarchiRichiesti = useCallback(
    (precodici) => {
      const existing = new Set(
        marchiRichiesti

          .map((row) =>
            textValue(pick(row, "Prec", "precodice"))
              .trim()

              .toUpperCase(),
          )

          .filter(Boolean),
      );

      setFormData((prev) => {
        const alreadyDraft = new Set(
          normalizeArray(prev.marchi)
            .map((value) => textValue(value).trim().toUpperCase())

            .filter(Boolean),
        );

        const toAdd = normalizeArray(precodici)
          .map((value) => textValue(value).trim().toUpperCase())

          .filter(
            (value) =>
              value && !existing.has(value) && !alreadyDraft.has(value),
          );

        if (toAdd.length === 0) return prev;

        return {
          ...prev,

          marchi: [...normalizeArray(prev.marchi), ...toAdd],
        };
      });
    },

    [marchiRichiesti],
  );

  const addMarchioRichiesto = () => {
    if (!marchioRichiestoAdd || isBusy) return;

    stageMarchiRichiesti([marchioRichiestoAdd]);

    setMarchioRichiestoAdd("");
  };

  const saveMarchioRichiesto = async (row) => {
    const id = Number(pick(row, "id_marchi_trattati"));

    if (!id) return;

    try {
      await executeAction(
        "marchio_richiesto_update",

        {
          id_marchi_trattati: id,

          web: boolValue(pick(row, "Web")),

          ord: boolValue(pick(row, "Ord")),
        },

        { refreshSection: "marchi_richiesti" },
      );
    } catch {
      // gestito centralmente
    }
  };

  const deleteMarchioRichiesto = async (row) => {
    const id = Number(pick(row, "id_marchi_trattati"));

    if (!id || !window.confirm("Eliminare questo marchio richiesto?")) return;

    try {
      await executeAction(
        "marchio_richiesto_delete",

        { id_marchi_trattati: id },

        { refreshSection: "marchi_richiesti" },
      );

      // Il PHP elimina anche stock/prezzi collegati al precodice: aggiorniamo stock.

      const stockResult = await api("stock", { id_contatto: currentId });

      setStock(normalizeArray(stockResult?.data));
    } catch {
      // gestito centralmente
    }
  };

  const addPresetMarchi = (action) => {
    if (isBusy) return;

    let rows = [];

    if (action === "marchi_web") {
      rows = lookups.marchi.filter((row) => boolValue(pick(row, "online")));
    } else if (action === "marchi_oe") {
      rows = lookups.marchi.filter((row) =>
        OE_PRECODICI.has(
          textValue(pick(row, "precodice")).trim().toUpperCase(),
        ),
      );
    }

    const precodici = rows

      .map((row) => textValue(pick(row, "precodice")).trim().toUpperCase())

      .filter(Boolean);

    if (precodici.length === 0) {
      notifyError(
        action === "marchi_web"
          ? "Nessun marchio WEB disponibile."
          : "Nessun marchio OE disponibile.",
      );

      return;
    }

    stageMarchiRichiesti(precodici);
  };

  const deleteAllMarchiRichiesti = async () => {
    const hasAnything =
      marchiRichiesti.length > 0 ||
      normalizeArray(formData.marchi).length > 0 ||
      Boolean(marchioRichiestoAdd);

    if (!hasAnything) return;

    if (!window.confirm("Svuotare tutti i marchi richiesti?")) return;

    setFormData((prev) => ({ ...prev, marchi: [] }));
    setMarchioRichiestoAdd("");
    setStock([]);

    if (!currentId) {
      setMarchiRichiesti([]);
      return;
    }

    try {
      await executeAction(
        "marchi_richiesti_delete_all",
        { id_contatto: currentId },
        { refreshSection: "marchi_richiesti" },
      );
    } catch {
      // gestito centralmente
    }
  };

  const stageMarchiForniti = useCallback(
    (precodici) => {
      const existing = new Set(
        marchiForniti
          .map((row) =>
            textValue(pick(row, "precodice", "Prec"))
              .trim()
              .toUpperCase(),
          )
          .filter(Boolean),
      );

      setMarchiFornitiPending((prev) => {
        const pending = new Set(
          normalizeArray(prev)
            .map((value) => textValue(value).trim().toUpperCase())
            .filter(Boolean),
        );

        normalizeArray(precodici)
          .map((value) => textValue(value).trim().toUpperCase())
          .filter(Boolean)
          .forEach((value) => {
            if (!existing.has(value)) pending.add(value);
          });

        return Array.from(pending);
      });
    },
    [marchiForniti],
  );

  const addMarchioFornito = () => {
    if (!marchioFornitoAdd || isBusy) return;
    stageMarchiForniti([marchioFornitoAdd]);
    setMarchioFornitoAdd("");
  };

  const addPresetMarchiFornitiOE = () => {
    if (isBusy) return;

    const precodici = lookups.marchi
      .filter((row) =>
        OE_PRECODICI.has(
          textValue(pick(row, "precodice")).trim().toUpperCase(),
        ),
      )
      .map((row) => textValue(pick(row, "precodice")).trim().toUpperCase())
      .filter(Boolean);

    if (precodici.length === 0) {
      notifyError("Nessun marchio OE disponibile.");
      return;
    }

    stageMarchiForniti(precodici);
  };

  const deleteMarchioFornito = async (row) => {
    const id = Number(pick(row, "id_marchi_trattati"));

    if (!id || !window.confirm("Eliminare questo marchio fornito?")) return;

    try {
      await executeAction(
        "marchio_fornito_delete",

        { id_marchi_trattati: id },

        { refreshSection: "marchi_forniti" },
      );
    } catch {
      // gestito centralmente
    }
  };

  const deleteAllMarchiForniti = async () => {
    const hasAnything =
      marchiForniti.length > 0 ||
      marchiFornitiPending.length > 0 ||
      Boolean(marchioFornitoAdd);

    if (!hasAnything) return;

    if (!window.confirm("Svuotare tutti i marchi forniti?")) return;

    setMarchiFornitiPending([]);
    setMarchioFornitoAdd("");

    if (!currentId) {
      setMarchiForniti([]);
      return;
    }

    try {
      await executeAction(
        "marchi_forniti_delete_all",
        { id_contatto: currentId },
        { refreshSection: "marchi_forniti" },
      );
    } catch {
      // gestito centralmente
    }
  };

  // ───────────────────────────────────────────────────────────────────────────

  // RUBRICA

  // ───────────────────────────────────────────────────────────────────────────

  const editRubrica = (row) => {
    const qualificaDraft = toRubricaQualificaDraft(pick(row, "qualifica"));

    setRubricaDraft({
      id_telefono_cont: numberOrNull(pick(row, "id_telefono_cont")),

      interlocutore: textValue(pick(row, "interlocutore")),

      ...qualificaDraft,

      tipo: textValue(pick(row, "codice_tipo", "tipo")) || "E",

      num_riferimento: textValue(pick(row, "num_riferimento")),

      descrizione: textValue(pick(row, "descrizione")),

      notifiche: boolValue(pick(row, "notifiche")),

      comunicazioni: boolValue(pick(row, "comunicazioni", "Comunicazioni")),

      invio_doc: boolValue(pick(row, "Invio Doc", "invio_doc")),

      valido: textValue(pick(row, "Valido")).toLowerCase() !== "non valido",
    });

    // Se sto lavorando nella tab Rubrica resto lì: il form ora è modificabile

    // direttamente, senza essere rimandato obbligatoriamente ad Anagrafica.

    window.setTimeout(() => {
      document

        .getElementById("uw-sezione-rubrica")

        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  const saveRubrica = async () => {
    if (!rubricaDraft.num_riferimento.trim()) {
      notifyError("Numero/email obbligatorio.");
      return;
    }

    // Qualunque nuovo riferimento viene salvato sempre in DBA.ut_telefono.
    // Le righe DBA.telefono/Infinity vengono mostrate insieme, ma restano sola lettura.

    const payloadBase = {
      interlocutore: resolveRubricaInterlocutore(rubricaDraft),
      qualifica: resolveRubricaQualifica(rubricaDraft),
      tipo: rubricaDraft.tipo || "E",
      num_riferimento: rubricaDraft.num_riferimento,
      descrizione: rubricaDraft.descrizione,
      notifiche: rubricaDraft.notifiche,
      comunicazioni: rubricaDraft.comunicazioni,
      invio_doc: rubricaDraft.invio_doc,
      valido: rubricaDraft.valido,
    };

    // Contatto NON ancora salvato: il riferimento resta in bozza locale e
    // verrà creato solo al momento del SALVA generale (vedi handleSubmit).
    if (!currentId) {
      const draftEntry = {
        ...rubricaDraft,
        qualifica: payloadBase.qualifica,
        qualifica_custom: "",
      };

      setRubricaPending((prev) => {
        const next = [...prev];

        if (rubricaPendingEditIndex !== null) {
          next[rubricaPendingEditIndex] = draftEntry;
        } else {
          next.push(draftEntry);
        }

        return next;
      });

      setRubricaDraft(EMPTY_RUBRICA);
      setRubricaPendingEditIndex(null);
      notifySuccess("Riferimento aggiunto alla lista da salvare.");
      return;
    }

    // Contatto esistente: scrittura immediata nel DB.
    try {
      if (rubricaDraft.id_telefono_cont) {
        await executeAction(
          "rubrica_update",

          {
            ...payloadBase,
            id_telefono_cont: rubricaDraft.id_telefono_cont,
          },

          { refreshSection: "rubrica" },
        );
      } else {
        await executeAction(
          "rubrica_add",

          {
            ...payloadBase,
            id_contatto: currentId,
          },

          { refreshSection: "rubrica" },
        );
      }

      setRubricaDraft(EMPTY_RUBRICA);
      setRubricaPendingEditIndex(null);
    } catch {
      // errore già gestito centralmente da executeAction
    }
  };
  const editPendingRubrica = (index) => {
    const row = rubricaPending[index];

    if (!row) return;

    const qualificaDraft = toRubricaQualificaDraft(row.qualifica);

    setRubricaDraft({
      ...EMPTY_RUBRICA,
      ...row,
      ...qualificaDraft,
      id_telefono_cont: null,
    });

    setRubricaPendingEditIndex(index);
  };

  const removePendingRubrica = (index) => {
    setRubricaPending((prev) =>
      prev.filter((_, rowIndex) => rowIndex !== index),
    );

    if (rubricaPendingEditIndex === index) {
      setRubricaDraft(EMPTY_RUBRICA);

      setRubricaPendingEditIndex(null);
    } else if (
      rubricaPendingEditIndex !== null &&
      rubricaPendingEditIndex > index
    ) {
      setRubricaPendingEditIndex((prev) => prev - 1);
    }
  };

  const deleteRubrica = async (row) => {
    const id = Number(pick(row, "id_telefono_cont"));

    if (!id || !window.confirm("Eliminare questo riferimento dalla rubrica?"))
      return;

    try {
      await executeAction(
        "rubrica_delete",

        { id_telefono_cont: id },

        { refreshSection: "rubrica" },
      );

      if (rubricaDraft.id_telefono_cont === id) setRubricaDraft(EMPTY_RUBRICA);
    } catch {
      // gestito centralmente
    }
  };

  // ───────────────────────────────────────────────────────────────────────────

  // STOCK, NOTE, EXTRA

  // ───────────────────────────────────────────────────────────────────────────

  const deleteStock = async (row) => {
    const id = Number(pick(row, "id_cat_invio_email"));

    if (!id || !window.confirm("Eliminare questo invio stock?")) return;

    try {
      await executeAction(
        "stock_delete",

        { id_cat_invio_email: id },

        { refreshSection: "stock" },
      );
    } catch {
      // gestito centralmente
    }
  };

  const addNote = async () => {
    if (isBusy) return;

    if (!newNoteTitle.trim()) {
      notifyError("Inserisci il Titolo della nota.");
      return;
    }

    if (!newNote.trim()) {
      notifyError("Inserisci il testo della nota.");
      return;
    }

    // Anche su un contatto NUOVO il pulsante funziona subito:

    // se c'è la Ragione Sociale richiama SALVA e il PHP salva

    // contatto + nota nella stessa transazione.

    if (!currentId) {
      if (!formData.ragsoc.trim()) {
        notifyError("Inserisci prima la Ragione Sociale.");

        return;
      }

      document.getElementById("contact-main-form")?.requestSubmit();

      return;
    }

    try {
      await executeAction(
        "nota_add",

        {
          id_contatto: currentId,

          nota: buildStoredNote(
            newNoteTitle,
            newNote,
            selectedNoteFieraLabel,
            newNoteFiera,
          ),

          tipo_nota: newNoteType,
        },

        { refreshSection: "note" },
      );

      setNewNoteTitle("");

      setNewNoteFiera("");

      setNewNote("");

      setNewNoteType("UT");
    } catch {
      // gestito centralmente
    }
  };

  const saveExtraField = async (action, key) => {
    if (!currentId) return;

    try {
      await executeAction(
        action,

        { id_contatto: currentId, [key]: extra[key] },

        { reloadContact: true },
      );
    } catch {
      // gestito centralmente
    }
  };

  const addTarghe = async () => {
    const qty = Number(extra.quantita_targhe);

    if (!currentId || !qty || qty <= 0) {
      notifyError("Inserisci una quantità targhe maggiore di zero.");

      return;
    }

    try {
      await executeAction(
        "targhe_add",

        { id_contatto: currentId, quantita: qty },

        { reloadContact: true },
      );

      setExtra((prev) => ({ ...prev, quantita_targhe: "" }));
    } catch {
      // gestito centralmente
    }
  };

  const collegaInfinity = async () => {
    const idAnagrafica = Number(extra.id_anagrafica);

    if (!currentId || !idAnagrafica) {
      notifyError("Inserisci un id_anagrafica valido.");

      return;
    }

    try {
      await executeAction(
        "collega_anagrafica",

        { id_contatto: currentId, id_anagrafica: idAnagrafica },

        { reloadContact: true },
      );
    } catch {
      // gestito centralmente
    }
  };

  const scollegaInfinity = async () => {
    if (
      !currentId ||
      !window.confirm("Scollegare il contatto dall'anagrafica Infinity?")
    )
      return;

    try {
      await executeAction(
        "scollega_anagrafica",

        { id_contatto: currentId },

        { reloadContact: true },
      );
    } catch {
      // gestito centralmente
    }
  };

  const updateMarchioRow = (id, key, checked) => {
    setMarchiRichiesti((prev) =>
      prev.map((row) =>
        Number(pick(row, "id_marchi_trattati")) === Number(id)
          ? { ...row, [key]: checked ? 1 : 0 }
          : row,
      ),
    );
  };

  const currentInfinityId = textValue(pick(currentContact, "id_anagrafica"));

  const currentClienteId = textValue(pick(currentContact, "codice_cliente"));

  const currentFornitoreId = textValue(
    pick(currentContact, "codice_fornitore"),
  );

  const targheAssegnate = textValue(pick(currentContact, "targhe_assegnate"));

  const targheConsumate = textValue(pick(currentContact, "targhe_consumate"));

  const targheResidue = textValue(pick(currentContact, "targhe_residue"));

  const resetInfinityWizard = () => {
    setInfinityModalOpen(false);
    setInfinityStep("setup");
    setInfinityPrecheck(null);
    setInfinityOptions({ tipi_cliente: [], zone: [], pagamenti: [] });
    setInfinityForm({
      tipo_cliente: "",
      zona: "",
      id_pagamento: "",
      id_nazione: "",
      provincia: "",
      localita: "",
      cap: "",
      indirizzo: "",
    });
    setInfinityCreatedId(null);
    setInfinityMergeData({ utility: [], infinity: [] });
  };

  const closeInfinityWizard = () => {
    if (
      infinityCreatedId &&
      infinityStep === "merge" &&
      !window.confirm(
        "L'anagrafica Infinity è già stata creata ma non è ancora collegata al contatto. Chiudere comunque?",
      )
    ) {
      return;
    }

    resetInfinityWizard();
  };

  const openInfinityWizard = async () => {
    if (!currentId) {
      notifyError("Salva prima il contatto.");
      return;
    }

    if (currentInfinityId) {
      notifyError(`Il contatto è già collegato a Infinity #${currentInfinityId}.`);
      return;
    }

    setInfinityBusy(true);
    setError("");

    try {
      const [precheckResult, optionsResult] = await Promise.all([
        api("infinity_precheck", { id_contatto: currentId }),
        api("infinity_options"),
      ]);

      setInfinityPrecheck(precheckResult);
      setInfinityOptions({
        tipi_cliente: normalizeArray(optionsResult?.data?.tipi_cliente),
        zone: normalizeArray(optionsResult?.data?.zone),
        pagamenti: normalizeArray(optionsResult?.data?.pagamenti),
      });
      setInfinityForm({
        tipo_cliente: "",
        zona: "",
        id_pagamento: "",
        id_nazione: textValue(formData.id_nazione),
        provincia: textValue(formData.provincia),
        localita: textValue(formData.localita),
        cap: textValue(formData.cap),
        indirizzo: textValue(formData.indirizzo),
      });
      setInfinityCreatedId(null);
      setInfinityMergeData({ utility: [], infinity: [] });
      setInfinityStep("setup");
      setInfinityModalOpen(true);
    } catch (err) {
      notifyError(err?.message || "Impossibile preparare la creazione Infinity.");
    } finally {
      setInfinityBusy(false);
    }
  };

  const createInfinityAnagrafica = async () => {
    if (!currentId) return;

    if (!infinityForm.tipo_cliente) {
      notifyError("Seleziona un tipo cliente.");
      return;
    }

    if (!infinityForm.zona) {
      notifyError("Seleziona una zona.");
      return;
    }

    if (!Number(infinityForm.id_pagamento)) {
      notifyError("Seleziona una modalità di pagamento.");
      return;
    }

    setInfinityBusy(true);
    setError("");

    try {
      const result = await api("infinity_create_hermes", {
        id_contatto: currentId,
        tipo_cliente: infinityForm.tipo_cliente,
        zona: infinityForm.zona,
        id_pagamento: Number(infinityForm.id_pagamento),

        // Come nel desktop: Hermes riceve i valori dei controlli correnti
        // del wizard, precompilati dal contatto ma modificabili prima dell'invio.
        indirizzo: infinityForm.indirizzo,
        localita: infinityForm.localita,
        cap: infinityForm.cap,
        provincia: infinityForm.provincia,
        id_nazione: numberOrNull(infinityForm.id_nazione),
        codice_iso_nazione: infinityNationIso,
      });

      const idAnagrafica = Number(result?.id_anagrafica);
      if (!idAnagrafica) {
        throw new Error("Hermes non ha restituito un id_anagrafica valido.");
      }

      setInfinityCreatedId(idAnagrafica);
      setInfinityMergeData({
        utility: normalizeArray(result?.rubriche?.utility),
        infinity: normalizeArray(result?.rubriche?.infinity),
      });
      setInfinityStep("merge");
      notifySuccess(`Anagrafica Infinity #${idAnagrafica} creata. Completa l'unione rubriche.`);
    } catch (err) {
      notifyError(err?.message || "Errore durante la creazione dell'anagrafica Infinity.");
    } finally {
      setInfinityBusy(false);
    }
  };

  const findInfinityRubricaMatch = (utilityRow) => {
    const utilityTipo = textValue(
      pick(utilityRow, "tipo", "codice_tipo"),
    )
      .trim()
      .toLowerCase();
    const utilityNumero = textValue(pick(utilityRow, "num_riferimento"))
      .trim()
      .toLowerCase();

    if (!utilityTipo || !utilityNumero) return null;

    return (
      infinityMergeData.infinity.find((row) => {
        const infinityTipo = textValue(pick(row, "tipo", "codice_tipo"))
          .trim()
          .toLowerCase();
        const infinityNumero = textValue(pick(row, "num_riferimento"))
          .trim()
          .toLowerCase();

        return infinityTipo === utilityTipo && infinityNumero === utilityNumero;
      }) || null
    );
  };

  const completeInfinityMerge = async () => {
    if (!currentId || !infinityCreatedId) return;

    const operations = infinityMergeData.utility.map((row) => {
      const idTelefonoCont = Number(pick(row, "id_telefono_cont"));
      const match = findInfinityRubricaMatch(row);

      if (match) {
        return {
          id_telefono_cont: idTelefonoCont,
          mode: "already_in_infinity",
          id_telefono: Number(pick(match, "id_telefono")),
        };
      }

      return {
        id_telefono_cont: idTelefonoCont,
        mode: "copy",
      };
    });

    setInfinityBusy(true);
    setError("");

    try {
      const result = await api("infinity_merge_commit", {
        id_contatto: currentId,
        id_anagrafica: infinityCreatedId,
        operations,
      });

      if (!result?.data?.linked) {
        throw new Error("L'unione rubriche non risulta completata.");
      }

      const linkedId = infinityCreatedId;
      resetInfinityWizard();
      await loadContact(currentId, { keepTab: true });
      notifySuccess(`Anagrafica Infinity #${linkedId} collegata con successo.`);
    } catch (err) {
      notifyError(err?.message || "Errore durante l'unione delle rubriche.");
    } finally {
      setInfinityBusy(false);
    }
  };

  const stagedMarchiRichiesti = useMemo(() => {
    const existing = new Set(
      marchiRichiesti

        .map((row) =>
          textValue(pick(row, "Prec", "precodice"))
            .trim()

            .toUpperCase(),
        )

        .filter(Boolean),
    );

    return normalizeArray(formData.marchi)
      .map((precodice) => textValue(precodice).trim().toUpperCase())

      .filter((precodice) => precodice && !existing.has(precodice))

      .map((precodice) => {
        const lookup = lookups.marchi.find(
          (row) =>
            textValue(pick(row, "precodice")).trim().toUpperCase() ===
            precodice,
        );

        return {
          _draft: true,

          Prec: precodice,

          precodice,

          Descrizione: optionValue(lookup, "descrizione"),

          descrizione: optionValue(lookup, "descrizione"),

          Email: "",

          Web: 0,

          Ord: 0,
        };
      });
  }, [formData.marchi, lookups.marchi, marchiRichiesti]);

  const visibleMarchiRichiesti = useMemo(
    () => [...marchiRichiesti, ...stagedMarchiRichiesti],

    [marchiRichiesti, stagedMarchiRichiesti],
  );

  const stagedMarchiForniti = useMemo(() => {
    const existing = new Set(
      marchiForniti
        .map((row) =>
          textValue(pick(row, "precodice", "Prec"))
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean),
    );

    return marchiFornitiPending
      .map((precodice) => textValue(precodice).trim().toUpperCase())
      .filter((precodice) => precodice && !existing.has(precodice))
      .map((precodice) => {
        const lookup = lookups.marchi.find(
          (row) =>
            textValue(pick(row, "precodice")).trim().toUpperCase() ===
            precodice,
        );

        return {
          _draft: true,
          precodice,
          descrizione: optionValue(lookup, "descrizione"),
        };
      });
  }, [lookups.marchi, marchiForniti, marchiFornitiPending]);

  const visibleMarchiForniti = useMemo(
    () => [...marchiForniti, ...stagedMarchiForniti],
    [marchiForniti, stagedMarchiForniti],
  );

  // Contatori usati come badge sui tab, per capire a colpo d'occhio dove c'è contenuto

  const visibleRubrica = useMemo(
    () => [
      ...rubrica,

      ...rubricaPending.map((row, index) => ({
        ...row,

        _draft: true,

        _draftIndex: index,

        Tipo: row.tipo,

        Valido: row.valido ? "Valido" : "Non valido",
      })),
    ],

    [rubrica, rubricaPending],
  );

  const tabCounts = {
    fiere: fiere.length,

    marchi: visibleMarchiRichiesti.length + visibleMarchiForniti.length,

    rubrica: visibleRubrica.length,

    stock: stock.length,

    note: note.length,
  };

  const noteSourceLabel = (row) => {
    const source = textValue(pick(row, "indi")).toUpperCase();
    const idNota = textValue(pick(row, "id_nota"));

    if (source === "U") return noteTypeDisplayLabel("UT");
    if (idNota) return noteTypeDisplayLabel(idNota); // CLI, C_1, C_s1, C_ODP, ...

    return noteTypeDisplayLabel(source || "UT");
  };

  const noteTypeValue = (row) => {
    const source = textValue(pick(row, "indi")).toUpperCase();
    const idNota = textValue(pick(row, "id_nota"));
    if (source === "U") return "UT";
    return idNota || source || "UT";
  };

  const isNoteDone = (row) =>
    Boolean(parseStoredNote(pick(row, "nota")).done);

  const updateNoteOnServer = async (row, overrides = {}) => {
    if (!row || !currentId) return;

    const parsed = parseStoredNote(pick(row, "nota"));
    const title = overrides.title ?? parsed.title;
    const body = overrides.body ?? parsed.body;
    const fieraId = overrides.fieraId ?? parsed.fieraId;
    const done = overrides.done ?? parsed.done;

    const fieraLabel =
      overrides.fiera ??
      (fieraId
        ? fieraDisplayLabel(
            lookups.fiere.find(
              (item) =>
                textValue(optionValue(item, "id_fiera")) === textValue(fieraId),
            ),
          )
        : parsed.fiera);

    await executeAction(
      "nota_update",
      {
        id_contatto: currentId,
        id_nota: textValue(pick(row, "id_nota")),
        tipo_nota: noteTypeValue(row),
        nota: buildStoredNote(title, body, fieraLabel, fieraId, done),
      },
      { refreshSection: "note" },
    );
  };

  const toggleNoteDone = async (row) => {
    const parsed = parseStoredNote(pick(row, "nota"));
    try {
      await updateNoteOnServer(row, { done: !parsed.done });
      setSelectedNote(null);
    } catch {
      // errore già gestito da executeAction
    }
  };

  const openEditNote = (row) => {
    const parsed = parseStoredNote(pick(row, "nota"));
    setEditingNote(row);
    setEditNoteTitle(parsed.title);
    setEditNoteBody(parsed.body);
    setEditNoteFiera(parsed.fieraId || "");
  };

  const saveEditedNote = async () => {
    if (!editingNote) return;
    if (!editNoteTitle.trim()) {
      notifyError("Inserisci il titolo della nota.");
      return;
    }
    if (!editNoteBody.trim()) {
      notifyError("Inserisci il testo della nota.");
      return;
    }

    try {
      await updateNoteOnServer(editingNote, {
        title: editNoteTitle.trim(),
        body: editNoteBody.trim(),
        fieraId: editNoteFiera,
      });
      setEditingNote(null);
      setSelectedNote(null);
      notifySuccess("Nota modificata correttamente.");
    } catch {
      // errore già gestito da executeAction
    }
  };

  const deleteNote = async (row) => {
    if (!row || !currentId) return;
    const parsed = parseStoredNote(pick(row, "nota"));

    if (!window.confirm(`Eliminare definitivamente la nota "${parsed.title}"?`)) {
      return;
    }

    try {
      await executeAction(
        "nota_delete",
        {
          id_contatto: currentId,
          id_nota: textValue(pick(row, "id_nota")),
          tipo_nota: noteTypeValue(row),
        },
        { refreshSection: "note" },
      );
      setSelectedNote(null);
      setEditingNote(null);
    } catch {
      // errore già gestito da executeAction
    }
  };

  const filteredNotes = useMemo(() => {
    const query = noteSearch.trim().toLowerCase();
    const fieraFilter = textValue(noteFieraFilter);

    return note.filter((row) => {
      const parsed = parseStoredNote(pick(row, "nota"));
      const matchesTitle =
        !query ||
        parsed.title.toLowerCase().includes(query) ||
        parsed.body.toLowerCase().includes(query);
      const matchesFiera =
        !fieraFilter || textValue(parsed.fieraId) === fieraFilter;
      const matchesStatus =
        noteStatusFilter === "all" ||
        (noteStatusFilter === "done" && parsed.done) ||
        (noteStatusFilter === "todo" && !parsed.done);

      return matchesTitle && matchesFiera && matchesStatus;
    });
  }, [note, noteSearch, noteFieraFilter, noteStatusFilter]);

  const selectedNoteContent = selectedNote
    ? parseStoredNote(pick(selectedNote, "nota"))
    : null;

  const selectedNoteFieraDisplay = selectedNoteContent
    ? selectedNoteContent.fiera ||
      (selectedNoteContent.fieraId
        ? fieraDisplayLabel(
            lookups.fiere.find(
              (row) =>
                textValue(optionValue(row, "id_fiera")) ===
                textValue(selectedNoteContent.fieraId),
            ),
          )
        : "") ||
      "—"
    : "—";

  const renderSectionHeader = (Icon, title, right = null) => (
    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
      <div className="d-flex align-items-center gap-2">
        <span className="uw-section-icon">
          <Icon size={13} />
        </span>

        <span className="uw-section-title">{title}</span>
      </div>

      {right}
    </div>
  );

  const scrollToSection = (sectionId) => {
    window.requestAnimationFrame(() => {
      document

        .getElementById(sectionId)

        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const goToMasterSection = (sectionId) => {
    if (activeTab !== "anagrafica") {
      setActiveTab("anagrafica");

      window.setTimeout(() => scrollToSection(sectionId), 0);

      return;
    }

    scrollToSection(sectionId);
  };

  const createNewFiera = async () => {
    const descrizione = newFieraDescrizione.trim();

    if (!descrizione) {
      notifyError("Inserisci il nome della fiera.");
      return;
    }

    setFieraCreateBusy(true);
    setError("");
    setSuccessMsg("");

    try {
      const response = await fetch(CONTATTI_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          action: "fiera_create",
          descrizione,
        }),
      });

      const raw = await response.text();

      let result;
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(`Il PHP non ha restituito JSON valido: ${raw}`);
      }

      if (!response.ok || result?.success === false) {
        throw new Error(
          result?.error ||
            result?.message ||
            `Errore HTTP ${response.status}`,
        );
      }

      // Aggiorna subito la lista fiere già usata dal resto della pagina,
      // così la nuova fiera compare nelle select senza ricaricare il browser.
      const fiereResult = await api("fiere_list");
      setLookups((prev) => ({
        ...prev,
        fiere: normalizeArray(fiereResult?.data),
      }));

      setNewFieraDescrizione("");
      setFieraCreateModalOpen(false);

      notifySuccess(
        result?.message ||
          `Fiera "${descrizione}" creata correttamente.`,
      );
    } catch (err) {
      notifyError(err?.message || "Errore durante la creazione della fiera.");
    } finally {
      setFieraCreateBusy(false);
    }
  };

  const scrollToTop = () => {
    document.getElementById("uw-page-scroll")?.scrollTo({
      top: 0,

      behavior: "smooth",
    });
  };

  const handleNoteModalConfirm = async () => {
    if (!newNoteTitle.trim()) {
      notifyError("Inserisci il Titolo della nota.");
      return;
    }

    if (!newNote.trim()) {
      notifyError("Inserisci il testo della nota.");

      return;
    }

    if (!currentId) {
      setNoteModalOpen(false);

      goToMasterSection("uw-sezione-note");

      return;
    }

    try {
      await executeAction(
        "nota_add",

        {
          id_contatto: currentId,

          nota: buildStoredNote(
            newNoteTitle,
            newNote,
            selectedNoteFieraLabel,
            newNoteFiera,
          ),

          tipo_nota: newNoteType,
        },

        { refreshSection: "note" },
      );

      setNewNoteTitle("");

      setNewNoteFiera("");

      setNewNote("");

      setNewNoteType("UT");

      setNoteModalOpen(false);
    } catch {
      // messaggio già gestito da executeAction
    }
  };



  return (
    <div
      className="uw-liquid-page"
      style={{
        background: `radial-gradient(circle at 15% 0%, rgba(235,181,27,0.10), transparent 34%), radial-gradient(circle at 88% 15%, rgba(255,255,255,0.04), transparent 30%), linear-gradient(145deg, #0d0f13 0%, ${BG_PAGE} 48%, #090b0e 100%)`,

        height: "100vh",

        width: "100%",

        color: TEXT_DARK,

        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",

        display: "flex",

        flexDirection: "column",

        overflow: "hidden",
      }}
    >
      <NavbarUtility
        title={currentId ? `Contatto #${currentId}` : "Nuovo Contatto"}
      />

      <div
        id="uw-page-scroll"
        style={{ flex: 1, overflowY: "auto" }}
        className="scroll-hidden uw-page-scroll"
      >
        <Container fluid className="py-3 px-3 px-md-4">
          {/* ───────────────────────────── RICERCA ───────────────────────────── */}

          <Card
            className="uw-card border-0 mb-3 overflow-hidden"
            style={{ backgroundColor: PANEL_BG }}
          >
            <div className="uw-card-accent" />

            <Card.Body className="p-3">
              <div className="d-flex flex-column flex-xl-row gap-2 align-items-xl-end">
                <div className="flex-grow-1">
                  <Form.Label className="uw-label">CERCA CONTATTO</Form.Label>

                  <div className="d-flex gap-2">
                    <div className="uw-input-icon-wrap flex-grow-1">
                      <Search size={14} className="uw-input-icon" />

                      <Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.preventDefault();
                        }}
                        placeholder="Cerca per nome, codice cliente, ID, P.IVA, località..."
                        className="uw-input uw-input-with-icon uw-search-live-input"
                        autoComplete="off"
                      />

                      {searching && (
                        <Spinner
                          animation="border"
                          size="sm"
                          className="uw-search-live-spinner"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="uw-action-btn"
                    onClick={() => navigate("/utility/contatti")}
                  >
                    <Phone size={15} />
                    <span>TUTTI I CONTATTI</span>
                  </button>

                  <button
                    type="button"
                    className="uw-action-btn"
                    onClick={resetToNew}
                  >
                    <UserPlus size={15} />
                    <span>NUOVO</span>
                  </button>

                  {!currentId && (
                    <button
                      type="button"
                      className="uw-action-btn uw-duplicate-btn"
                      onClick={openDuplicateModal}
                      disabled={isBusy}
                      title="Copia i parametri da un contatto esistente senza copiarne l'identità"
                    >
                      <RefreshCw size={15} />
                      <span>DUPLICA DA...</span>
                    </button>
                  )}
                </div>
              </div>

              {contacts.length > 0 && (
                <div className={`uw-search-results mt-3${searching ? " uw-search-results-loading" : ""}`}>
                  <Table
                    responsive
                    hover
                    size="sm"
                    className="mb-0 uw-table align-middle"
                  >
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Codice cliente</th>
                        <th>Ragione sociale</th>
                        <th>P.IVA</th>
                        <th>Località</th>
                        <th>Tipo</th>
                      </tr>
                    </thead>

                    <tbody>
                      {contacts.map((row) => {
                        const id = Number(pick(row, "id_contatto"));

                        return (
                          <tr
                            key={id || JSON.stringify(row)}
                            className="uw-contact-row"
                            onClick={() => openContactFromSearch(id)}
                            title="Apri contatto"
                          >
                            <td className="text-muted">{textValue(id)}</td>

                            <td className="fw-semibold">
                              {textValue(
                                pick(
                                  row,
                                  "codice_cliente",
                                  "cod_cliente",
                                  "codice_cli",
                                  "id_cliente",
                                ),
                              ) || "---"}
                            </td>

                            <td className="fw-semibold">
                              {textValue(pick(row, "ragione_sociale"))}
                            </td>

                            <td>{textValue(pick(row, "par_iva"))}</td>

                            <td>
                              {textValue(pick(row, "localita"))}

                              {pick(row, "provincia")
                                ? ` (${textValue(pick(row, "provincia"))})`
                                : ""}
                            </td>

                            <td>{textValue(pick(row, "tipo_contatto"))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}

              {search.trim() &&
                !searching &&
                contacts.length === 0 &&
                !error && (
                  <div className="uw-search-empty mt-2">
                    Nessun contatto trovato per <strong>{search.trim()}</strong>
                    .
                  </div>
                )}
            </Card.Body>
          </Card>

          {/* ─────────────────────────── SCHEDA CONTATTO ───────────────────────── */}

          <Card
            className="uw-card border-0 mb-3 overflow-hidden"
            style={{ backgroundColor: PANEL_BG }}
          >
            <div className="uw-card-accent" />

            <Card.Body className="p-3 p-md-4">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 mb-3">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="uw-eyebrow-dash" />

                   <h6 className="mb-0 fw-bold text-uppercase uw-page-title">
  {currentId ? (
    <>
      Gestione Anagrafica — ID: {currentId}
      {formData.codice_cliente && (
        <> · CODICE CLIENTE: {formData.codice_cliente}</>
      )}
    </>
  ) : (
    "Inserimento Anagrafica Completa"
  )}
</h6>
                  </div>

                  {currentId && (
                    <div className="small text-muted mt-1">
                      {formData.ragsoc || "Contatto"}

                      {currentInfinityId
                        ? ` · Infinity #${currentInfinityId}`
                        : " · Non collegato a Infinity"}
                    </div>
                  )}
                </div>

                {loading && (
                  <div className="d-flex align-items-center gap-2 small text-muted">
                    <Spinner animation="border" size="sm" /> Caricamento...
                  </div>
                )}
              </div>

              <div
                className=" mb-3"
                style={{
                  width: "100%",

                  display: "flex",

                  justifyContent: "flex-end",
                }}
              >
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <div className="uw-editable-status">
                    <span className="uw-editable-status-dot" />
                    MODIFICABILE
                  </div>

                  {currentId && !currentInfinityId && (
                    <button
                      type="button"
                      className="uw-action-btn uw-infinity-create-btn"
                      disabled={isBusy}
                      onClick={openInfinityWizard}
                      title="Crea l'anagrafica Infinity con le stesse regole del gestionale"
                    >
                      {infinityBusy ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Link2 size={14} />
                      )}
                      CREA ANAGRAFICA INFINITY
                    </button>
                  )}

                  <button
                    type="button"
                    className="uw-action-btn uw-action-solid"
                    disabled={isBusy}
                    onClick={() =>
                      document.getElementById("contact-main-form")?.requestSubmit()
                    }
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <Save size={14} />
                    )}

                    {loading ? "SALVATAGGIO..." : "SALVA"}
                  </button>
                </div>
              </div>

              {/* ───────────────────────────── TAB BAR ───────────────────────────── */}

              <div className="uw-tabs mb-3">
                {TABS.map((tab) => {
                  const Icon = tab.icon;

                  const count = tabCounts[tab.id];

                  return (
                    <button
                      type="button"
                      key={tab.id}
                      className={`uw-tab${activeTab === tab.id ? " active" : ""}`}
                      onClick={() => setActiveTab(tab.id)}
                      title={`Gestione ${tab.label}`}
                    >
                      <Icon size={14} />

                      <span>{tab.label}</span>

                      {Boolean(count) && (
                        <span className="uw-tab-badge">{count}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              <Form id="contact-main-form" onSubmit={handleSubmit}>
                {/* ───────────────────────── ANAGRAFICA ───────────────────────── */}
                {activeTab === "anagrafica" && (
                  <div
                    id="uw-sezione-anagrafica"
                    className="row g-3 uw-anchor-section"
                  >
                    <div className="col-12 col-xl-6">
                      <div className="p-3 mb-3 uw-section-box">
                        {renderSectionHeader(Building, "Anagrafica Azienda")}

                        <div className="row g-2">
                          {/* RAGIONE SOCIALE */}

                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">
                              RAGIONE SOCIALE *
                            </Form.Label>

                            <Form.Control
                              name="ragsoc"
                              value={formData.ragsoc}
                              onChange={handleChange}
                              className="uw-input"
                              required
                            />
                          </div>

                          {/* CODICE CLIENTE */}

                          {formData.codice_cliente && (
                            <div className="col-12 col-md-6">
                              <Form.Label className="uw-label">
                                CODICE CLIENTE
                              </Form.Label>

                              <Form.Control
                                value={formData.codice_cliente}
                                className="uw-input"
                                readOnly
                              />
                            </div>
                          )}

                          {/* PEC */}

                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">PEC</Form.Label>

                            <Form.Control
                              name="pec"
                              type="email"
                              value={formData.pec}
                              onChange={handleChange}
                              className="uw-input"
                            />
                          </div>

                          {/* PARTITA IVA */}

                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">
                              PARTITA IVA
                            </Form.Label>

                            <Form.Control
                              name="piva"
                              value={formData.piva}
                              onChange={handleChange}
                              className="uw-input"
                            />
                          </div>

                          {/* TIPO CONTATTO */}

                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">
                              TIPO CONTATTO *
                            </Form.Label>

                            <Form.Select
                              name="tpcontatto"
                              value={formData.tpcontatto}
                              onChange={handleChange}
                              className="uw-input"
                              required
                            >
                              <option value="">— Nessuno —</option>

                              {lookups.tipiContatto.map((row, index) => {
                                const value = optionValue(
                                  row,

                                  "tipo_contatto",

                                  "codice",

                                  "id_tipo_contatto",
                                );

                                const label =
                                  optionValue(
                                    row,

                                    "descrizione",

                                    "tipo_contatto",
                                  ) || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {label}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>

                          {/* CLIENTE / FORNITORE */}

                          <div className="col-12 d-flex flex-wrap gap-4 pt-1">
                            <Form.Check
                              type="checkbox"
                              name="is_cliente"
                              checked={formData.is_cliente}
                              onChange={handleChange}
                              label="Cliente"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              name="is_fornitore"
                              checked={formData.is_fornitore}
                              onChange={handleChange}
                              label="Fornitore"
                              className="uw-checkbox"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="p-3 mb-3 uw-section-box">
                        {renderSectionHeader(MapPin, "Sede e Località")}

                        <div className="row g-2">
                          <div className="col-12">
                            <Form.Label className="uw-label">
                              INDIRIZZO
                            </Form.Label>

                            <Form.Control
                              name="indirizzo"
                              value={formData.indirizzo}
                              onChange={handleChange}
                              className="uw-input"
                            />
                          </div>

                          <div className="col-12 col-md-5">
                            <Form.Label className="uw-label">
                              LOCALITÀ
                            </Form.Label>

                            <Form.Control
                              name="localita"
                              value={formData.localita}
                              onChange={handleChange}
                              className="uw-input"
                            />
                          </div>

                          <div className="col-6 col-md-3">
                            <Form.Label className="uw-label">
                              PROVINCIA
                            </Form.Label>

                            <Form.Select
                              name="provincia"
                              value={formData.provincia}
                              onChange={handleChange}
                              className="uw-input"
                              disabled={!isItalyNation}
                              title={
                                isItalyNation
                                  ? "Seleziona la provincia"
                                  : "La provincia è disponibile solo per l'Italia"
                              }
                            >
                              <option value="">
                                {isItalyNation ? "—" : "Non disponibile"}
                              </option>

                              {lookups.province.map((row, index) => {
                                const value = optionValue(row, "provincia");

                                const label =
                                  optionValue(row, "descrizione") || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {value ? `${value} — ${label}` : label}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>

                          <div className="col-6 col-md-4">
                            <Form.Label className="uw-label">CAP</Form.Label>

                            <Form.Control
                              name="cap"
                              value={formData.cap}
                              onChange={handleChange}
                              className="uw-input"
                            />
                          </div>

                          <div className="col-12 col-md-4">
                            <Form.Label className="uw-label">
                              NAZIONE *
                            </Form.Label>

                            <Form.Select
                              name="id_nazione"
                              value={formData.id_nazione}
                              onChange={handleChange}
                              className="uw-input"
                              required
                            >
                              <option value="">—</option>

                              {lookups.nazioni.map((row, index) => {
                                const value = optionValue(row, "id_nazione");

                                const descr = optionValue(row, "descrizione");

                                const iso = optionValue(row, "codice_iso");

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {descr}

                                    {iso ? ` (${iso})` : ""}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>

                          <div className="col-12 col-md-4">
                            <Form.Label className="uw-label">
                              LINGUA *
                            </Form.Label>

                            <Form.Select
                              name="id_lingua"
                              value={formData.id_lingua}
                              onChange={handleChange}
                              className="uw-input"
                              required
                            >
                              <option value="">—</option>

                              {lookups.lingue.map((row, index) => {
                                const value = optionValue(row, "id_lingua");

                                const descr = optionValue(row, "descrizione");

                                const codice = optionValue(row, "codice");

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {descr}

                                    {codice ? ` (${codice})` : ""}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 mb-3 uw-section-box">
                        {renderSectionHeader(Wrench, "Settori Trattati")}

                        <div className="d-flex flex-wrap gap-4">
                          <Form.Check
                            type="checkbox"
                            name="meccanica"
                            checked={formData.meccanica}
                            onChange={handleChange}
                            label="Meccanica"
                            className="uw-checkbox"
                          />

                          <Form.Check
                            type="checkbox"
                            name="carrozzeria"
                            checked={formData.carrozzeria}
                            onChange={handleChange}
                            label="Carrozzeria"
                            className="uw-checkbox"
                          />

                          <Form.Check
                            type="checkbox"
                            name="vetro"
                            checked={formData.vetro}
                            onChange={handleChange}
                            label="Vetro"
                            className="uw-checkbox"
                          />
                        </div>
                      </div>

                    </div>

                    <div className="col-12 col-xl-6">
                      <div className="p-3 mb-3 uw-section-box">
                        {renderSectionHeader(Settings, "parametri web")}

                        <div className="row g-2">
                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">
                              TIPO TRASPORTO
                            </Form.Label>

                            <Form.Select
                              name="tipo_trasporto"
                              value={formData.tipo_trasporto}
                              onChange={handleChange}
                              className="uw-input"
                            >
                              <option value="">— Nessuno —</option>

                              {lookups.tipiTrasporto.map((row, index) => {
                                const value = optionValue(
                                  row,

                                  "tipo_trasporto",

                                  "codice",
                                );

                                const label =
                                  optionValue(
                                    row,

                                    "descrizione",

                                    "tipo_trasporto",
                                  ) || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {label}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>

                          <div className="col-12 col-md-6 d-flex flex-column gap-1 pt-md-4">
                            <Form.Check
                              type="checkbox"
                              name="odpv_inviare_packing_list"
                              checked={formData.odpv_inviare_packing_list}
                              onChange={handleChange}
                              label="Inviare packing list"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              name="odpv_inserire_note_ordine"
                              checked={formData.odpv_inserire_note_ordine}
                              onChange={handleChange}
                              label="Inserire note ordine"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              name="odpv_inviare_ddt"
                              checked={formData.odpv_inviare_ddt}
                              onChange={handleChange}
                              label="Inviare DDT"
                              className="uw-checkbox"
                            />
                          </div>

                          <div className="col-12 d-flex flex-wrap gap-3 pt-1">
                            <Form.Check
                              type="checkbox"
                              name="autorizza_ordini_normali"
                              checked={formData.autorizza_ordini_normali}
                              onChange={handleChange}
                              label="Ordini normali"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              name="autorizza_ordini_urgenti"
                              checked={formData.autorizza_ordini_urgenti}
                              onChange={handleChange}
                              label="Ordini urgenti"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              name="scarica_stock"
                              checked={formData.scarica_stock}
                              onChange={handleChange}
                              label="Scarica stock"
                              className="uw-checkbox"
                            />
                          </div>
                        </div>

                                              <div className="p-3 mb-3 uw-section-box">
                        {renderSectionHeader(Package, "Dettagli logistici")}

                        <div className="row g-2">
                          <div className="col-12 d-flex flex-wrap gap-4">
                            <Form.Check
                              type="checkbox"
                              name="odp_pesare_merce"
                              checked={formData.odp_pesare_merce}
                              onChange={handleChange}
                              label="Pesare merce"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              name="odp_film_trasparente"
                              checked={formData.odp_film_trasparente}
                              onChange={handleChange}
                              label="Film trasparente"
                              className="uw-checkbox"
                            />
                          </div>

                          {[
                            ["odp_origine_merci", "Origine merci"],

                            ["odp_palette_fumegate", "Palette fumigate"],

                            ["odp_carico_sfuso", "Carico sfuso"],
                          ].map(([name, label]) => (
                            <div className="col-12 col-md-4" key={name}>
                              <Form.Label className="uw-label">
                                {label.toUpperCase()}
                              </Form.Label>

                              <Form.Select
                                value={
                                  formData[name] === null
                                    ? ""
                                    : formData[name]
                                      ? "1"
                                      : "0"
                                }
                                onChange={(e) =>
                                  handleNullableBool(name, e.target.value)
                                }
                                className="uw-input"
                              >
                                <option value="">Non impostato</option>

                                <option value="1">Sì</option>

                                <option value="0">No</option>
                              </Form.Select>
                            </div>
                          ))}
                        </div>
                      </div>

                      </div>

                      <div className="p-3 mb-3 uw-section-box">
                        {renderSectionHeader(CreditCard, "Stock / Credito")}

                        <div className="row g-2">
                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">
                              CATALOGO PREDEFINITO
                            </Form.Label>

                            <Form.Select
                              name="id_cat_predefinito"
                              value={formData.id_cat_predefinito}
                              onChange={handleChange}
                              className="uw-input"
                            >
                              <option value="">— Nessuno —</option>

                              {lookups.stockPredefiniti.map((row, index) => {
                                const value = optionValue(
                                  row,

                                  "id_cat_predefinito",
                                );

                                const label =
                                  optionValue(row, "descrizione") || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {label}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>

                          <div className="col-6 col-md-3">
                            <Form.Label className="uw-label">
                              FIDO CREDITSAFE
                            </Form.Label>

                            <div className="input-group">
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                name="fido_credit_safe"
                                value={formData.fido_credit_safe}
                                onChange={handleChange}
                                className="uw-input"
                              />
                              <span className="input-group-text uw-euro-suffix">
                                €
                              </span>
                            </div>
                          </div>

                          <div className="col-6 col-md-3">
                            <Form.Label className="uw-label">
                              ASSICURAZIONE SACE
                            </Form.Label>

                            <div className="input-group">
                              <Form.Control
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                name="assicurazione_SACE"
                                value={formData.assicurazione_SACE}
                                onChange={handleChange}
                                className="uw-input"
                              />
                              <span className="input-group-text uw-euro-suffix">
                                €
                              </span>
                            </div>
                          </div>

                          <div className="col-12 d-flex flex-wrap gap-3 pt-1">
                            <Form.Check
                              type="checkbox"
                              name="stock_visualizza_disp_attuale"
                              checked={formData.stock_visualizza_disp_attuale}
                              onChange={handleChange}
                              label="Disponibilità attuale"
                              className="uw-checkbox"
                            />
                          </div>
                        </div>
                      </div>

                      {!isExisting && (
                        <>
                          <div className="p-3 mb-3 uw-section-box">
                            {renderSectionHeader(
                              FileText,

                              "Dati Commerciali",
                            )}

                            <div className="row g-2">
                              <div className="col-12 col-md-6">
                                <Form.Label className="uw-label">
                                  BANCA
                                </Form.Label>

                                <Form.Control
                                  name="banca"
                                  value={formData.banca}
                                  onChange={handleChange}
                                  className="uw-input"
                                />
                              </div>

                              <div className="col-12 col-md-6">
                                <Form.Label className="uw-label">
                                  IBAN
                                </Form.Label>

                                <Form.Control
                                  name="iban"
                                  value={formData.iban}
                                  onChange={handleChange}
                                  className="uw-input"
                                />
                              </div>

                              <div className="col-12 col-md-5">
                                <Form.Label className="uw-label">
                                  MODALITÀ PAGAMENTO
                                </Form.Label>

                                <Form.Control
                                  name="mpaga"
                                  value={formData.mpaga}
                                  onChange={handleChange}
                                  className="uw-input"
                                />
                              </div>

                              <div className="col-6 col-md-3">
                                <Form.Label className="uw-label">
                                  SDI
                                </Form.Label>

                                <Form.Control
                                  name="sdi"
                                  value={formData.sdi}
                                  onChange={handleChange}
                                  className="uw-input text-uppercase"
                                  maxLength={7}
                                />
                              </div>

                              <div className="col-6 col-md-4">
                                <Form.Label className="uw-label">
                                  FIDO CONSIGLIATO
                                </Form.Label>

                                <Form.Control
                                  name="fcons"
                                  value={formData.fcons}
                                  onChange={handleChange}
                                  className="uw-input"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {/* ─────────────────────────── FIERE ──────────────────────────── */}
                {activeTab === "fiere" && (
                  <fieldset
                    id="uw-sezione-fiere"
                    className="uw-tab-panel-fieldset uw-anchor-section uw-direct-edit-view"
                  >

                    <div>
                      <div
                        ref={fiereEditorRef}
                        className="p-3 mb-3 uw-section-box"
                      >
                        {renderSectionHeader(CalendarDays, "Fiere")}

                        {!currentId && (
                          <div className="uw-draft-hint mb-3">
                            In creazione vengono mostrate solo le fiere che
                            contengono l'anno corrente ({currentYear}).
                          </div>
                        )}

                        <div className="row g-2 align-items-end">
                          <div className="col-12 col-lg-5">
                            <Form.Label className="uw-label">FIERA</Form.Label>

                            <Form.Select
                              value={selectedFiera}
                              onChange={(e) => setSelectedFiera(e.target.value)}
                              className="uw-input"
                              
                            >
                              <option value="">— Seleziona —</option>

                              {availableFiereForDropdown.map((row, index) => {
                                const value = optionValue(row, "id_fiera");

                                const label = fieraDisplayLabel(row) || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {label}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>

                          <div className="col-12 col-lg-2">
                            <button
                              type="button"
                              className="uw-action-btn uw-action-solid w-100"
                              onClick={addFiera}
                              disabled={!currentId || !selectedFiera || isBusy}
                            >
                              <Plus size={14} /> AGGIUNGI
                            </button>
                          </div>
                        </div>
                      </div>

                      <div
                        ref={fiereListRef}
                        className="uw-table-wrap uw-matched-list"
                      >
                        <Table
                          responsive
                          size="sm"
                          className="uw-table mb-0 align-middle"
                        >
                          <thead>
                            <tr>
                              <th className="p-2">Fiera</th>

                              <th className="p-2" style={{ width: 150 }}>
                                Azioni
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {fiere.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={3}
                                  className="text-center text-muted py-4"
                                >
                                  Nessuna fiera associata.
                                </td>
                              </tr>
                            ) : (
                              fiere.map((row) => {
                                const id = Number(
                                  pick(row, "id_partecipazione"),
                                );

                                const hasNote = Boolean(
                                  textValue(pick(row, "note")).trim(),
                                );

                                return (
                                  <tr key={id}>
                                    <td className="fw-semibold p-2">
                                      {fieraDisplayLabel(row)}
                                    </td>

                                    <td className="text-end">
                                      <button
                                        type="button"
                                        className="uw-mini-btn danger p-2"
                                        onClick={() => deleteFiera(row)}
                                        disabled={isBusy}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </fieldset>
                )}
                {/* ─────────────────────────── MARCHI ─────────────────────────── */}
                {activeTab === "marchi" && (
                  <fieldset
                    id="uw-sezione-marchi"
                    className="uw-tab-panel-fieldset uw-anchor-section uw-direct-edit-view"
                  >

                    <div className="row g-3">
                      <div className="col-12 col-xl-6">
                        <div className="p-3 mb-3 uw-section-box">
                          {renderSectionHeader(
                            Tags,

                            "Marchi Richiesti Cliente",

                            <div className="d-flex gap-1 flex-wrap justify-content-end">
                              <button
                                type="button"
                                className="uw-mini-btn"
                                onClick={() => addPresetMarchi("marchi_oe")}
                                disabled={isBusy}
                              >
                                + OE
                              </button>

                              <button
                                type="button"
                                className="uw-mini-btn danger"
                                onClick={deleteAllMarchiRichiesti}
                                disabled={isBusy}
                              >
                                SVUOTA
                              </button>
                            </div>,
                          )}

                          <div className="d-flex gap-2 mb-2">
                            <Form.Select
                              value={marchioRichiestoAdd}
                              onChange={(e) =>
                                setMarchioRichiestoAdd(e.target.value)
                              }
                              className="uw-input"
                            >
                              <option value="">— Seleziona marchio —</option>

                              {lookups.marchi.map((row, index) => {
                                const value = optionValue(row, "precodice");

                                const label =
                                  optionValue(row, "descrizione") || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {value} — {label}
                                  </option>
                                );
                              })}
                            </Form.Select>

                            <button
                              type="button"
                              className="uw-action-btn"
                              onClick={addMarchioRichiesto}
                              disabled={!marchioRichiestoAdd || isBusy}
                            >
                              <Plus size={14} /> AGGIUNGI
                            </button>
                          </div>
                        </div>

                        <div className="uw-table-wrap">
                          <Table
                            responsive
                            size="sm"
                            className="uw-table mb-0 align-middle"
                          >
                            <thead>
                              <tr>
                                <th>Prec.</th>

                                <th>Descrizione</th>

                                <th>Email</th>

                                <th>Web</th>

                                <th>Ord.</th>

                                <th />
                              </tr>
                            </thead>

                            <tbody>
                              {visibleMarchiRichiesti.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={6}
                                    className="text-center text-muted py-4"
                                  >
                                    Nessun marchio richiesto.
                                  </td>
                                </tr>
                              ) : (
                                visibleMarchiRichiesti.map((row, index) => {
                                  const id = Number(
                                    pick(row, "id_marchi_trattati"),
                                  );

                                  const precodice = textValue(
                                    pick(row, "Prec", "precodice"),
                                  )
                                    .trim()

                                    .toUpperCase();

                                  const isDraft = Boolean(row?._draft);

                                  return (
                                    <tr
                                      key={id || `draft-${precodice}-${index}`}
                                    >
                                      <td className="fw-semibold">
                                        {precodice}
                                      </td>

                                      <td>
                                        {textValue(
                                          pick(
                                            row,

                                            "Descrizione",

                                            "descrizione",
                                          ),
                                        )}
                                      </td>

                                      <td>
                                        {isDraft ? (
                                          <span className="uw-draft-badge">
                                            DA SALVARE
                                          </span>
                                        ) : (
                                          textValue(pick(row, "Email"))
                                        )}
                                      </td>

                                      <td>
                                        <Form.Check
                                          type="checkbox"
                                          checked={boolValue(pick(row, "Web"))}
                                          onChange={(e) =>
                                            updateMarchioRow(
                                              id,

                                              "Web",

                                              e.target.checked,
                                            )
                                          }
                                          className="uw-checkbox"
                                          disabled={isDraft}
                                        />
                                      </td>

                                      <td>
                                        <Form.Check
                                          type="checkbox"
                                          checked={boolValue(pick(row, "Ord"))}
                                          onChange={(e) =>
                                            updateMarchioRow(
                                              id,

                                              "Ord",

                                              e.target.checked,
                                            )
                                          }
                                          className="uw-checkbox"
                                          disabled={isDraft}
                                        />
                                      </td>

                                      <td className="text-end text-nowrap">
                                        {isDraft ? (
                                          <button
                                            type="button"
                                            className="uw-mini-btn danger"
                                            onClick={() =>
                                              handleRemoveInitialMarchio(
                                                precodice,
                                              )
                                            }
                                            title="Rimuovi dalla lista da salvare"
                                          >
                                            <X size={12} />
                                          </button>
                                        ) : (
                                          <>
                                            <button
                                              type="button"
                                              className="uw-mini-btn me-1"
                                              onClick={() =>
                                                saveMarchioRichiesto(row)
                                              }
                                            >
                                              <Save size={12} />
                                            </button>

                                            <button
                                              type="button"
                                              className="uw-mini-btn danger"
                                              onClick={() =>
                                                deleteMarchioRichiesto(row)
                                              }
                                            >
                                              <Trash2 size={12} />
                                            </button>
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </div>

                      <div className="col-12 col-xl-6">
                        <div className="p-3 mb-3 uw-section-box">
                          {renderSectionHeader(
                            Package,

                            "Marchi Forniti",

                            <div className="d-flex gap-1 flex-wrap justify-content-end">
                              <button
                                type="button"
                                className="uw-mini-btn"
                                onClick={addPresetMarchiFornitiOE}
                                disabled={isBusy}
                              >
                                + OE
                              </button>

                              <button
                                type="button"
                                className="uw-mini-btn danger"
                                onClick={deleteAllMarchiForniti}
                                disabled={isBusy}
                              >
                                SVUOTA
                              </button>
                            </div>,
                          )}

                          <div className="d-flex gap-2">
                            <Form.Select
                              value={marchioFornitoAdd}
                              onChange={(e) =>
                                setMarchioFornitoAdd(e.target.value)
                              }
                              className="uw-input"
                            >
                              <option value="">— Seleziona marchio —</option>

                              {lookups.marchi.map((row, index) => {
                                const value = optionValue(row, "precodice");

                                const label =
                                  optionValue(row, "descrizione") || value;

                                return (
                                  <option
                                    key={`${value}-${index}`}
                                    value={value}
                                  >
                                    {value} — {label}
                                  </option>
                                );
                              })}
                            </Form.Select>

                            <button
                              type="button"
                              className="uw-action-btn"
                              onClick={addMarchioFornito}
                              disabled={!marchioFornitoAdd || isBusy}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>

                        <div className="uw-table-wrap">
                          <Table
                            responsive
                            size="sm"
                            className="uw-table mb-0 align-middle"
                          >
                            <thead>
                              <tr>
                                <th>Prec.</th>

                                <th>Descrizione</th>

                                <th />
                              </tr>
                            </thead>

                            <tbody>
                              {visibleMarchiForniti.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={3}
                                    className="text-center text-muted py-4"
                                  >
                                    Nessun marchio fornito.
                                  </td>
                                </tr>
                              ) : (
                                visibleMarchiForniti.map((row, index) => {
                                  const id = Number(
                                    pick(row, "id_marchi_trattati"),
                                  );

                                  const isDraft = Boolean(row?._draft);

                                  const precodice = textValue(
                                    pick(row, "precodice", "Prec"),
                                  )
                                    .trim()
                                    .toUpperCase();

                                  return (
                                    <tr
                                      key={
                                        isDraft
                                          ? `fornito-draft-${precodice}-${index}`
                                          : id || `${precodice}-${index}`
                                      }
                                    >
                                      <td className="fw-semibold">
                                        {precodice}

                                        {isDraft && (
                                          <span className="uw-draft-badge ms-2">
                                            DA SALVARE
                                          </span>
                                        )}
                                      </td>

                                      <td>
                                        {textValue(pick(row, "descrizione"))}
                                      </td>

                                      <td className="text-end">
                                        {isDraft ? (
                                          <button
                                            type="button"
                                            className="uw-mini-btn danger"
                                            onClick={() =>
                                              setMarchiFornitiPending((prev) =>
                                                prev.filter(
                                                  (value) =>
                                                    textValue(value)
                                                      .trim()

                                                      .toUpperCase() !==
                                                    precodice,
                                                ),
                                              )
                                            }
                                          >
                                            <Trash2 size={12} />
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="uw-mini-btn danger"
                                            onClick={() =>
                                              deleteMarchioFornito(row)
                                            }
                                          >
                                            <Trash2 size={12} />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                )}
                {/* ────────────────────────── RUBRICA ─────────────────────────── */}
                {activeTab === "rubrica" && (
                  <fieldset
                    id="uw-sezione-rubrica"
                    className="uw-tab-panel-fieldset uw-anchor-section uw-direct-edit-view"
                  >
                    <div>
                      <div
                        ref={rubricaEditorRef}
                        className="p-3 mb-3 uw-section-box"
                      >
                        {renderSectionHeader(
                          Phone,

                          rubricaDraft.id_telefono_cont
                            ? "Modifica Riferimento"
                            : rubricaPendingEditIndex !== null
                              ? "Modifica Riferimento da salvare"
                              : "Rubrica / Nuovo Riferimento",
                        )}

                        <div className="row g-2">
                          <div className="col-12 col-md-3">
                            <Form.Label className="uw-label">
                              INTERLOCUTORE
                            </Form.Label>

                            <Form.Control
                              value={rubricaDraft.interlocutore}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  interlocutore: e.target.value,
                                }))
                              }
                              className="uw-input"
                            />
                          </div>

                          <div className="col-12 col-md-3">
                            <Form.Label className="uw-label">
                              QUALIFICA
                            </Form.Label>

                            <Form.Select
                              value={rubricaDraft.qualifica}
                              onChange={(e) => {
                                const value = e.target.value;
                                setRubricaDraft((p) => ({
                                  ...p,
                                  qualifica: value,
                                  qualifica_custom:
                                    value === RUBRICA_QUALIFICA_OTHER
                                      ? p.qualifica_custom
                                      : "",
                                }));
                              }}
                              className="uw-input"
                            >
                              <option value="">— Seleziona —</option>
                              {RUBRICA_QUALIFICA_OPTIONS.map((qualifica) => (
                                <option key={qualifica} value={qualifica}>
                                  {qualifica}
                                </option>
                              ))}
                              <option value={RUBRICA_QUALIFICA_OTHER}>
                                Other...
                              </option>
                            </Form.Select>

                            {rubricaDraft.qualifica ===
                              RUBRICA_QUALIFICA_OTHER && (
                              <Form.Control
                                value={rubricaDraft.qualifica_custom}
                                onChange={(e) =>
                                  setRubricaDraft((p) => ({
                                    ...p,
                                    qualifica_custom: e.target.value,
                                  }))
                                }
                                placeholder="Scrivi la mansione"
                                className="uw-input mt-2"
                              />
                            )}
                          </div>

                          <div className="col-12 col-md-2">
                            <Form.Label className="uw-label">TIPO</Form.Label>
                            <Form.Select
                              value={rubricaDraft.tipo}
                              onChange={(e) => {
                                const nextTipo = e.target.value;

                                setRubricaDraft((p) => ({
                                  ...p,
                                  tipo: nextTipo,
                                  interlocutore:
                                    !textValue(p.interlocutore).trim() &&
                                    rubricaTipoIsWeb(
                                      nextTipo,
                                      lookups.rubricaTipi,
                                    )
                                      ? formData.ragsoc
                                      : p.interlocutore,
                                }));
                              }}
                              className="uw-input"
                            >
                              <option value="">—</option>

                              {lookups.rubricaTipi

                                .filter((row) => {
                                  const value = String(
                                    optionValue(
                                      row,
                                      "telefono_tipo",
                                      "tipo",
                                      "codice",
                                    ) || "",
                                  ).toLowerCase();

                                  const label = String(
                                    optionValue(row, "descrizione") || value,
                                  ).toLowerCase();

                                  return (
                                    value.includes("cell") ||
                                    value.includes("telefon") ||
                                    value.includes("mail") ||
                                    value.includes("web") ||
                                    label.includes("cell") ||
                                    label.includes("telefon") ||
                                    label.includes("mail") ||
                                    label.includes("web")
                                  );
                                })

                                .map((row, index) => {
                                  const value = optionValue(
                                    row,

                                    "telefono_tipo",

                                    "tipo",

                                    "codice",
                                  );

                                  const label =
                                    optionValue(row, "descrizione") || value;

                                  return (
                                    <option
                                      key={`${value}-${index}`}
                                      value={value}
                                    >
                                      {label}
                                    </option>
                                  );
                                })}
                            </Form.Select>{" "}
                          </div>

                          <div className="col-12 col-md-4">
                            <Form.Label className="uw-label">
                              NUMERO / EMAIL *
                            </Form.Label>

                            <Form.Control
                              value={rubricaDraft.num_riferimento}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  num_riferimento: e.target.value,
                                }))
                              }
                              className="uw-input"
                            />
                          </div>

                          <div className="col-12 col-md-6">
                            <Form.Label className="uw-label">
                              DESCRIZIONE
                            </Form.Label>

                            <Form.Control
                              value={rubricaDraft.descrizione}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  descrizione: e.target.value,
                                }))
                              }
                              className="uw-input"
                            />
                          </div>

                          <div className="col-12 col-md-6 d-flex flex-wrap gap-3 align-items-end pb-1">
                            <Form.Check
                              type="checkbox"
                              checked={rubricaDraft.notifiche}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  notifiche: e.target.checked,
                                }))
                              }
                              label="Notifiche"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              checked={rubricaDraft.comunicazioni}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  comunicazioni: e.target.checked,
                                }))
                              }
                              label="Comunicazioni"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              checked={rubricaDraft.invio_doc}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  invio_doc: e.target.checked,
                                }))
                              }
                              label="Invio doc"
                              className="uw-checkbox"
                            />

                            <Form.Check
                              type="checkbox"
                              checked={rubricaDraft.valido}
                              onChange={(e) =>
                                setRubricaDraft((p) => ({
                                  ...p,

                                  valido: e.target.checked,
                                }))
                              }
                              label="Valido"
                              className="uw-checkbox"
                            />
                          </div>

                          <div className="col-12 d-flex justify-content-end gap-2">
                            {(rubricaDraft.id_telefono_cont ||
                              rubricaPendingEditIndex !== null) && (
                              <button
                                type="button"
                                className="uw-action-btn"
                                onClick={() => {
                                  setRubricaDraft(EMPTY_RUBRICA);

                                  setRubricaPendingEditIndex(null);
                                }}
                              >
                                <X size={14} /> ANNULLA
                              </button>
                            )}

                            <button
                              type="button"
                              className="uw-action-btn uw-action-solid"
                              onClick={saveRubrica}
                              disabled={isBusy}
                            >
                              <Save size={14} />{" "}
                              {rubricaDraft.id_telefono_cont
                                ? "AGGIORNA"
                                : !currentId
                                  ? rubricaPendingEditIndex !== null
                                    ? "AGGIORNA LISTA"
                                    : "AGGIUNGI ALLA LISTA"
                                  : "AGGIUNGI"}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div
                        ref={rubricaListRef}
                        className="uw-table-wrap uw-matched-list uw-rubrica-table-frame"
                      >
                        <div className="uw-rubrica-table-scroll">
                          <Table
                            size="sm"
                            className="uw-table mb-0 align-middle"
                          >
                            <thead>
                              <tr>
                                <th>Interlocutore</th>

                                <th>Qualifica</th>

                                <th>Tipo</th>

                                <th>Riferimento</th>

                                <th>Descrizione</th>

                                <th>Stato</th>

                                <th />
                              </tr>
                            </thead>

                            <tbody>
                              {visibleRubrica.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={7}
                                    className="text-center text-muted py-4"
                                  >
                                    Rubrica vuota.
                                  </td>
                                </tr>
                              ) : (
                                visibleRubrica.map((row, index) => {
                                  const localId = numberOrNull(
                                    pick(row, "id_telefono_cont"),
                                  );

                                  const infinityId = numberOrNull(
                                    pick(row, "id_telefono"),
                                  );

                                  const isDraft = Boolean(row?._draft);

                                  const draftIndex = numberOrNull(
                                    row?._draftIndex,
                                  );

                                  return (
                                    <tr
                                      key={
                                        isDraft
                                          ? `draft-${draftIndex}`
                                          : localId
                                            ? `ut-${localId}`
                                            : infinityId
                                              ? `infinity-${infinityId}`
                                              : `rubrica-${index}`
                                      }
                                    >
                                      <td className="fw-semibold">
                                        {textValue(pick(row, "interlocutore"))}
                                      </td>

                                      <td>
                                        {textValue(pick(row, "qualifica"))}
                                      </td>

                                      <td>
                                        {textValue(
                                          pick(row, "Tipo", "codice_tipo"),
                                        )}
                                      </td>

                                      <td>
                                        {textValue(
                                          pick(row, "num_riferimento"),
                                        )}
                                      </td>

                                      <td>
                                        {textValue(pick(row, "descrizione"))}
                                      </td>

                                      <td>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          {!isDraft &&
                                            !localId &&
                                            infinityId && (
                                              <span className="uw-draft-badge">
                                                INFINITY
                                              </span>
                                            )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                )}{" "}
                {/* ─────────────────────────── STOCK ──────────────────────────── */}
                {activeTab === "stock" && (
                  <div>
                    <div className="uw-table-wrap">
                      <Table
                        responsive
                        size="sm"
                        className="uw-table mb-0 align-middle"
                      >
                        <thead>
                          <tr>
                            <th>Email</th>

                            <th>Precodice</th>

                            <th>Marchio</th>

                            <th>Predefinito</th>

                            <th />
                          </tr>
                        </thead>

                        <tbody>
                          {stock.length === 0 ? (
                            <tr>
                              <td
                                colSpan={5}
                                className="text-center text-muted py-4"
                              >
                                Nessun invio stock configurato.
                              </td>
                            </tr>
                          ) : (
                            stock.map((row, index) => {
                              const id = Number(
                                pick(row, "id_cat_invio_email"),
                              );

                              return (
                                <tr key={id || index}>
                                  <td>{textValue(pick(row, "Email"))}</td>

                                  <td className="fw-semibold">
                                    {textValue(pick(row, "precodice"))}
                                  </td>

                                  <td>{textValue(pick(row, "Marchio"))}</td>

                                  <td>{textValue(pick(row, "Predefinito"))}</td>

                                  <td className="text-end">
                                    <button
                                      type="button"
                                      className="uw-mini-btn danger"
                                      onClick={() => deleteStock(row)}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
                {/* ─────────────────────────── NOTE ───────────────────────────── */}
                {activeTab === "note" && (
                  <fieldset
                    id="uw-sezione-note"
                    className="uw-tab-panel-fieldset uw-anchor-section uw-direct-edit-view"
                  >

                    <div>
                      <div
                        ref={noteEditorRef}
                        className="p-3 mb-3 uw-section-box"
                      >
                        {renderSectionHeader(
                          FileText,

                          currentId ? "Aggiungi Nota" : "Nota aggiuntiva",
                        )}

<div className="row g-2 mb-2">
  <div className="col-12 col-lg-6">
    <Form.Label className="uw-label">
      TITOLO *
    </Form.Label>

    <Form.Select
      value={newNoteTitleType}
      onChange={(e) => {
        const value = e.target.value;

        setNewNoteTitleType(value);

        if (value !== "free") {
          setNewNoteTitle("");
        }

        if (value !== "fair") {
          setNewNoteFiera("");
        }
      }}
      className="uw-input"
    >
      {noteTitleOptions.map((option) => (
        <option
          key={option.value}
          value={option.value}
        >
          {option.label}
        </option>
      ))}
    </Form.Select>
  </div>

  {newNoteTitleType === "free" && (
    <div className="col-12 col-lg-6">
      <Form.Label className="uw-label">
        TITOLO LIBERO *
      </Form.Label>

      <Form.Control
        value={newNoteTitle}
        onChange={(e) =>
          setNewNoteTitle(e.target.value)
        }
        className="uw-input"
        placeholder="Scrivi il titolo..."
      />
    </div>
  )}

  {newNoteTitleType === "fair" && (
    <div className="col-12 col-lg-6">
      <Form.Label className="uw-label">
        FIERA *
      </Form.Label>

      <Form.Select
        value={newNoteFiera}
        onChange={(e) =>
          setNewNoteFiera(e.target.value)
        }
        className="uw-input"
        required
      >
        <option value="">
          — Seleziona obbligatoriamente una fiera —
        </option>

        {availableFiereForDropdown.map(
          (row, index) => {
            const value = optionValue(
              row,
              "id_fiera",
            );

            return (
              <option
                key={`nota-fiera-${value}-${index}`}
                value={value}
              >
                {fieraDisplayLabel(row)}
              </option>
            );
          },
        )}
      </Form.Select>

      {!newNoteFiera && (
        <div className="small text-warning mt-1">
          Se scegli "Fiera" devi indicare la fiera.
        </div>
      )}
    </div>
  )}

  {newNoteTitleType === "call" && (
    <div className="col-12 col-lg-6">
      <Form.Label className="uw-label">
        TITOLO
      </Form.Label>

      <div className="uw-note-generated-title">
        Call con{" "}
        <strong>
          {formData.ragsoc || "cliente"}
        </strong>
      </div>
    </div>
  )}

  {newNoteTitleType === "meeting" && (
    <div className="col-12 col-lg-6">
      <Form.Label className="uw-label">
        TITOLO
      </Form.Label>

      <div className="uw-note-generated-title">
        Riunione presso Technoparts
      </div>
    </div>
  )}
</div>
                        <div className="row g-2 mb-2">
                          <div className="col-12 col-lg-6">
                            <Form.Label className="uw-label">
                              TIPO NOTA
                            </Form.Label>

                            <Form.Select
                              value={newNoteType}
                              onChange={(e) => setNewNoteType(e.target.value)}
                              className="uw-input"
                            >
                              {NOTE_TYPES.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>

                          <div className="col-12 col-lg-6 d-flex align-items-end">
                            <div className="small text-muted pb-2">
                              {
                                NOTE_TYPES.find(
                                  (item) => item.value === newNoteType,
                                )?.label
                              }

                              {newNoteType.startsWith("C_")
                                ? ` · ${newNoteType.substring(2)} viene usato come cod_note.`
                                : ""}
                            </div>
                          </div>
                        </div>

                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="uw-input mb-2"
                          placeholder="Nuova nota..."
                        />

                        <div className="mb-2">
                          <Form.Label className="uw-label">
                            CERCA FIERA
                          </Form.Label>
                          <Form.Select
                            value={noteFieraFilter}
                            onChange={(e) => setNoteFieraFilter(e.target.value)}
                            className="uw-input"
                          >
                            <option value="">— Tutte le fiere —</option>
                            {lookups.fiere.map((row, index) => {
                              const value = optionValue(row, "id_fiera");
                              return (
                                <option
                                  key={`filtro-note-fiera-${value}-${index}`}
                                  value={value}
                                >
                                  {fieraDisplayLabel(row)}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </div>

                        <div className="d-flex justify-content-end">
                          <button
                            type="button"
                            className="uw-action-btn uw-action-solid"
                            onClick={addNote}
                            disabled={
                              !newNoteTitle.trim() || !newNote.trim() || isBusy
                            }
                          >
                            {isBusy ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <Plus size={14} />
                            )}
                            AGGIUNGI NOTA
                          </button>
                        </div>
                      </div>

                      <div
                        ref={noteListRef}
                        className="d-flex flex-column gap-2 uw-note-list uw-matched-list"
                      >
                        <div className="uw-note-search-box">
                          <Form.Label className="uw-label mb-1">
                            CERCA NOTE
                          </Form.Label>
                          <Form.Control
                            type="search"
                            value={noteSearch}
                            onChange={(e) => setNoteSearch(e.target.value)}
                            className="uw-input"
                            placeholder="Cerca per titolo..."
                          />
                          <div className="small text-muted mt-1">
                            {filteredNotes.length} di {note.length} note
                          </div>
                        </div>

                       <div className="uw-note-status-filters">
  <button
    type="button"
    className={`uw-note-status-btn ${
      noteStatusFilter === "all" ? "active" : ""
    }`}
    onClick={() => setNoteStatusFilter("all")}
  >
    TUTTE
  </button>

  <button
    type="button"
    className={`uw-note-status-btn ${
      noteStatusFilter === "done" ? "active" : ""
    }`}
    onClick={() => setNoteStatusFilter("done")}
  >
    FATTE
  </button>

  <button
    type="button"
    className={`uw-note-status-btn ${
      noteStatusFilter === "todo" ? "active" : ""
    }`}
    onClick={() => setNoteStatusFilter("todo")}
  >
    DA FARE
  </button>
</div>

                        {note.length === 0 ? (
                          <div className="text-center text-muted py-4">
                            Nessuna nota presente.
                          </div>
                        ) : filteredNotes.length === 0 ? (
                          <div className="text-center text-muted py-4">
                            Nessuna nota corrisponde ai filtri selezionati.
                          </div>
                        ) : (
                          filteredNotes.map((row, index) => {
                            const parsed = parseStoredNote(pick(row, "nota"));
                            return (
                            <div
                              className={`uw-note-list-row ${
                                isNoteDone(row) ? "uw-note-list-row-done" : ""
                              }`}
                              key={`${textValue(pick(row, "id_nota"))}-${index}`}
                            >
                              <div className="uw-note-list-date">
                                {pick(row, "data_nota")
                                  ? new Date(
                                      pick(row, "data_nota"),
                                    ).toLocaleDateString("it-IT", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                    })
                                  : "—"}
                              </div>

                              <button
                                type="button"
                                className={`uw-note uw-note-title-btn ${
                                  isNoteDone(row) ? "uw-note-done" : ""
                                }`}
                                onClick={() => setSelectedNote(row)}
                                title="Apri nota"
                              >
                                <span className="uw-note-title-text">
                                  {parsed.title}
                                </span>

                                {isNoteDone(row) && (
                                  <span className="uw-note-done-badge">
                                    <CheckCircle2 size={12} />
                                    FATTO
                                  </span>
                                )}
                              </button>

                              <div className="uw-note-row-actions">
                                <button
                                  type="button"
                                  className="uw-note-row-action uw-note-row-action-edit"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openEditNote(row);
                                  }}
                                  title="Modifica nota"
                                  aria-label={`Modifica nota ${parsed.title}`}
                                  disabled={isBusy}
                                >
                                  <Edit3 size={15} />
                                </button>

                                <button
                                  type="button"
                                  className="uw-note-row-action uw-note-row-action-delete"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    deleteNote(row);
                                  }}
                                  title="Elimina nota"
                                  aria-label={`Elimina nota ${parsed.title}`}
                                  disabled={isBusy}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </fieldset>
                )}

                {/* ─────────────────────── BACKORDER ─────────────────────── */}

{activeTab === "backorder" && (
  <div className="uw-backorder-tab">
    {!currentId ? (
      <div className="uw-empty-state">
        <PackageSearch size={34} />

        <div>
          Salva prima l'anagrafica per visualizzare il Backorder.
        </div>
      </div>
    ) : !formData.codice_cliente ? (
      <div className="uw-empty-state">
        <PackageSearch size={34} />

        <div>
          Questa anagrafica non ha un codice cliente associato.
        </div>
      </div>
    ) : (
      <BackOrder
        embedded
        initialClientId={formData.codice_cliente}
        initialRagioneSociale={formData.ragsoc}
      />
    )}
  </div>
)}


                {/* ─────────────────────────── AZIONI FINALI ──────────────────── */}
                <div className="d-flex flex-wrap justify-content-end gap-2 pt-3 mt-2 uw-bottom-actions">
                  {activeTab === "anagrafica" && currentId && (
                    <button
                      type="button"
                      className="uw-action-btn uw-danger-btn"
                      onClick={deleteContact}
                      disabled={isBusy}
                    >
                      <Trash2 size={15} /> ELIMINA
                    </button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* ALERT GLOBALI */}

          {error && (
            <Alert
              variant="danger"
              className="d-flex align-items-center gap-3 uw-alert-toast mb-3"
              style={{
                backgroundColor: "#fbe4e4",

                color: "#8a2626",

                borderLeft: "4px solid #d9534f",
              }}
            >
              <AlertCircle size={18} className="flex-shrink-0" />

              <span className="fw-medium" style={{ fontSize: "0.85rem" }}>
                {error}
              </span>
            </Alert>
          )}

          {successMsg && (
            <div
              className="uw-success-popup-layer"
              role="status"
              aria-live="polite"
            >
              <div className="uw-success-popup">
                <div className="uw-success-popup-icon">
                  <CheckCircle2 size={28} />
                </div>

                <div className="uw-success-popup-content">
                  <div className="uw-success-popup-title">
                    Salvataggio completato
                  </div>

                  <div className="uw-success-popup-message">{successMsg}</div>
                </div>

                <button
                  type="button"
                  className="uw-success-popup-close"
                  onClick={() => setSuccessMsg("")}
                  aria-label="Chiudi"
                >
                  <X size={17} />
                </button>
              </div>
            </div>
          )}
        </Container>
      </div>

      <div
        className="uw-floating-dock"
        aria-label="Scorciatoie gestione contatto"
      >
        {/* Trigger visuale a riposo */}

        <div className="uw-floating-dock-trigger">
          <SlidersHorizontal size={18} />

          <span>Gestione Completa</span>
        </div>

        {/* Contenuto che si espande all'hover */}

        <div className="uw-floating-dock-content">
          <div className="uw-floating-dock-head">
            <div>
              <div className="uw-floating-dock-kicker">GESTIONE COMPLETA</div>

              <div className="uw-floating-dock-copy">
                Accesso rapido alla gestione completa, fiere e note del contatto.
              </div>
            </div>
          </div>

          <div className="uw-floating-links uw-floating-links-main">
            <button
              type="button"
              className="uw-floating-link uw-floating-fiera-add"
              onClick={() => setFieraCreateModalOpen(true)}
              title="Aggiungi una nuova fiera"
            >
              <CalendarDays size={15} />
              <span>Fiera</span>
            </button>

            <button
              type="button"
              className="uw-floating-link uw-floating-note-add"
              onClick={() => setNoteModalOpen(true)}
              title="Aggiungi nota"
            >
              <Plus size={15} />
              <span>Nota</span>
            </button>

            <button
              type="button"
              className="uw-floating-link uw-floating-top-btn"
              onClick={scrollToTop}
              title="Torna in alto"
              aria-label="Torna in alto"
            >
              <ArrowUp size={15} />
              <span>Torna su</span>
            </button>
          </div>
        </div>
      </div>

      <Modal
        show={fieraCreateModalOpen}
        onHide={() => {
          if (!fieraCreateBusy) {
            setFieraCreateModalOpen(false);
            setNewFieraDescrizione("");
          }
        }}
        centered
        backdrop="static"
        dialogClassName="uw-liquid-glass-dialog"
        contentClassName="uw-note-modal uw-liquid-glass-modal"
      >
        <Modal.Header
          closeButton={!fieraCreateBusy}
          className="uw-note-modal-header pb-0"
        >
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <CalendarDays size={14} />
            </span>
            Aggiungi Fiera
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <Form.Label className="uw-label">NOME FIERA *</Form.Label>

          <Form.Control
            value={newFieraDescrizione}
            onChange={(e) => setNewFieraDescrizione(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                newFieraDescrizione.trim() &&
                !fieraCreateBusy
              ) {
                e.preventDefault();
                createNewFiera();
              }
            }}
            className="uw-input"
            placeholder="Es. Automechanika Dubai 2027"
            autoFocus
            disabled={fieraCreateBusy}
          />
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer pt-0">
          <button
            type="button"
            className="uw-action-btn"
            onClick={() => {
              setFieraCreateModalOpen(false);
              setNewFieraDescrizione("");
            }}
            disabled={fieraCreateBusy}
          >
            <X size={14} />
            ANNULLA
          </button>

          <button
            type="button"
            className="uw-action-btn uw-action-solid"
            onClick={createNewFiera}
            disabled={!newFieraDescrizione.trim() || fieraCreateBusy}
          >
            {fieraCreateBusy ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Plus size={14} />
            )}
            CREA FIERA
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={noteModalOpen}
        onHide={() => setNoteModalOpen(false)}
        centered
        dialogClassName="uw-postit-modal-dialog uw-liquid-glass-dialog mx-auto"
        style={{
          display: "flex",

          alignItems: "center",

          justifyContent: "center",
        }}
        contentClassName="uw-note-modal uw-liquid-glass-modal"
      >
        <Modal.Header closeButton className="uw-note-modal-header pb-0">
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <FileText size={14} />
            </span>

            {currentId ? "Aggiungi Nota" : "Nota aggiuntiva"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-6">
              <Form.Label className="uw-label text-light">TITOLO *</Form.Label>
              <Form.Control
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                className="uw-input"
                placeholder="Titolo della nota..."
                autoFocus
              />
            </div>

            <div className="col-12 col-md-6">
              <Form.Label className="uw-label text-light">FIERA</Form.Label>
              <Form.Select
                value={newNoteFiera}
                onChange={(e) => setNewNoteFiera(e.target.value)}
                className="uw-input"
              >
                <option value="">— Nessuna fiera —</option>
                {availableFiereForDropdown.map((row, index) => {
                  const value = optionValue(row, "id_fiera");
                  return (
                    <option
                      key={`modal-nota-fiera-${value}-${index}`}
                      value={value}
                    >
                      {fieraDisplayLabel(row)}
                    </option>
                  );
                })}
              </Form.Select>
            </div>
          </div>

          <div className="mb-2">
            <Form.Label className="uw-label text-light">TIPO NOTA</Form.Label>

            <Form.Select
              value={newNoteType}
              onChange={(e) => setNewNoteType(e.target.value)}
              className="uw-input"
            >
              {NOTE_TYPES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="mb-1">
            <Form.Label className="uw-label">NOTA</Form.Label>

            <Form.Control
              as="textarea"
              rows={4}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="uw-input"
              placeholder="Scrivi nota..."
            />
          </div>
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer pt-0">
          <button
            type="button"
            className="uw-action-btn uw-action-solid"
            onClick={handleNoteModalConfirm}
            disabled={!newNoteTitle.trim() || !newNote.trim() || isBusy}
          >
            {isBusy ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Plus size={14} />
            )}
            AGGIUNGI NOTA
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(selectedFieraNote)}
        onHide={() => {
          setSelectedFieraNote(null);
          setFieraNoteDraft("");
        }}
        centered
        dialogClassName="uw-postit-modal-dialog uw-liquid-glass-dialog mx-auto"
        style={{
          display: "flex",

          alignItems: "center",

          justifyContent: "center",
        }}
        contentClassName="uw-note-modal uw-liquid-glass-modal"
      >
        <Modal.Header closeButton className="uw-note-modal-header">
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <CalendarDays size={14} />
            </span>
            {selectedFieraNote
              ? fieraDisplayLabel(selectedFieraNote)
              : "Nota Fiera"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <Form.Label className="uw-label">NOTA DELLA FIERA</Form.Label>

          <Form.Control
            as="textarea"
            rows={7}
            value={fieraNoteDraft}
            onChange={(e) => setFieraNoteDraft(e.target.value)}
            className="uw-input"
            placeholder="Nessuna nota presente..."
          />
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer">
          <button
            type="button"
            className="uw-action-btn uw-action-solid"
            onClick={saveFieraNoteFromModal}
            disabled={isBusy}
          >
            <Save size={14} /> SALVA NOTA
          </button>
          <button
            type="button"
            className="uw-action-btn"
            onClick={() => {
              setSelectedFieraNote(null);
              setFieraNoteDraft("");
            }}
          >
            CHIUDI
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(editingNote)}
        onHide={() => setEditingNote(null)}
        centered
        dialogClassName="uw-postit-modal-dialog uw-liquid-glass-dialog mx-auto"
        contentClassName="uw-note-modal uw-liquid-glass-modal"
      >
        <Modal.Header closeButton className="uw-note-modal-header">
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <Edit3 size={14} />
            </span>
            Modifica Nota
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <div className="row g-2 mb-2">
            <div className="col-12 col-md-6">
              <Form.Label className="uw-label text-light">TITOLO *</Form.Label>
              <Form.Control
                value={editNoteTitle}
                onChange={(e) => setEditNoteTitle(e.target.value)}
                className="uw-input"
                autoFocus
              />
            </div>

            <div className="col-12 col-md-6">
              <Form.Label className="uw-label text-light">FIERA</Form.Label>
              <Form.Select
                value={editNoteFiera}
                onChange={(e) => setEditNoteFiera(e.target.value)}
                className="uw-input"
              >
                <option value="">— Nessuna fiera —</option>
                {availableFiereForDropdown.map((row, index) => {
                  const value = optionValue(row, "id_fiera");
                  return (
                    <option key={`edit-nota-fiera-${value}-${index}`} value={value}>
                      {fieraDisplayLabel(row)}
                    </option>
                  );
                })}
              </Form.Select>
            </div>
          </div>

          <Form.Label className="uw-label text-light">NOTA *</Form.Label>
          <Form.Control
            as="textarea"
            rows={7}
            value={editNoteBody}
            onChange={(e) => setEditNoteBody(e.target.value)}
            className="uw-input"
          />
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer">
          <button
            type="button"
            className="uw-action-btn uw-action-solid"
            onClick={saveEditedNote}
            disabled={isBusy || !editNoteTitle.trim() || !editNoteBody.trim()}
          >
            <Save size={14} />
            SALVA MODIFICHE
          </button>

          <button
            type="button"
            className="uw-action-btn"
            onClick={() => setEditingNote(null)}
          >
            CHIUDI
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(selectedNote)}
        onHide={() => setSelectedNote(null)}
        centered
        dialogClassName="uw-postit-modal-dialog uw-liquid-glass-dialog mx-auto"
        style={{
          display: "flex",

          alignItems: "center",

          justifyContent: "center",
        }}
        contentClassName="uw-note-modal uw-liquid-glass-modal"
      >
        <Modal.Header closeButton className="uw-note-modal-header">
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <FileText size={14} />
            </span>
            {selectedNoteContent?.title || "Nota"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <div className="uw-note-detail-meta mb-3">
            <div>
              <span className="uw-note-detail-label">Data</span>
              <strong className="text-light">
                {formatItalianDateTime(pick(selectedNote, "data_nota"))}
              </strong>
            </div>

            <div>
              <span className="uw-note-detail-label">Tipo</span>
              <strong className="text-light">
                {selectedNote ? noteSourceLabel(selectedNote) : "—"}
              </strong>
            </div>
            <div>
              <span className="uw-note-detail-label">Fiera</span>
              <strong className="text-light">{selectedNoteFieraDisplay}</strong>
            </div>
            <div>
              <span className="uw-note-detail-label">Stato</span>
              <strong className={selectedNote && isNoteDone(selectedNote) ? "text-success" : "text-warning"}>
                {selectedNote && isNoteDone(selectedNote) ? "FATTO" : "DA FARE"}
              </strong>
            </div>
          </div>

          <div className="uw-note-detail-body text-light">
            {selectedNoteContent?.body || "Nessun testo presente."}
          </div>
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer">
          {selectedNote && (
            <>
              <button
                type="button"
                className={`uw-action-btn ${isNoteDone(selectedNote) ? "" : "uw-action-solid"}`}
                onClick={() => toggleNoteDone(selectedNote)}
                disabled={isBusy}
              >
                <CheckCircle2 size={15} />
                {isNoteDone(selectedNote) ? "RIPRISTINA" : "SEGNA FATTO"}
              </button>

              <button
                type="button"
                className="uw-action-btn"
                onClick={() => openEditNote(selectedNote)}
                disabled={isBusy}
              >
                <Edit3 size={15} />
                MODIFICA
              </button>

              <button
                type="button"
                className="uw-action-btn uw-action-danger"
                onClick={() => deleteNote(selectedNote)}
                disabled={isBusy}
              >
                <Trash2 size={15} />
                ELIMINA
              </button>
            </>
          )}

          <button
            type="button"
            className="uw-action-btn"
            onClick={() => setSelectedNote(null)}
          >
            CHIUDI
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={duplicateModalOpen}
        onHide={closeDuplicateModal}
        centered
        size="lg"
        backdrop="static"
        dialogClassName="uw-liquid-glass-dialog"
        contentClassName="uw-note-modal uw-liquid-glass-modal uw-duplicate-modal"
      >
        <Modal.Header closeButton={!duplicateBusy} className="uw-note-modal-header">
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <RefreshCw size={15} />
            </span>
            Duplica da un altro contatto
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          <div className="uw-duplicate-warning mb-3">
            <AlertCircle size={16} />
            <div>
              <strong>Vengono copiati i parametri, non l'identità del contatto.</strong>
              <span>
                Codice cliente, Ragione Sociale, P.IVA, Infinity, fiere, rubrica,
                note e record collegati restano vuoti.
              </span>
            </div>
          </div>

          <Form.Label className="uw-label">CERCA CONTATTO DA DUPLICARE</Form.Label>

          <div className="uw-input-icon-wrap mb-3">
            <Search size={14} className="uw-input-icon" />
            <Form.Control
              value={duplicateSearch}
              onChange={(e) => setDuplicateSearch(e.target.value)}
              placeholder="Cerca per ragione sociale, codice cliente, ID, P.IVA..."
              className="uw-input uw-input-with-icon"
              autoFocus
              autoComplete="off"
              disabled={duplicateBusy}
            />

            {duplicateSearching && (
              <Spinner
                animation="border"
                size="sm"
                className="uw-search-live-spinner"
              />
            )}
          </div>

          {duplicateResults.length > 0 ? (
            <div className="uw-search-results uw-duplicate-results">
              <Table responsive hover size="sm" className="mb-0 uw-table align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Codice cliente</th>
                    <th>Ragione sociale</th>
                    <th>P.IVA</th>
                    <th>Località</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateResults.map((row) => {
                    const id = Number(pick(row, "id_contatto"));

                    return (
                      <tr
                        key={`dup-${id || JSON.stringify(row)}`}
                        className="uw-contact-row"
                        onClick={() => !duplicateBusy && duplicateFromContact(id)}
                        title="Usa questo contatto come modello"
                      >
                        <td className="text-muted">{textValue(id)}</td>
                        <td className="fw-semibold">
                          {textValue(
                            pick(
                              row,
                              "codice_cliente",
                              "cod_cliente",
                              "codice_cli",
                              "id_cliente",
                            ),
                          ) || "---"}
                        </td>
                        <td className="fw-semibold">
                          {textValue(pick(row, "ragione_sociale")) || "---"}
                        </td>
                        <td>{textValue(pick(row, "par_iva")) || "---"}</td>
                        <td>{textValue(pick(row, "localita")) || "---"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : (
            duplicateSearch.trim() &&
            !duplicateSearching && (
              <div className="uw-search-empty">
                Nessun contatto trovato per <strong>{duplicateSearch.trim()}</strong>.
              </div>
            )
          )}
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer">
          <button
            type="button"
            className="uw-action-btn"
            onClick={closeDuplicateModal}
            disabled={duplicateBusy}
          >
            <X size={14} /> CHIUDI
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={infinityModalOpen}
        onHide={closeInfinityWizard}
        centered
        size="lg"
        backdrop="static"
        dialogClassName="uw-liquid-glass-dialog"
        contentClassName="uw-note-modal uw-liquid-glass-modal uw-infinity-modal"
      >
        <Modal.Header
          closeButton={!infinityBusy}
          className="uw-note-modal-header"
        >
          <Modal.Title className="uw-note-modal-title">
            <span className="uw-section-icon me-2">
              <Link2 size={15} />
            </span>
            {infinityStep === "merge"
              ? "Unione Rubriche Infinity"
              : "Crea Anagrafica Infinity"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
          {infinityStep === "setup" ? (
            <>
              <div className="uw-infinity-summary mb-3">
                <div>
                  <span>CONTATTO</span>
                  <strong>{formData.ragsoc || "—"}</strong>
                </div>
                <div>
                  <span>P.IVA</span>
                  <strong>{formData.piva || "—"}</strong>
                </div>
                <div>
                  <span>AZIENDA</span>
                  <strong>{infinityPrecheck?.azienda || "—"}</strong>
                </div>
                <div>
                  <span>NAZIONE</span>
                  <strong>
                    {infinityNationLabel}
                    {infinityNationIso ? ` (${infinityNationIso})` : ""}
                  </strong>
                </div>
                <div>
                  <span>INDIRIZZO</span>
                  <strong>{infinityForm.indirizzo || "—"}</strong>
                </div>
                <div>
                  <span>CITTÀ / CAP</span>
                  <strong>
                    {[infinityForm.localita, infinityForm.cap].filter(Boolean).join(" · ") || "—"}
                  </strong>
                </div>
              </div>

              <div className="uw-infinity-contact-check mb-3">
                <div className="uw-label mb-2">RIFERIMENTI INVIATI A HERMES</div>
                <div className="d-flex flex-wrap gap-2">
                  <span className={`uw-infinity-chip ${infinityPrecheck?.hermes_contacts?.email ? "ok" : ""}`}>
                    Email: {infinityPrecheck?.hermes_contacts?.email || "—"}
                  </span>
                  <span className={`uw-infinity-chip ${infinityPrecheck?.hermes_contacts?.telefono ? "ok" : ""}`}>
                    Telefono: {infinityPrecheck?.hermes_contacts?.telefono || "—"}
                  </span>
                  <span className={`uw-infinity-chip ${infinityPrecheck?.hermes_contacts?.cellulare ? "ok" : ""}`}>
                    Cellulare: {infinityPrecheck?.hermes_contacts?.cellulare || "—"}
                  </span>
                </div>
              </div>

              <div className="uw-infinity-address-box mb-3">
                <div className="uw-label mb-2">DATI ANAGRAFICI INVIATI A HERMES</div>

                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <Form.Label className="uw-label">NAZIONE</Form.Label>
                    <Form.Select
                      className="uw-input"
                      value={infinityForm.id_nazione}
                      onChange={(e) =>
                        setInfinityForm((prev) => ({
                          ...prev,
                          id_nazione: e.target.value,
                        }))
                      }
                    >
                      <option value="">— Nessuna nazione —</option>
                      {lookups.nazioni.map((row, index) => {
                        const value = optionValue(row, "id_nazione");
                        const label =
                          optionValue(row, "descrizione", "nazione", "nome") || value;
                        const iso = optionValue(row, "codice_iso", "iso", "codice");

                        return (
                          <option key={`inf-naz-${value}-${index}`} value={value}>
                            {iso ? `${label} (${iso})` : label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </div>

                  <div className="col-12 col-md-6">
                    <Form.Label className="uw-label">PROVINCIA</Form.Label>
                    <Form.Select
                      className="uw-input"
                      value={infinityForm.provincia}
                      onChange={(e) =>
                        setInfinityForm((prev) => ({
                          ...prev,
                          provincia: e.target.value,
                        }))
                      }
                    >
                      <option value="">— Nessuna provincia —</option>
                      {lookups.province.map((row, index) => {
                        const value = optionValue(row, "provincia", "codice");
                        const label =
                          optionValue(row, "descrizione", "provincia") || value;

                        return (
                          <option key={`inf-prov-${value}-${index}`} value={value}>
                            {value && label !== value ? `${value} - ${label}` : label}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </div>

                  <div className="col-12">
                    <Form.Label className="uw-label">INDIRIZZO</Form.Label>
                    <Form.Control
                      className="uw-input"
                      value={infinityForm.indirizzo}
                      onChange={(e) =>
                        setInfinityForm((prev) => ({
                          ...prev,
                          indirizzo: e.target.value,
                        }))
                      }
                      placeholder="Via / Piazza / numero civico"
                    />
                  </div>

                  <div className="col-12 col-md-8">
                    <Form.Label className="uw-label">CITTÀ</Form.Label>
                    <Form.Control
                      className="uw-input"
                      value={infinityForm.localita}
                      onChange={(e) =>
                        setInfinityForm((prev) => ({
                          ...prev,
                          localita: e.target.value,
                        }))
                      }
                      placeholder="Località"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <Form.Label className="uw-label">CAP</Form.Label>
                    <Form.Control
                      className="uw-input"
                      value={infinityForm.cap}
                      onChange={(e) =>
                        setInfinityForm((prev) => ({
                          ...prev,
                          cap: e.target.value,
                        }))
                      }
                      placeholder="CAP"
                    />
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <Form.Label className="uw-label">TIPO CLIENTE *</Form.Label>
                  <Form.Select
                    className="uw-input"
                    value={infinityForm.tipo_cliente}
                    onChange={(e) =>
                      setInfinityForm((prev) => ({
                        ...prev,
                        tipo_cliente: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Seleziona —</option>
                    {infinityOptions.tipi_cliente.map((row, index) => {
                      const value = optionValue(row, "codice", "tipo_cliente");
                      const label = optionValue(row, "descrizione", "tipo_cliente") || value;
                      return (
                        <option key={`inf-tipo-${value}-${index}`} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label className="uw-label">ZONA *</Form.Label>
                  <Form.Select
                    className="uw-input"
                    value={infinityForm.zona}
                    onChange={(e) =>
                      setInfinityForm((prev) => ({
                        ...prev,
                        zona: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Seleziona —</option>
                    {infinityOptions.zone.map((row, index) => {
                      const value = optionValue(row, "codice", "zona");
                      const descr = optionValue(row, "descrizione");
                      return (
                        <option key={`inf-zona-${value}-${index}`} value={value}>
                          {descr ? `${value} - ${descr}` : value}
                        </option>
                      );
                    })}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label className="uw-label">PAGAMENTO *</Form.Label>
                  <Form.Select
                    className="uw-input"
                    value={infinityForm.id_pagamento}
                    onChange={(e) =>
                      setInfinityForm((prev) => ({
                        ...prev,
                        id_pagamento: e.target.value,
                      }))
                    }
                  >
                    <option value="">— Seleziona —</option>
                    {infinityOptions.pagamenti.map((row, index) => {
                      const value = optionValue(row, "codice", "id_con_pagamento");
                      const label = optionValue(row, "descrizione") || value;
                      return (
                        <option key={`inf-pag-${value}-${index}`} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </Form.Select>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="uw-infinity-created mb-3">
                <CheckCircle2 size={18} />
                <div>
                  <strong>Anagrafica Infinity #{infinityCreatedId} creata</strong>
                  <span>
                    Prima del collegamento definitivo devono essere risolti tutti i riferimenti della rubrica Utility.
                  </span>
                </div>
              </div>

              {infinityMergeData.utility.length === 0 ? (
                <div className="uw-infinity-empty-merge">
                  Nessun riferimento Utility da trasferire. Il contatto può essere collegato direttamente.
                </div>
              ) : (
                <div className="uw-infinity-merge-list">
                  {infinityMergeData.utility.map((row, index) => {
                    const match = findInfinityRubricaMatch(row);
                    const tipo = optionValue(row, "Tipo", "tipo", "codice_tipo") || "Riferimento";
                    const numero = optionValue(row, "num_riferimento");
                    const interlocutore = optionValue(row, "interlocutore");

                    return (
                      <div
                        className="uw-infinity-merge-row"
                        key={`inf-merge-${optionValue(row, "id_telefono_cont")}-${index}`}
                      >
                        <div className="uw-infinity-merge-info">
                          <strong>{tipo}: {numero}</strong>
                          <span>{interlocutore || "Nessun interlocutore"}</span>
                        </div>

                        <div className={`uw-infinity-merge-mode ${match ? "matched" : "copy"}`}>
                          {match ? (
                            <>
                              <CheckCircle2 size={14} /> GIÀ PRESENTE SU INFINITY
                            </>
                          ) : (
                            <>
                              <ArrowUp size={14} /> COPIA SU INFINITY
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="uw-note-modal-footer">
          <button
            type="button"
            className="uw-action-btn"
            onClick={closeInfinityWizard}
            disabled={infinityBusy}
          >
            <X size={14} /> CHIUDI
          </button>

          {infinityStep === "setup" ? (
            <button
              type="button"
              className="uw-action-btn uw-action-solid"
              onClick={createInfinityAnagrafica}
              disabled={
                infinityBusy ||
                !infinityForm.tipo_cliente ||
                !infinityForm.zona ||
                !infinityForm.id_pagamento
              }
            >
              {infinityBusy ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Link2 size={14} />
              )}
              CREA SU INFINITY
            </button>
          ) : (
            <button
              type="button"
              className="uw-action-btn uw-action-solid"
              onClick={completeInfinityMerge}
              disabled={infinityBusy}
            >
              {infinityBusy ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <CheckCircle2 size={14} />
              )}
              UNISCI E COLLEGA
            </button>
          )}
        </Modal.Footer>
      </Modal>

      <style>{`

        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');



        * { box-sizing: border-box; }



        .uw-note-search-box {
          padding: 10px;
          border-radius: 12px;
          background: rgba(255,255,255,.035);
          border: 1px solid rgba(255,255,255,.08);
        }

        .uw-note-title-btn {
          width: 100%;
          text-align: left;
          cursor: pointer;
          color: inherit;
          transition: transform .15s ease, border-color .15s ease, background .15s ease;
        }

        .uw-note-title-btn:hover {
          transform: translateY(-1px);
          border-color: rgba(235,181,27,.42);
          background: rgba(235,181,27,.07);
        }

        .uw-note-title-text {
          display: block;
          font-size: .9rem;
          font-weight: 700;
          line-height: 1.35;
        }

        .uw-note-detail-meta {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }

        .uw-note-detail-meta > div {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 10px 12px;
          border-radius: 12px;
          background: rgba(255,255,255,.045);
          border: 1px solid rgba(255,255,255,.08);
        }

        .uw-note-detail-label {
          color: #9b9da2;
          font-size: .68rem;
          font-weight: 800;
          letter-spacing: .06em;
          text-transform: uppercase;
        }

        .uw-note-detail-body {
          width: 100%;
          max-width: 100%;
          min-width: 0;

          white-space: pre-wrap;

          /* IMPORTANTISSIMO:
             spezza anche stringhe senza spazi (codici, URL, testi incollati,
             sequenze tipo 444444444444444...), evitando qualsiasi overflow. */
          overflow-wrap: anywhere;
          word-wrap: break-word;
          word-break: break-word;

          overflow-x: hidden;
          line-height: 1.6;
          padding: 14px;
          border-radius: 12px;
          background: rgba(255,255,255,.035);
          border: 1px solid rgba(255,255,255,.08);
        }

        /* Protezione anche per eventuale HTML/testo annidato nel corpo nota. */
        .uw-note-detail-body * {
          max-width: 100%;
          min-width: 0;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        /* Anche titolo e metadati non devono mai allargare il modal. */
        .uw-note-modal-title {
          min-width: 0;
          max-width: calc(100% - 44px);
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .uw-note-detail-meta > div {
          min-width: 0;
          overflow: hidden;
        }

        .uw-note-detail-meta strong {
          min-width: 0;
          max-width: 100%;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        /* Nessuna scrollbar orizzontale nel modal delle note. */
        .uw-note-modal .modal-body {
          min-width: 0;
          max-width: 100%;
          overflow-x: hidden !important;
        }

        @media (max-width: 767px) {
          .uw-note-detail-meta {
            grid-template-columns: 1fr;
          }
        }

        .uw-draft-badge {

          display: inline-flex;

          align-items: center;

          padding: 3px 7px;

          border-radius: 999px;

          background: rgba(235, 181, 27, 0.14);

          border: 1px solid rgba(166, 122, 16, 0.35);

          color: #7b5b0c;

          font-size: 0.68rem;

          font-weight: 800;

          letter-spacing: 0.35px;

          white-space: nowrap;

        }



        .uw-success-popup-layer {

          position: fixed;

          inset: 0;

          z-index: 9999;

          display: flex;

          align-items: center;

          justify-content: center;

          padding: 20px;

          pointer-events: none;

        }



        .uw-success-popup {

          position: relative;

          min-width: min(420px, calc(100vw - 40px));

          max-width: 520px;

          display: flex;

          align-items: center;

          gap: 14px;

          padding: 18px 46px 18px 18px;

          border-radius: 10px;

          background: #eef9f1;

          border: 1px solid rgba(40, 167, 69, 0.28);

          border-left: 5px solid #28a745;

          box-shadow: 0 20px 55px rgba(0, 0, 0, 0.30);

          color: #185c31;

          pointer-events: auto;

          animation: uwSuccessPopupIn 180ms ease-out;

        }



        .uw-success-popup-icon {

          flex: 0 0 auto;

          display: flex;

          align-items: center;

          justify-content: center;

        }



        .uw-success-popup-content {

          min-width: 0;

        }



        .uw-success-popup-title {

          font-size: 0.86rem;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: 0.45px;

          margin-bottom: 2px;

        }



        .uw-success-popup-message {

          font-size: 0.88rem;

          font-weight: 500;

          line-height: 1.35;

        }



        .uw-success-popup-close {

          position: absolute;

          top: 8px;

          right: 8px;

          width: 28px;

          height: 28px;

          display: inline-flex;

          align-items: center;

          justify-content: center;

          border: 0;

          border-radius: 50%;

          background: transparent;

          color: inherit;

          cursor: pointer;

          opacity: 0.68;

        }



        .uw-success-popup-close:hover {

          background: rgba(0, 0, 0, 0.06);

          opacity: 1;

        }



        @keyframes uwSuccessPopupIn {

          from {

            opacity: 0;

            transform: translateY(8px) scale(0.97);

          }

          to {

            opacity: 1;

            transform: translateY(0) scale(1);

          }

        }



        .uw-card {

          border-radius: 8px;

          box-shadow: 0 8px 24px rgba(39, 39, 39, 0.14), 0 1px 3px rgba(228, 223, 223, 0.08);

        }

        .uw-card-accent {

          height: 4px;

          background: linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%);

        }



        .uw-eyebrow-dash { width: 14px; height: 2px; background-color: ${GOLD}; display: inline-block; border-radius: 1px; }

        .uw-page-title { font-size: 0.88rem; letter-spacing: 0.6px; color: ${TEXT_DARK}; }



        .uw-alert-note {

          border-radius: 4px;

          border: 1px solid rgba(0,0,0,0.06);

          border-left: 3px solid ${GOLD};

          background: rgba(235,181,27,0.08);

        }

        .uw-alert-toast {

          border-radius: 6px;

          border: none;

          box-shadow: 0 4px 14px rgba(0,0,0,0.10);

        }



        .uw-save-strip {

          position: sticky;

          top: 0;

          z-index: 20;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 12px;

          padding: 12px 14px;

          background: rgba(247,244,236,0.97);

          border: 1px solid ${FILTER_BOX_BORDER};

          border-left: 4px solid ${GOLD};

          border-radius: 6px;

          box-shadow: 0 6px 16px rgba(0,0,0,0.08);

          backdrop-filter: blur(4px);

        }



        .uw-draft-hint {

          padding: 8px 10px;

          background: rgba(235,181,27,0.09);

          border-left: 3px solid ${GOLD};

          border-radius: 3px;

          font-size: 0.75rem;

          color: #62562f;

        }



        .uw-section-box {

          background-color: ${FILTER_BOX_BG};

          border: 1px solid ${FILTER_BOX_BORDER};

          border-radius: 6px;

          transition: border-color 0.15s ease;

        }

        .uw-section-icon {

          display: inline-flex;

          align-items: center;

          justify-content: center;

          width: 22px;

          height: 22px;

          border-radius: 5px;

          background: rgba(235,181,27,0.16);

          color: ${GOLD_DARK};

          flex-shrink: 0;

        }

        .uw-section-title {

          font-size: 0.72rem;

          font-weight: 700;

          color: ${GOLD_DARK};

          text-transform: uppercase;

          letter-spacing: 0.8px;

        }

        .uw-label { font-size: 0.7rem !important; font-weight: 700 !important; color: #6b6b6b !important; letter-spacing: 0.3px; margin-bottom: 3px !important; }

        .uw-euro-suffix {
          background: rgba(235, 181, 27, 0.10) !important;
          border-color: rgba(235, 181, 27, 0.28) !important;
          color: #ebb51b !important;
          font-weight: 800;
        }

        .uw-input {

          border: 1px solid rgba(0,0,0,0.14) !important;

          background-color: #fff !important;

          font-size: 0.8rem !important;

          color: ${TEXT_DARK} !important;

          padding: 7px 9px !important;

          min-height: 36px;

          border-radius: 4px !important;

          transition: border-color 0.15s ease, box-shadow 0.15s ease;

        }

        .uw-input:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(235,181,27,0.18) !important; outline: none !important; }

        .uw-input:hover:not(:focus) { border-color: rgba(0,0,0,0.24) !important; }



        .uw-input-icon-wrap { position: relative; }

        .uw-input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9a9a9a; pointer-events: none; }

        .uw-input-with-icon { padding-left: 30px !important; }

        .uw-search-live-input { padding-right: 38px !important; }

        .uw-search-live-spinner {

          position: absolute;

          right: 11px;

          top: 50%;

          transform: translateY(-50%);

          width: 15px;

          height: 15px;

          color: ${GOLD_DARK};

        }

        .uw-search-results {
          transition: opacity 0.15s ease;
        }

        .uw-search-results-loading {
          opacity: 0.55;
          pointer-events: none;
        }

        .uw-search-live-hint {

          margin-top: 5px;

          font-size: .67rem;

          font-weight: 600;

          color: #8a8170;

        }



        .uw-checkbox .form-check-input:checked { background-color: ${GOLD_DARK} !important; border-color: ${GOLD_DARK} !important; }

        .uw-checkbox .form-check-input { border-radius: 3px; }

        .uw-checkbox .form-check-label { font-size: 0.8rem !important; font-weight: 600 !important; color: ${TEXT_DARK} !important; }




        .uw-note-done {
          opacity: .72;
          border-color: rgba(25,135,84,.35) !important;
        }
        .uw-note-done .uw-note-title-text { text-decoration: line-through; }
        .uw-action-danger {
  border-color: rgba(220, 53, 69, 0.55) !important;
  color: #ff7b86 !important;
}
.uw-action-danger:hover {
  background: rgba(220, 53, 69, 0.14) !important;
  border-color: rgba(220, 53, 69, 0.9) !important;
}

.uw-note-done-badge {
          margin-left: auto;
          font-size: .62rem;
          font-weight: 800;
          color: #198754;
          border: 1px solid rgba(25,135,84,.35);
          padding: 2px 6px;
          border-radius: 999px;
          text-decoration: none !important;
        }

        .uw-action-btn {

          display: inline-flex; align-items: center; justify-content: center; gap: 6px;

          min-height: 36px; border: 1px solid ${GOLD}; background-color: transparent; color: ${GOLD_DARK};

          font-size: 0.71rem; font-weight: 700; letter-spacing: 0.35px; text-transform: uppercase;

          transition: all 0.15s ease-in-out; cursor: pointer; padding: 7px 13px; border-radius: 4px;

        }

        .uw-action-btn:hover:not(:disabled) { background-color: rgba(235,181,27,0.14); box-shadow: 0 2px 6px rgba(235,181,27,0.18); }

        .uw-action-btn:disabled { opacity: .45; cursor: not-allowed; }

        .uw-action-solid { background-color: ${GOLD}; color: ${TEXT_DARK}; border-color: ${GOLD}; box-shadow: 0 2px 6px rgba(235,181,27,0.3); }

        .uw-action-solid:hover:not(:disabled) { background-color: ${GOLD_DARK}; border-color: ${GOLD_DARK}; color: #fff; box-shadow: 0 3px 10px rgba(166,122,16,0.35); }

        .uw-danger-btn { border-color: #c94d4d; color: #a52c2c; }

        .uw-danger-btn:hover:not(:disabled) { background: #a52c2c; border-color: #a52c2c; color: #fff; }



        .uw-mini-btn {

          border: 1px solid rgba(166,122,16,.5); background: transparent; color: ${GOLD_DARK};

          min-height: 28px; padding: 4px 8px; font-size: .67rem; font-weight: 700; display: inline-flex;

          align-items: center; justify-content: center; gap: 4px; text-transform: uppercase; border-radius: 3px;

          transition: background 0.15s ease;

        }

        .uw-mini-btn:hover { background: rgba(235,181,27,.14); }

        .uw-mini-btn.danger { border-color: rgba(180,50,50,.5); color: #a52c2c; }

        .uw-mini-btn.danger:hover { background: rgba(180,50,50,.09); }



        .uw-master-toolbar {

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 14px;

          padding: 12px 14px;

          background: linear-gradient(135deg, rgba(235,181,27,0.12), rgba(255,255,255,0.72));

          border: 1px solid rgba(166,122,16,0.24);

          border-left: 4px solid ${GOLD};

          border-radius: 8px;

        }

        .uw-master-toolbar-copy {

          display: flex;

          flex-direction: column;

          gap: 2px;

          min-width: 220px;

        }

        .uw-master-kicker {

          color: ${GOLD_DARK};

          font-size: .68rem;

          font-weight: 800;

          letter-spacing: .7px;

        }

        .uw-master-subtitle {

          color: #656565;

          font-size: .73rem;

          line-height: 1.35;

        }

        .uw-master-jumps {

          display: flex;

          flex-wrap: wrap;

          gap: 6px;

          justify-content: flex-end;

        }

        .uw-jump-btn {

          border: 1px solid rgba(166,122,16,.30);

          background: rgba(255,255,255,.78);

          color: ${TEXT_DARK};

          padding: 6px 9px;

          border-radius: 999px;

          font-size: .66rem;

          font-weight: 700;

          text-transform: uppercase;

          letter-spacing: .25px;

          transition: all .15s ease;

        }

        .uw-jump-btn:hover {

          border-color: ${GOLD};

          background: #fff;

          color: ${GOLD_DARK};

          transform: translateY(-1px);

        }

        .uw-anchor-section { scroll-margin-top: 18px; }

        .uw-tab-panel-fieldset {

          min-width: 0;

          margin: 0;

          padding: 0;

          border: 0;

        }

        .uw-master-section {

          margin-top: 20px;

          padding-top: 20px;

          border-top: 1px solid rgba(0,0,0,.08);

        }

        .uw-readonly-banner {

          display: flex;

          align-items: center;

          gap: 8px;

          padding: 9px 11px;

          border: 1px solid rgba(0,0,0,.08);

          border-left: 3px solid ${GOLD};

          border-radius: 5px;

          background: rgba(255,255,255,.58);

          color: #686868;

          font-size: .73rem;

          font-weight: 600;

        }

        .uw-readonly-status {

          display: inline-flex;

          align-items: center;

          gap: 8px;

          min-height: 36px;

          padding: 7px 11px;

          border-radius: 999px;

          border: 1px solid rgba(0,0,0,.10);

          background: rgba(255,255,255,.62);

          color: #6b6b6b;

          font-size: .68rem;

          font-weight: 800;

          letter-spacing: .35px;

        }

        .uw-readonly-status-dot {

          width: 7px;

          height: 7px;

          border-radius: 50%;

          background: ${GOLD_DARK};

          box-shadow: 0 0 0 3px rgba(235,181,27,.16);

          flex: 0 0 auto;

        }

        .uw-readonly-view .uw-input:disabled {

          opacity: 1;

          background: #f5f3ed !important;

          color: #494949 !important;

          -webkit-text-fill-color: #494949;

          cursor: default;

        }

        .uw-readonly-view .form-check-input:disabled {

          opacity: .72;

        }

        .uw-readonly-view .form-check-input:disabled ~ .form-check-label {

          opacity: 1;

          color: #4f4f4f !important;

        }

        .uw-readonly-view button:disabled {

          opacity: .32;

          cursor: default;

          box-shadow: none;

        }



        .uw-page-scroll {

          padding-bottom: 130px;

        }



        .uw-floating-dock {

          position: fixed;

          right: 18px;

          bottom: 18px;

          z-index: 1040;

          width: min(430px, calc(100vw - 36px));

          padding: 10px;

          border-radius: 12px;

          border: 1px solid rgba(0,0,0,.16);

          background: rgba(247,244,236,.97);

          box-shadow: 0 12px 34px rgba(0,0,0,.26);

          backdrop-filter: blur(10px);

        }

        .uw-floating-dock-head {

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 12px;

          padding: 2px 2px 9px;

          border-bottom: 1px solid rgba(0,0,0,.08);

          margin-bottom: 8px;

        }

        .uw-floating-dock-kicker {

          color: ${GOLD_DARK};

          font-size: .63rem;

          font-weight: 800;

          letter-spacing: .6px;

        }

        .uw-floating-dock-copy {

          max-width: 335px;

          color: #666;

          font-size: .67rem;

          line-height: 1.25;

          margin-top: 2px;

        }

        .uw-floating-icon-btn {

          width: 36px;

          height: 36px;

          flex: 0 0 36px;

          border-radius: 50%;

          border: 1px solid ${GOLD};

          background: ${GOLD};

          color: ${TEXT_DARK};

          display: inline-flex;

          align-items: center;

          justify-content: center;

          cursor: pointer;

          box-shadow: 0 3px 8px rgba(166,122,16,.24);

          transition: all .15s ease;

        }

        .uw-floating-icon-btn:hover {

          background: ${GOLD_DARK};

          border-color: ${GOLD_DARK};

          color: #fff;

          transform: translateY(-1px);

        }

        .uw-floating-links {

          display: flex;

          flex-wrap: wrap;

          justify-content: flex-end;

          gap: 6px;

        }

        .uw-floating-link {

          min-height: 34px;

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 5px;

          padding: 6px 9px;

          border: 1px solid rgba(166,122,16,.28);

          border-radius: 7px;

          background: #fff;

          color: ${TEXT_DARK};

          font-size: .66rem;

          font-weight: 750;

          text-transform: uppercase;

          letter-spacing: .2px;

          cursor: pointer;

          transition: all .15s ease;

        }

        .uw-floating-link:hover {

          border-color: ${GOLD};

          color: ${GOLD_DARK};

          background: rgba(235,181,27,.08);

          transform: translateY(-1px);

        }

        .uw-floating-note-add {

          border-color: ${GOLD};

          background: ${GOLD};

          color: ${TEXT_DARK};

          box-shadow: 0 2px 7px rgba(166,122,16,.18);

        }

        .uw-floating-note-add:hover {

          background: ${GOLD_DARK};

          border-color: ${GOLD_DARK};

          color: #fff;

        }



        .uw-note-modal {

          border: 0;

          border-radius: 10px;

          overflow: hidden;

          background: ${PANEL_BG};

          box-shadow: 0 22px 60px rgba(0,0,0,.34);

        }

        .uw-note-modal::before {

          content: "";

          display: block;

          height: 4px;

          background: linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%);

        }

        .uw-note-modal-header {

          border-bottom: 1px solid rgba(0,0,0,.08);

          background: rgba(255,255,255,.34);

        }

        .uw-note-modal-title {

          display: flex;

          align-items: center;

          color: ${TEXT_DARK};

          font-size: .85rem;

          font-weight: 800;

          text-transform: uppercase;

          letter-spacing: .5px;

        }

        .uw-note-modal-footer {

          border-top: 1px solid rgba(0,0,0,.08);

          background: rgba(255,255,255,.34);

        }



        .uw-tabs {

          display: flex;

          flex-wrap: wrap;

          gap: 4px;

          padding: 5px;

          background: rgba(0,0,0,0.03);

          border: 1px solid rgba(0,0,0,0.07);

          border-radius: 8px;

        }

        .uw-tab {

          display: inline-flex; align-items: center; gap: 7px; border: 1px solid transparent; background: transparent;

          color: #6b6b6b; font-size: .72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px;

          padding: 8px 12px; border-radius: 5px; cursor: pointer; transition: all 0.15s ease;

        }

        .uw-tab:hover:not(.active) { background: rgba(235,181,27,.10); color: ${GOLD_DARK}; }

        .uw-tab.active {

          color: ${TEXT_DARK};

          background: #fff;

          border-color: rgba(166,122,16,.3);

          box-shadow: 0 2px 6px rgba(0,0,0,0.08);

        }

        .uw-tab.active svg { color: ${GOLD_DARK}; }

        .uw-tab-badge {

          display: inline-flex; align-items: center; justify-content: center;

          min-width: 17px; height: 17px; padding: 0 4px; border-radius: 9px;

          background: ${GOLD_DARK}; color: #fff; font-size: 0.62rem; font-weight: 800;

        }



        .uw-bottom-actions { border-top: 1px solid rgba(0,0,0,0.08); }



        .uw-table-wrap, .uw-search-results {

          border: 1px solid rgba(0,0,0,.09);

          border-radius: 6px;

          overflow: hidden;

        }

        .uw-table-wrap { overflow: auto; }

        .uw-table { font-size: .76rem; background: #fff; }

        .uw-table thead th {

          background: rgba(0,0,0,.03);

          color: #6b6b6b;

          font-size: .66rem;

          text-transform: uppercase;

          letter-spacing: .5px;

          white-space: nowrap;

          border-bottom: 1px solid rgba(0,0,0,.08) !important;

          font-weight: 700;

        }

        .uw-table td { vertical-align: middle; border-color: rgba(0,0,0,.06); }

        .uw-table tbody tr:hover { background: rgba(235,181,27,0.045); }



        .uw-pill {

          display: inline-flex; align-items: center; gap: 5px; padding: 4px 8px;

          background: rgba(235,181,27,.16); border: 1px solid rgba(166,122,16,.4);

          border-radius: 12px; font-size: .72rem; font-weight: 700; color: ${GOLD_DARK};

        }

        .uw-pill svg { cursor: pointer; opacity: 0.6; }

        .uw-pill svg:hover { opacity: 1; }



        .uw-note {

          padding: 11px 13px;

          background: #fff;

          border: 1px solid rgba(0,0,0,.06);

          border-left: 3px solid ${GOLD};

          border-radius: 4px;

          box-shadow: 0 1px 4px rgba(0,0,0,.04);

        }

        .uw-note-source {

          display: inline-flex; min-width: 24px; justify-content: center; padding: 2px 6px;

          background: rgba(235,181,27,.17); color: ${GOLD_DARK}; font-size: .65rem; font-weight: 800;

          border-radius: 3px;

        }

        .uw-stat {

          background: #fff; border: 1px solid rgba(0,0,0,.08); border-radius: 5px;

          padding: 9px; text-align: center;

        }

        .uw-stat span { display: block; color: #888; font-size: .64rem; text-transform: uppercase; letter-spacing: 0.3px; }

        .uw-stat strong { display: block; font-size: 1.05rem; margin-top: 3px; color: ${TEXT_DARK}; }

        .uw-infinity-status {

          display: flex; align-items: center; gap: 9px; padding: 11px;

          background: #e7f6ed; color: #1e6d3b; border-left: 3px solid #28a745; border-radius: 4px;

        }

        .uw-info-row {

          height: 100%; padding: 8px 9px; background: #fff;

          border: 1px solid rgba(0,0,0,.07); border-radius: 4px;

        }

        .uw-info-row span { display: block; color: #888; font-size: .64rem; text-transform: uppercase; letter-spacing: 0.3px; }

        .uw-info-row strong { display: block; font-size: .78rem; margin-top: 2px; word-break: break-word; }

        .scroll-hidden { scrollbar-width: none; -ms-overflow-style: none; }

        .scroll-hidden::-webkit-scrollbar { display: none; }



        @media (max-width: 767.98px) {

          .uw-page-scroll { padding-bottom: 190px; }

          .uw-floating-dock {

            left: 10px;

            right: 10px;

            bottom: 10px;

            width: auto;

            border-radius: 10px;

          }

          .uw-floating-dock-copy { display: none; }

          .uw-floating-dock-head { padding-bottom: 7px; }

          .uw-floating-links { justify-content: stretch; }

          .uw-floating-link { flex: 1 1 calc(50% - 6px); min-width: 0; }
          .uw-floating-top-btn { margin-left: 0; }

          .uw-tab { flex: 1 1 calc(50% - 4px); justify-content: center; }

          .uw-action-btn { font-size: .68rem; }

          .uw-save-strip { flex-direction: column; align-items: stretch; }

          .uw-save-strip > div:last-child { justify-content: stretch; }

          .uw-master-toolbar { align-items: flex-start; flex-direction: column; }

          .uw-master-jumps { justify-content: flex-start; }

          .uw-jump-btn { flex: 1 1 auto; }

        }



        /* Container Principale Dock */

.uw-floating-dock {

  background: rgba(255, 255, 255, 0.45);

  backdrop-filter: blur(16px) saturate(180%);

  -webkit-backdrop-filter: blur(16px) saturate(180%);

  border: 1px solid rgba(255, 255, 255, 0.6);

  border-radius: 20px;

  padding: 16px;

  box-shadow: 

    0 8px 32px 0 rgba(31, 38, 135, 0.12),

    inset 0 1px 1px 0 rgba(255, 255, 255, 0.8),

    inset 0 -1px 2px 0 rgba(0, 0, 0, 0.05);

  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

}



/* Header & Testi */

.uw-floating-dock-kicker {

  font-size: 0.75rem;

  font-weight: 700;

  letter-spacing: 0.08em;

  color: rgba(30, 41, 59, 0.7);

  text-transform: uppercase;

}



.uw-floating-dock-copy {

  font-size: 0.85rem;

  color: rgba(51, 65, 85, 0.85);

  margin-top: 2px;

}



/* Griglia o Flex per i link */

.uw-floating-links {

  display: flex;

  flex-wrap: wrap;

  gap: 8px;

  margin-top: 12px;

}



/* Pulsanti traslucidi (Liquid Buttons) */

.uw-floating-link,

.uw-floating-icon-btn {

  display: inline-flex;

  align-items: center;

  gap: 6px;

  background: rgba(255, 255, 255, 0.3);

  backdrop-filter: blur(8px);

  -webkit-backdrop-filter: blur(8px);

  border: 1px solid rgba(255, 255, 255, 0.5);

  border-radius: 12px;

  padding: 6px 12px;

  color: #1e293b;

  font-size: 0.85rem;

  font-weight: 500;

  cursor: pointer;

  box-shadow: 

    0 2px 6px rgba(0, 0, 0, 0.04),

    inset 0 1px 0 rgba(255, 255, 255, 0.6);

  transition: all 0.2s ease-in-out;

}



.uw-floating-icon-btn {

  padding: 8px;

  border-radius: 50%;

}



/* Hover & Active States con Effetto Fluido */

.uw-floating-link:hover,

.uw-floating-icon-btn:hover {

  background: rgba(255, 255, 255, 0.65);

  border-color: rgba(255, 255, 255, 0.9);

  transform: translateY(-2px);

  box-shadow: 

    0 6px 15px rgba(0, 0, 0, 0.08),

    inset 0 1px 1px rgba(255, 255, 255, 0.9);

}



.uw-floating-link:active,

.uw-floating-icon-btn:active {

  transform: translateY(0);

  background: rgba(255, 255, 255, 0.4);

}



/* Bottone Speciale "Nota" */

.uw-floating-note-add {

  background: rgba(59, 130, 246, 0.15);

  border-color: rgba(59, 130, 246, 0.3);

  color: #1d4ed8;

}



.uw-floating-note-add:hover {

  background: rgba(59, 130, 246, 0.25);

  border-color: rgba(59, 130, 246, 0.5);

}

.uw-floating-fiera-add {
  background: rgba(235, 181, 27, 0.14);
  border-color: rgba(235, 181, 27, 0.30);
  color: #d09a05;
}

.uw-floating-fiera-add:hover {
  background: rgba(235, 181, 27, 0.24);
  border-color: rgba(235, 181, 27, 0.48);
}

.uw-floating-top-btn {
  margin-left: auto;
}

.uw-floating-links-main {
  align-items: stretch;
}



/* Variante Dark Mode (Opzionale) */

@media (prefers-color-scheme: dark) {

  .uw-floating-dock {

    background: rgba(15, 23, 42, 0.55);

    border-color: rgba(255, 255, 255, 0.12);

    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);

  }



  .uw-floating-dock-kicker { color: rgba(241, 245, 249, 0.6); }

  .uw-floating-dock-copy { color: rgba(226, 232, 240, 0.8); }



  .uw-floating-link,

  .uw-floating-icon-btn {

    background: rgba(255, 255, 255, 0.06);

    border-color: rgba(255, 255, 255, 0.12);

    color: #f1f5f9;

  }



  .uw-floating-link:hover,

  .uw-floating-icon-btn:hover {

    background: rgba(255, 255, 255, 0.15);

    border-color: rgba(255, 255, 255, 0.25);

  }



  .uw-floating-note-add {

    background: rgba(59, 130, 246, 0.25);

    border-color: rgba(59, 130, 246, 0.4);

    color: #93c5fd;

  }

}



/* Container principale: stato compatto (Tasto Liquid Glass) */

.uw-floating-dock {

  position: fixed; /* o absolute a seconda del layout */

  bottom: 20px;

  right: 20px;

  z-index: 1000;

  

  /* Aspetto del pulsante compatto */

  background: rgba(255, 255, 255, 0.45);

  backdrop-filter: blur(16px) saturate(180%);

  -webkit-backdrop-filter: blur(16px) saturate(180%);

  border: 1px solid rgba(255, 255, 255, 0.6);

  border-radius: 30px;

  padding: 10px 18px;

  

  box-shadow: 

    0 8px 32px 0 rgba(31, 38, 135, 0.12),

    inset 0 1px 1px 0 rgba(255, 255, 255, 0.8);

    

  /* Transizione fluida Liquid */

  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  overflow: hidden;

  cursor: pointer;

  max-width: 200px;

  max-height: 48px;

}



/* Pulsante visibile quando è compatto */

.uw-floating-dock-trigger {

  display: flex;

  align-items: center;

  gap: 8px;

  font-size: 0.85rem;

  font-weight: 600;

  color: #1e293b;

  white-space: nowrap;

  transition: opacity 0.2s ease;

}



/* Contenuto nascosto di default */

.uw-floating-dock-content {

  opacity: 0;

  visibility: hidden;

  pointer-events: none;

  transition: opacity 0.3s ease 0.1s;

  width: 320px; /* Larghezza del pannello aperto */

}



/* --- STATO HOVER: ESPANSIONE --- */

.uw-floating-dock:hover {

  border-radius: 20px;

  padding: 16px;

  max-width: 360px;

  max-height: 500px; /* Altezza sufficiente ad ospitare tutto */

  background: rgba(255, 255, 255, 0.65);

  box-shadow: 

    0 12px 40px 0 rgba(31, 38, 135, 0.18),

    inset 0 1px 1px 0 rgba(255, 255, 255, 0.9);

}



.uw-floating-dock:hover .uw-floating-dock-trigger {

  display: none; /* Nasconde il trigger al passaggio */

}



.uw-floating-dock:hover .uw-floating-dock-content {

  opacity: 1;

  visibility: visible;

  pointer-events: auto;

}



/* Layout interno del pannello aperto */

.uw-floating-dock-head {

  display: flex;

  justify-content: space-between;

  align-items: flex-start;

  gap: 12px;

}



.uw-floating-dock-kicker {

  font-size: 0.7rem;

  font-weight: 700;

  letter-spacing: 0.08em;

  color: rgba(30, 41, 59, 0.7);

}



.uw-floating-dock-copy {

  font-size: 0.8rem;

  color: rgba(51, 65, 85, 0.85);

  margin-top: 2px;

}



.uw-floating-links {

  display: flex;

  flex-wrap: wrap;

  gap: 8px;

  margin-top: 14px;

}



/* Stile bottoni interni */

.uw-floating-link,

.uw-floating-icon-btn {

  display: inline-flex;

  align-items: center;

  gap: 6px;

  background: rgba(255, 255, 255, 0.35);

  backdrop-filter: blur(8px);

  border: 1px solid rgba(255, 255, 255, 0.6);

  border-radius: 12px;

  padding: 6px 12px;

  color: #1e293b;

  font-size: 0.82rem;

  font-weight: 500;

  cursor: pointer;

  transition: all 0.2s ease;

}



.uw-floating-icon-btn {

  padding: 6px;

  border-radius: 50%;

}



.uw-floating-link:hover,

.uw-floating-icon-btn:hover {

  background: rgba(255, 255, 255, 0.8);

  transform: translateY(-1px);

}



.uw-floating-note-add {

  background: rgba(59, 130, 246, 0.15);

  border-color: rgba(59, 130, 246, 0.3);

  color: #1d4ed8;

}



/* Supporto Dark Mode per lo stato aperto/chiuso */

@media (prefers-color-scheme: dark) {

  .uw-floating-dock {

    background: rgba(15, 23, 42, 0.5);

    border-color: rgba(255, 255, 255, 0.15);

  }

  .uw-floating-dock-trigger { color: #f1f5f9; }

  

  .uw-floating-dock:hover {

    background: rgba(15, 23, 42, 0.75);

  }

  

  .uw-floating-dock-kicker { color: rgba(241, 245, 249, 0.6); }

  .uw-floating-dock-copy { color: rgba(226, 232, 240, 0.8); }

  

  .uw-floating-link,

  .uw-floating-icon-btn {

    background: rgba(255, 255, 255, 0.08);

    border-color: rgba(255, 255, 255, 0.15);

    color: #f1f5f9;

  }

}



/* Forza la dimensione ridotta tipo Post-it */

.uw-postit-modal-dialog {

  max-width: 360px !important; /* Dimensione ridotta e compatta */

  margin: 1.75rem auto;

}



/* Contenitore Modale Liquid Glass */

.uw-note-modal {

  background: rgba(255, 255, 255, 0.7) !important;

  backdrop-filter: blur(20px) saturate(180%);

  -webkit-backdrop-filter: blur(10px) saturate(180%);

  border: 1px solid rgba(255, 255, 255, 0.8) !important;

  border-radius: 18px !important;

  box-shadow: 

    0 16px 36px 0 rgba(31, 38, 135, 0.15),

    inset 0 1px 1px 0 rgba(255, 255, 255, 0.9) !important;

  overflow: hidden;

}



/* Modale Header & Footer compatti */

.uw-note-modal-header,

.uw-note-modal-footer {

  border: none !important;

  background: transparent !important;

  padding: 12px 16px !important;

}



.uw-note-modal-title {

  font-size: 0.88rem;

  font-weight: 700;

  color: #1e293b;

  display: flex;

  align-items: center;

}



.uw-draft-hint {

  font-size: 0.75rem;

  color: #64748b;

  background: rgba(255, 255, 255, 0.4);

  padding: 6px 10px;

  border-radius: 8px;

  border: 1px solid rgba(255, 255, 255, 0.5);

}



/* Form Controls stile Vetro */

.uw-note-modal .uw-input {

  background: rgba(255, 255, 255, 0.5) !important;

  border: 1px solid rgba(255, 255, 255, 0.7) !important;

  border-radius: 10px !important;

  color: #1e293b !important;

  font-size: 0.82rem !important;

}



.uw-note-modal .uw-input:focus {

  background: rgba(255, 255, 255, 0.85) !important;

  border-color: rgba(59, 130, 246, 0.5) !important;

  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;

}



/* Dark Mode */

@media (prefers-color-scheme: dark) {

  .uw-note-modal {

    background: rgba(255, 255, 255, 0.06) !important;

    border-color: rgba(255, 255, 255, 0.15) !important;

  }



  .uw-note-modal-title { color: #f1f5f9; }



  .uw-note-modal .uw-input {

    background: rgba(255, 255, 255, 0.25) !important;

    border-color: rgba(255, 255, 255, 0.12) !important;

    color: #000000 !important;

  }

}



/* Centratura perfetta sia orizzontale che verticale per la modale Post-it */

.modal-dialog.uw-postit-modal-dialog {

  max-width: 750px !important;

  width: 90% !important;

  margin-left: auto !important;

  margin-right: auto !important;

}





       /* ────────────────────────────────────────────────────────────────────────

           GLOBAL LIQUID GLASS THEME — DARK GRAPHITE + WHITE + GOLD

           Override finale: mantiene invariata la logica e uniforma l'intera UI.

        ──────────────────────────────────────────────────────────────────────── */

        .uw-liquid-page {

          --uw-gold: #ebb51b;

          --uw-gold-dark: #a67a10;

          --uw-bg: #181b21;

          --uw-bg-soft: #22262e;

          --uw-panel: rgba(35, 39, 47, .72);

          --uw-panel-strong: rgba(43, 47, 56, .88);

          --uw-panel-soft: rgba(255, 255, 255, .055);

          --uw-white: #f7f7f4;

          --uw-text: #f1f1ee;

          --uw-muted: #a9aaad;

          --uw-muted-2: #81838a;

          --uw-glass-border: rgba(255, 255, 255, .11);

          --uw-glass-highlight: rgba(255, 255, 255, .08);

          --uw-gold-border: rgba(235, 181, 27, .36);

          --uw-shadow: 0 22px 54px rgba(0, 0, 0, .40);

          color: var(--uw-text);

          background: transparent;

        }



        .uw-liquid-page,

        .uw-liquid-page .card,

        .uw-liquid-page .modal-content,

        .uw-liquid-page .table,

        .uw-liquid-page .form-control,

        .uw-liquid-page .form-select {

          color-scheme: dark;

        }



        .uw-liquid-page > nav,

        .uw-liquid-page > .navbar,

        .uw-liquid-page .navbar {

          background: rgba(22, 25, 31, .80) !important;

          backdrop-filter: blur(24px) saturate(145%);

          -webkit-backdrop-filter: blur(24px) saturate(145%);

          border-bottom: 1px solid rgba(235,181,27,.23) !important;

          box-shadow: 0 12px 34px rgba(0,0,0,.32), inset 0 -1px 0 rgba(255,255,255,.04);

          color: var(--uw-white) !important;

        }

        .uw-liquid-page .navbar * { color: inherit; }



        .uw-liquid-page .uw-page-scroll {

          position: relative;

          background:

            radial-gradient(circle at 12% 8%, rgba(235,181,27,.10), transparent 28%),

            radial-gradient(circle at 88% 14%, rgba(255,255,255,.045), transparent 25%),

            radial-gradient(circle at 62% 85%, rgba(166,122,16,.055), transparent 34%),

            linear-gradient(145deg, #191c22 0%, #242831 52%, #15181e 100%);

        }



        .uw-liquid-page .uw-card {

          position: relative;

          background:

            radial-gradient(circle at 12% 0%, rgba(255,255,255,.070), transparent 31%),

            linear-gradient(145deg, rgba(62, 62, 63, 0.88), rgba(74, 81, 94, 0.78)) !important;

          border: 1px solid var(--uw-glass-border) !important;

          border-radius: 24px !important;

          box-shadow:

            var(--uw-shadow),

            inset 0 1px 0 rgba(255,255,255,.08),

            inset 0 -1px 0 rgba(0,0,0,.35) !important;

          backdrop-filter: blur(24px) saturate(145%);

          -webkit-backdrop-filter: blur(24px) saturate(145%);

          overflow: hidden;

          color: var(--uw-text);

        }



        .uw-liquid-page .uw-card::after {

          content: "";

          position: absolute;

          inset: 0;

          pointer-events: none;

          border-radius: inherit;

          background: linear-gradient(115deg, rgba(255,255,255,.045), transparent 24%, transparent 74%, rgba(235,181,27,.025));

        }

        .uw-liquid-page .uw-card > * { position: relative; z-index: 1; }



        .uw-liquid-page .uw-card-accent {

          height: 2px;

          background: linear-gradient(90deg, transparent 0%, rgba(235,181,27,.32) 12%, #ebb51b 48%, rgba(166,122,16,.76) 78%, transparent 100%);

          box-shadow: 0 0 18px rgba(235,181,27,.22);

        }



        .uw-liquid-page .uw-section-box,

        .uw-liquid-page .uw-master-toolbar,

        .uw-liquid-page .uw-save-strip,

        .uw-liquid-page .uw-readonly-banner,

        .uw-liquid-page .uw-readonly-status,

        .uw-liquid-page .uw-draft-hint {

          background:

            linear-gradient(145deg, rgba(255,255,255,.090), rgba(255,255,255,.025)) !important;

          border: 1px solid rgba(255,255,255,.095) !important;

          border-radius: 16px !important;

          box-shadow: 0 10px 28px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.070) !important;

          backdrop-filter: blur(16px) saturate(125%);

          -webkit-backdrop-filter: blur(16px) saturate(125%);

          color: var(--uw-text);

        }



        .uw-liquid-page .uw-master-toolbar,

        .uw-liquid-page .uw-save-strip {

          border-left: 1px solid rgba(235,181,27,.22) !important;

          background:

            radial-gradient(circle at 0 0, rgba(235,181,27,.10), transparent 40%),

            linear-gradient(145deg, rgba(255,255,255,.080), rgba(255,255,255,.025)) !important;

        }



        .uw-liquid-page .uw-master-section {

          border-top: 1px solid rgba(235,181,27,.13);

        }



        .uw-liquid-page .uw-section-icon {

          width: 27px;

          height: 27px;

          border-radius: 9px;

          background: linear-gradient(145deg, rgba(235,181,27,.19), rgba(255,255,255,.04));

          border: 1px solid rgba(235,181,27,.30);

          color: #ebb51b;

          box-shadow: inset 0 1px 0 rgba(255,255,255,.07), 0 5px 14px rgba(0,0,0,.18);

        }



        .uw-liquid-page .uw-section-title,

        .uw-liquid-page .uw-master-kicker,

        .uw-liquid-page .uw-floating-dock-kicker {

          color: #e6bd4d;

          letter-spacing: .095em;

        }



        .uw-liquid-page .uw-page-title,

        .uw-liquid-page h1,

        .uw-liquid-page h2,

        .uw-liquid-page h3,

        .uw-liquid-page h4,

        .uw-liquid-page h5,

        .uw-liquid-page h6,

        .uw-liquid-page strong,

        .uw-liquid-page .fw-semibold,

        .uw-liquid-page .fw-bold {

          color: var(--uw-white) !important;

        }



        .uw-liquid-page .text-muted,

        .uw-liquid-page .small.text-muted,

        .uw-liquid-page .uw-search-live-hint,

        .uw-liquid-page .uw-master-subtitle,

        .uw-liquid-page .uw-floating-dock-copy,

        .uw-liquid-page .uw-stat span,

        .uw-liquid-page .uw-info-row span {

          color: var(--uw-muted) !important;

        }



        .uw-liquid-page .uw-eyebrow-dash {

          width: 20px;

          height: 3px;

          background: linear-gradient(90deg, #ebb51b, #8d6810);

          box-shadow: 0 0 14px rgba(235,181,27,.28);

        }



        .uw-liquid-page .uw-label {

          color: #c6c6c3 !important;

          letter-spacing: .055em;

        }



        .uw-liquid-page .uw-input,

        .uw-liquid-page .form-control.uw-input,

        .uw-liquid-page .form-select.uw-input {

          background: linear-gradient(145deg, rgba(255,255,255,.090), rgba(255,255,255,.038)) !important;

          border: 1px solid rgba(255,255,255,.115) !important;

          color: var(--uw-white) !important;

          -webkit-text-fill-color: var(--uw-white);

          border-radius: 12px !important;

          min-height: 38px;

          box-shadow: inset 0 1px 0 rgba(255,255,255,.070), 0 4px 14px rgba(0,0,0,.16);

          backdrop-filter: blur(12px);

          -webkit-backdrop-filter: blur(12px);

          transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;

        }

        .uw-liquid-page .uw-input::placeholder,

        .uw-liquid-page .form-control::placeholder { color: #777a80 !important; opacity: 1; }

        .uw-liquid-page .uw-input:hover:not(:focus) {

          background: rgba(255,255,255,.085) !important;

          border-color: rgba(235,181,27,.24) !important;

        }

        .uw-liquid-page .uw-input:focus {

          background: rgba(255,255,255,.10) !important;

          border-color: rgba(235,181,27,.68) !important;

          box-shadow: 0 0 0 3px rgba(235,181,27,.11), 0 8px 24px rgba(0,0,0,.22) !important;

          color: #fff !important;

          -webkit-text-fill-color: #fff;

        }

        .uw-liquid-page select.uw-input option {

          background: #2a2e36;

          color: #f6f6f3;

        }



        .uw-liquid-page .uw-input-icon { color: #c49a2c; }



        .uw-liquid-page .uw-checkbox .form-check-input {

          background-color: rgba(255,255,255,.070);

          border: 1px solid rgba(235,181,27,.30);

          box-shadow: inset 0 1px 0 rgba(255,255,255,.05);

        }

        .uw-liquid-page .uw-checkbox .form-check-input:checked {

          background-color: #ebb51b !important;

          border-color: #ebb51b !important;

          box-shadow: 0 0 0 3px rgba(235,181,27,.10), 0 0 14px rgba(235,181,27,.15);

        }

        .uw-liquid-page .uw-checkbox .form-check-label,

        .uw-liquid-page .form-check-label {

          color: #e5e5e2 !important;

        }



        .uw-liquid-page .uw-tabs {

          gap: 7px;

          padding: 7px;

          background: rgba(255,255,255,.050);

          border: 1px solid rgba(255,255,255,.090);

          border-radius: 18px;

          box-shadow: inset 0 1px 0 rgba(255,255,255,.045), 0 9px 26px rgba(0,0,0,.18);

          backdrop-filter: blur(16px);

          -webkit-backdrop-filter: blur(16px);

        }

        .uw-liquid-page .uw-tab {

          border-radius: 12px;

          padding: 9px 13px;

          color: #a7a8ab;

        }

        .uw-liquid-page .uw-tab svg { color: #898b90; }

        .uw-liquid-page .uw-tab:hover:not(.active) {

          color: #f1cf72;

          background: rgba(235,181,27,.075);

          border-color: rgba(235,181,27,.12);

        }

        .uw-liquid-page .uw-tab:hover:not(.active) svg { color: #ebb51b; }

        .uw-liquid-page .uw-tab.active {

          color: #fff;

          background: linear-gradient(145deg, rgba(235,181,27,.15), rgba(255,255,255,.070));

          border: 1px solid rgba(235,181,27,.34);

          box-shadow: 0 8px 20px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.080);

        }

        .uw-liquid-page .uw-tab.active svg { color: #ebb51b; }

        .uw-liquid-page .uw-tab-badge {

          background: linear-gradient(145deg, #f2c84b, #a67a10);

          color: #16171b;

          box-shadow: inset 0 1px 0 rgba(255,255,255,.34), 0 0 12px rgba(235,181,27,.12);

        }



        .uw-liquid-page .uw-action-btn,

        .uw-liquid-page .uw-mini-btn,

        .uw-liquid-page .uw-jump-btn,

        .uw-liquid-page .uw-floating-link,

        .uw-liquid-page .uw-floating-icon-btn {

          border: 1px solid rgba(235,181,27,.30);

          background: linear-gradient(145deg, rgba(255,255,255,.080), rgba(255,255,255,.025));

          color: #edc85f;

          border-radius: 999px;

          box-shadow: 0 6px 16px rgba(0,0,0,.22), inset 0 1px 0 rgba(255,255,255,.070);

          backdrop-filter: blur(12px) saturate(125%);

          -webkit-backdrop-filter: blur(12px) saturate(125%);

          transition: transform .18s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease;

        }

        .uw-liquid-page .uw-action-btn:hover:not(:disabled),

        .uw-liquid-page .uw-mini-btn:hover,

        .uw-liquid-page .uw-jump-btn:hover,

        .uw-liquid-page .uw-floating-link:hover,

        .uw-liquid-page .uw-floating-icon-btn:hover {

          background: linear-gradient(145deg, rgba(235,181,27,.14), rgba(255,255,255,.070));

          border-color: rgba(235,181,27,.65);

          color: #ffe29a;

          transform: translateY(-1px);

          box-shadow: 0 9px 24px rgba(0,0,0,.28), 0 0 18px rgba(235,181,27,.06), inset 0 1px 0 rgba(255,255,255,.07);

        }



        .uw-liquid-page .uw-action-solid,

        .uw-liquid-page .uw-floating-note-add {

          background: linear-gradient(145deg, #f4cf61 0%, #ebb51b 48%, #9b710f 100%) !important;

          border-color: rgba(255,225,130,.72) !important;

          color: #16171b !important;

          box-shadow: 0 8px 24px rgba(166,122,16,.26), inset 0 1px 0 rgba(255,255,255,.48) !important;

        }

        .uw-liquid-page .uw-action-solid:hover:not(:disabled),

        .uw-liquid-page .uw-floating-note-add:hover {

          background: linear-gradient(145deg, #ffe18a 0%, #dca514 58%, #8d660d 100%) !important;

          color: #101114 !important;

          transform: translateY(-1px);

        }



        .uw-liquid-page .uw-danger-btn,

        .uw-liquid-page .uw-mini-btn.danger {

          background: rgba(203,76,76,.075);

          border-color: rgba(235,95,95,.35);

          color: #ff9b9b;

        }

        .uw-liquid-page .uw-danger-btn:hover:not(:disabled),

        .uw-liquid-page .uw-mini-btn.danger:hover {

          background: rgba(203,76,76,.16);

          border-color: rgba(255,120,120,.50);

          color: #ffd0d0;

        }



        .uw-liquid-page .uw-table-wrap,

        .uw-liquid-page .uw-search-results {

          border: 1px solid rgba(255,255,255,.085);

          border-radius: 16px;

          background: rgba(255,255,255,.025);

          box-shadow: inset 0 1px 0 rgba(255,255,255,.045), 0 10px 28px rgba(0,0,0,.18);

          backdrop-filter: blur(14px);

          -webkit-backdrop-filter: blur(14px);

        }

        .uw-liquid-page .uw-table { background: transparent !important; color: var(--uw-text) !important; }

        .uw-liquid-page .uw-table thead th {

          background: rgba(255,255,255,.045) !important;

          color: #bdbebf !important;

          border-bottom-color: rgba(235,181,27,.14) !important;

        }

        .uw-liquid-page .uw-table td {

          background: rgba(255,255,255,.018) !important;

          color: #eeeeeb !important;

          border-color: rgba(255,255,255,.070) !important;

        }

        .uw-liquid-page .uw-table tbody tr:hover td { background: rgba(235,181,27,.055) !important; }

        .uw-liquid-page .uw-table .text-muted { color: #96989d !important; }



        .uw-liquid-page .uw-pill,

        .uw-liquid-page .uw-note-source,

        .uw-liquid-page .uw-draft-badge {

          background: linear-gradient(145deg, rgba(235,181,27,.15), rgba(255,255,255,.050));

          border: 1px solid rgba(235,181,27,.28);

          color: #f1cb63;

          box-shadow: inset 0 1px 0 rgba(255,255,255,.070);

        }



        .uw-liquid-page .uw-note,

        .uw-liquid-page .uw-stat,

        .uw-liquid-page .uw-info-row {

          background: linear-gradient(145deg, rgba(255,255,255,.070), rgba(255,255,255,.022));

          border: 1px solid rgba(255,255,255,.085);

          border-radius: 14px;

          color: var(--uw-text);

          box-shadow: 0 8px 22px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.045);

          backdrop-filter: blur(12px);

          -webkit-backdrop-filter: blur(12px);

        }

        .uw-liquid-page .uw-note { border-left: 2px solid rgba(235,181,27,.65); }

        .uw-liquid-page .uw-stat strong,

        .uw-liquid-page .uw-info-row strong { color: #fff !important; }



        .uw-liquid-page .uw-infinity-status {

          background: linear-gradient(145deg, rgba(65,146,92,.13), rgba(255,255,255,.025));

          color: #9be1b1;

          border: 1px solid rgba(86,190,121,.22);

          border-left: 3px solid rgba(86,190,121,.62);

          border-radius: 14px;

          box-shadow: inset 0 1px 0 rgba(255,255,255,.045);

        }



        .uw-liquid-page .uw-readonly-view .uw-input:disabled,

        .uw-liquid-page .uw-input:disabled {

          opacity: 1;

          background: rgba(255,255,255,.032) !important;

          border-color: rgba(255,255,255,.07) !important;

          color: #a8a9ac !important;

          -webkit-text-fill-color: #a8a9ac !important;

          cursor: default;

        }

        .uw-liquid-page .uw-readonly-view .form-check-input:disabled ~ .form-check-label {

          color: #b7b8ba !important;

        }



        .uw-liquid-page .uw-floating-dock {

          right: 20px;

          bottom: 20px;

          padding: 10px 18px;

          border-radius: 30px;

          background:

            radial-gradient(circle at 18% 0%, rgba(255,255,255,.08), transparent 40%),

            linear-gradient(145deg, rgba(28,31,37,.82), rgba(13,15,19,.70));

          border: 1px solid rgba(255,255,255,.12);

          box-shadow: 0 16px 44px rgba(0,0,0,.42), inset 0 1px 0 rgba(255,255,255,.085), inset 0 -1px 0 rgba(235,181,27,.09);

          backdrop-filter: blur(24px) saturate(145%);

          -webkit-backdrop-filter: blur(24px) saturate(145%);

        }

        .uw-liquid-page .uw-floating-dock-trigger {

          color: #fff;

          letter-spacing: .015em;

        }

        .uw-liquid-page .uw-floating-dock-trigger svg { color: #ebb51b; }

        .uw-liquid-page .uw-floating-dock:hover {

          background:

            radial-gradient(circle at 12% 0%, rgba(235,181,27,.075), transparent 36%),

            linear-gradient(145deg, rgba(35,38,45,.94), rgba(18,20,25,.88));

          border-color: rgba(235,181,27,.30);

          box-shadow: 0 20px 54px rgba(0,0,0,.48), 0 0 24px rgba(235,181,27,.035), inset 0 1px 0 rgba(255,255,255,.09);

        }



        .uw-liquid-page .uw-note-modal {

          background:

            radial-gradient(circle at 10% 0%, rgba(235,181,27,.09), transparent 36%),

            linear-gradient(145deg, rgba(31,34,41,.96), rgba(15,17,21,.92)) !important;

          border: 1px solid rgba(255,255,255,.11) !important;

          border-radius: 22px !important;

          color: var(--uw-text) !important;

          box-shadow: 0 26px 70px rgba(0,0,0,.52), inset 0 1px 0 rgba(255,255,255,.090) !important;

          backdrop-filter: blur(26px) saturate(140%);

          -webkit-backdrop-filter: blur(26px) saturate(140%);

        }

        .uw-liquid-page .uw-note-modal::before {

          height: 2px;

          background: linear-gradient(90deg, transparent, #ebb51b, #a67a10, transparent);

        }

        .uw-liquid-page .uw-note-modal-header,

        .uw-liquid-page .uw-note-modal-footer { background: transparent !important; }

        .uw-liquid-page .uw-note-modal-title { color: #fff !important; }

        .uw-liquid-page .uw-note-modal .btn-close { filter: invert(1) grayscale(1); opacity: .72; }

        .uw-liquid-page .uw-note-modal .uw-input { color: #fff !important; -webkit-text-fill-color: #fff !important; }

        .uw-liquid-page .uw-note-modal .uw-input:focus {

          border-color: rgba(235,181,27,.72) !important;

          box-shadow: 0 0 0 3px rgba(235,181,27,.11) !important;

        }



        .uw-liquid-page .uw-success-popup {

          background: linear-gradient(145deg, rgba(28,47,34,.94), rgba(17,29,21,.92));

          border: 1px solid rgba(123,215,151,.19);

          border-left: 3px solid rgba(82,200,116,.72);

          color: #baf0ca;

          border-radius: 18px;

          box-shadow: 0 22px 58px rgba(0,0,0,.44), inset 0 1px 0 rgba(255,255,255,.070);

          backdrop-filter: blur(20px) saturate(135%);

          -webkit-backdrop-filter: blur(20px) saturate(135%);

        }



        @media (max-width: 767.98px) {

          .uw-liquid-page .uw-card { border-radius: 18px !important; }

          .uw-liquid-page .uw-section-box,

          .uw-liquid-page .uw-master-toolbar,

          .uw-liquid-page .uw-save-strip { border-radius: 14px !important; }

          .uw-liquid-page .uw-tab { flex: 1 1 calc(50% - 7px); justify-content: center; }

          .uw-liquid-page .uw-floating-dock {

            left: 10px;

            right: 10px;

            bottom: 10px;

            width: auto;

          }

          .uw-liquid-page .uw-floating-dock:hover { max-width: none; }

        }





        .uw-liquid-page .uw-editable-status {

          display: inline-flex;

          align-items: center;

          gap: 8px;

          min-height: 34px;

          padding: 7px 11px;

          border: 1px solid rgba(72, 187, 120, .28);

          border-radius: 12px;

          background: rgba(72, 187, 120, .08);

          color: #baf0ca;

          font-size: .72rem;

          font-weight: 800;

          letter-spacing: .04em;

        }



        .uw-liquid-page .uw-editable-status-dot {

          width: 7px;

          height: 7px;

          border-radius: 50%;

          background: #68d391;

          box-shadow: 0 0 0 4px rgba(104,211,145,.10);

        }



        .uw-liquid-page .uw-search-empty {

          padding: 9px 11px;

          border: 1px dashed rgba(235,181,27,.22);

          border-radius: 12px;

          background: rgba(235,181,27,.04);

          color: #b7b9bd;

          font-size: .78rem;

        }



        /* ==========================================================================

   LAYOUT EDITOR + LISTA

   Fiere / Rubrica-Contatti / Note

   Da aggiungere IN FONDO al CSS attuale.

   ========================================================================== */



/* RUBRICA: frame esterno fisso, un solo scroll sulla tabella interna. */
.uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-frame {
  overflow: hidden !important;
  min-height: 0;
}

.uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-scroll {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(235,181,27,.55) rgba(255,255,255,.035);
}

.uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-scroll .uw-table {
  min-width: 900px;
}

.uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-scroll::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-scroll::-webkit-scrollbar-track {
  background: rgba(255,255,255,.025);
  border-radius: 999px;
}

.uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(242,200,75,.72), rgba(166,122,16,.68));
  border-radius: 999px;
  border: 2px solid rgba(28,31,37,.9);
}

/* Le altre liste mantengono il contenitore scrollabile dedicato. */

.uw-liquid-page .uw-matched-list {

  min-height: 0;

  overflow: auto !important;

  scrollbar-gutter: stable;

  scrollbar-width: thin;

  scrollbar-color: rgba(235,181,27,.55) rgba(255,255,255,.035);

}



/* Pannello lista note: stesso linguaggio Liquid Glass del resto della UI. */

.uw-liquid-page #uw-sezione-note .uw-note-list {

  padding: 10px;

  border: 1px solid rgba(255,255,255,.085);

  border-radius: 18px;

  background:

    radial-gradient(circle at 12% 0%, rgba(235,181,27,.055), transparent 34%),

    linear-gradient(145deg, rgba(255,255,255,.045), rgba(255,255,255,.018));

  box-shadow:

    0 12px 30px rgba(0,0,0,.18),

    inset 0 1px 0 rgba(255,255,255,.05);

  backdrop-filter: blur(14px) saturate(120%);

  -webkit-backdrop-filter: blur(14px) saturate(120%);

}



/* Header tabelle sempre visibile durante lo scroll verticale. */

.uw-liquid-page #uw-sezione-rubrica .uw-table thead th,

.uw-liquid-page #uw-sezione-fiere .uw-table thead th {

  position: sticky;

  top: 0;

  z-index: 4;

  background: rgba(39, 42, 49, .96) !important;

  backdrop-filter: blur(14px);

  -webkit-backdrop-filter: blur(14px);

  box-shadow: 0 1px 0 rgba(235,181,27,.13);

}



.uw-liquid-page .uw-matched-list::-webkit-scrollbar {

  width: 8px;

  height: 8px;

}



.uw-liquid-page .uw-matched-list::-webkit-scrollbar-track {

  background: rgba(255,255,255,.025);

  border-radius: 999px;

}



.uw-liquid-page .uw-matched-list::-webkit-scrollbar-thumb {

  background: linear-gradient(

    180deg,

    rgba(242,200,75,.72),

    rgba(166,122,16,.68)

  );

  border-radius: 999px;

  border: 2px solid rgba(28,31,37,.9);

}



/* ========================================================================== 

   DESKTOP: editor a sinistra, lista a destra.

   L'altezza della lista arriva dal ResizeObserver tramite --uw-list-height.

   ========================================================================== */

@media (min-width: 1200px) {

  .uw-liquid-page #uw-sezione-fiere > div:last-child,

  .uw-liquid-page #uw-sezione-rubrica > div:last-child,

  .uw-liquid-page #uw-sezione-note > div:last-child {

    display: grid;

    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    gap: 16px;

    align-items: start;

  }



  .uw-liquid-page #uw-sezione-fiere > div:last-child > .uw-section-box,

  .uw-liquid-page #uw-sezione-rubrica > div:last-child > .uw-section-box,

  .uw-liquid-page #uw-sezione-note > div:last-child > .uw-section-box {

    margin-bottom: 0 !important;

  }



  .uw-liquid-page #uw-sezione-rubrica > div:last-child > .uw-alert-note {

    margin-bottom: 0 !important;

  }



  .uw-liquid-page .uw-matched-list {

    height: var(--uw-list-height, 430px) !important;

    max-height: var(--uw-list-height, 430px) !important;

  }



  /* FIERE: select e pulsante uno sotto l'altro nella colonna editor. */

  .uw-liquid-page

    #uw-sezione-fiere

    > div:last-child

    > .uw-section-box

    .row

    > [class*="col-lg-"] {

    flex: 0 0 100%;

    width: 100%;

  }



  /* NOTE: tipo e descrizione verticali nella colonna editor. */

  .uw-liquid-page

    #uw-sezione-note

    > div:last-child

    > .uw-section-box

    .row

    > .col-lg-6 {

    flex: 0 0 100%;

    width: 100%;

  }



  .uw-liquid-page

    #uw-sezione-note

    > div:last-child

    > .uw-section-box

    .small.text-muted {

    padding-bottom: 0 !important;

    line-height: 1.4;

  }



  /* RUBRICA: campi riorganizzati per la colonna sinistra. */

  .uw-liquid-page

    #uw-sezione-rubrica

    > div:last-child

    > .uw-section-box

    > .row

    > div:nth-child(1) {

    flex: 0 0 100%;

    width: 100%;

  }



  .uw-liquid-page

    #uw-sezione-rubrica

    > div:last-child

    > .uw-section-box

    > .row

    > div:nth-child(2),

  .uw-liquid-page

    #uw-sezione-rubrica

    > div:last-child

    > .uw-section-box

    > .row

    > div:nth-child(3) {

    flex: 0 0 50%;

    width: 50%;

  }



  .uw-liquid-page

    #uw-sezione-rubrica

    > div:last-child

    > .uw-section-box

    > .row

    > div:nth-child(n+4) {

    flex: 0 0 100%;

    width: 100%;

  }

}



/* Tablet/mobile: layout verticale con liste compatte e scrollabili. */

@media (max-width: 1199.98px) {

  .uw-liquid-page .uw-matched-list {

    margin-top: 12px;

    max-height: 360px;

  }

  .uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-frame {
    height: 360px;
    max-height: 360px;
  }

}



@media (max-width: 767.98px) {

  .uw-liquid-page .uw-matched-list {

    max-height: 320px;

  }

  .uw-liquid-page #uw-sezione-rubrica .uw-rubrica-table-frame {
    height: 320px;
    max-height: 320px;
  }

}

.uw-all-contacts-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 9px 16px;
  border: 1px solid rgba(235, 181, 27, 0.4);
  border-radius: 8px;

  background: rgba(235, 181, 27, 0.08);
  color: #ebb51b;

  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;

  cursor: pointer;
  transition: all 0.2s ease;
}

.uw-all-contacts-btn:hover {
  background: #ebb51b;
  border-color: #ebb51b;
  color: #121419;
  transform: translateY(-1px);
}

/* =========================================================
   FILTRI NOTE - TUTTE / FATTE / DA FARE
   ========================================================= */

.uw-note-status-filters {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.uw-note-status-btn {
  appearance: none;
  border: 1px solid transparent;
  background: transparent;
  color: #8f949d;

  min-height: 34px;
  padding: 7px 14px;

  border-radius: 9px;

  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  line-height: 1;

  cursor: pointer;

  transition:
    background 0.18s ease,
    color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.uw-note-status-btn:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}

.uw-note-status-btn:active {
  transform: scale(0.97);
}

/* Pulsante attualmente selezionato */
.uw-note-status-btn.active {
  color: #111111;
  background: #ebb51b;
  border-color: #ebb51b;

  box-shadow:
    0 3px 10px rgba(235, 181, 27, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

/* Evita il bordo blu del browser */
.uw-note-status-btn:focus,
.uw-note-status-btn:focus-visible {
  outline: none;
  box-shadow: none;
}

.uw-note-status-btn.active:focus,
.uw-note-status-btn.active:focus-visible {
  box-shadow:
    0 3px 10px rgba(235, 181, 27, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

/* MOBILE */
@media (max-width: 576px) {
  .uw-note-status-filters {
    display: flex;
    width: 100%;
  }

  .uw-note-status-btn {
    flex: 1;
    padding: 8px 7px;
    font-size: 10px;
    white-space: nowrap;
  }
}


.uw-note-list-row {
  display: grid;
  grid-template-columns: 105px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: stretch;
}

.uw-note-row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-width: 72px;
}

.uw-note-row-action {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 9px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.035);
  color: #9ea3ac;
  cursor: pointer;
  transition:
    transform .15s ease,
    border-color .15s ease,
    background .15s ease,
    color .15s ease;
}

.uw-note-row-action:hover:not(:disabled) {
  transform: translateY(-1px);
}

.uw-note-row-action-edit:hover:not(:disabled) {
  color: #f0c84d;
  border-color: rgba(235, 181, 27, 0.48);
  background: rgba(235, 181, 27, 0.10);
}

.uw-note-row-action-delete:hover:not(:disabled) {
  color: #ff7b86;
  border-color: rgba(220, 53, 69, 0.55);
  background: rgba(220, 53, 69, 0.12);
}

.uw-note-row-action:disabled {
  opacity: .38;
  cursor: not-allowed;
}

.uw-note-list-date {
  display: flex;
  align-items: center;
  justify-content: center;

  padding: 8px 10px;

  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;

  background: rgba(255, 255, 255, 0.025);

  color: #8f949d;

  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.uw-note-list-row-done .uw-note-list-date {
  color: #6c9f7d;
  border-color: rgba(25, 135, 84, 0.2);
}

.uw-note-generated-title {
  min-height: 38px;

  display: flex;
  align-items: center;

  padding: 8px 12px;

  border-radius: 9px;
  border: 1px solid rgba(235, 181, 27, 0.22);

  background: rgba(235, 181, 27, 0.06);

  color: #ddd;
  font-size: 13px;
}


.uw-infinity-create-btn {
  color: #8fd3ff !important;
  border-color: rgba(81, 173, 232, 0.42) !important;
  background: rgba(81, 173, 232, 0.10) !important;
}

.uw-infinity-create-btn:hover:not(:disabled) {
  color: #c6ebff !important;
  border-color: rgba(104, 196, 255, 0.72) !important;
  background: rgba(81, 173, 232, 0.18) !important;
  box-shadow: 0 0 14px rgba(81, 173, 232, 0.13);
}

.uw-infinity-modal {
  border: 1px solid rgba(81, 173, 232, 0.22) !important;
}

.uw-infinity-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.uw-infinity-summary > div {
  padding: 11px 12px;
  border-radius: 11px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.03);
  min-width: 0;
}

.uw-infinity-summary span {
  display: block;
  color: #838994;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .6px;
  margin-bottom: 3px;
}

.uw-infinity-summary strong {
  display: block;
  color: #f2f4f7;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uw-infinity-contact-check {
  padding: 11px 12px;
  border-radius: 11px;
  border: 1px solid rgba(255,255,255,.07);
  background: rgba(255,255,255,.025);
}

.uw-infinity-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.035);
  color: #7f858f;
  font-size: 10px;
  font-weight: 700;
}

.uw-infinity-chip.ok {
  color: #8fd3ff;
  border-color: rgba(81,173,232,.32);
  background: rgba(81,173,232,.08);
}

.uw-infinity-created {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  color: #86d5a2;
  border: 1px solid rgba(25,135,84,.28);
  background: rgba(25,135,84,.09);
}

.uw-infinity-created div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.uw-infinity-created strong {
  color: #dff5e7;
  font-size: 12px;
}

.uw-infinity-created span {
  color: #8ba598;
  font-size: 10px;
}

.uw-infinity-merge-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
  max-height: 380px;
  overflow-y: auto;
}

.uw-infinity-merge-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 11px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.028);
}

.uw-duplicate-btn {
  border-color: rgba(235, 181, 27, 0.28);
  background: rgba(235, 181, 27, 0.06);
  color: #e8c75d;
}

.uw-duplicate-btn:hover:not(:disabled) {
  border-color: rgba(235, 181, 27, 0.48);
  background: rgba(235, 181, 27, 0.11);
  color: #f2d875;
}

.uw-duplicate-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid rgba(235, 181, 27, 0.22);
  border-radius: 12px;
  background: rgba(235, 181, 27, 0.055);
  color: #d9dde2;
}

.uw-duplicate-warning svg {
  flex: 0 0 auto;
  margin-top: 1px;
  color: #ebb51b;
}

.uw-duplicate-warning div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.uw-duplicate-warning strong {
  font-size: 11px;
  color: #f0f1f2;
}

.uw-duplicate-warning span {
  font-size: 10px;
  line-height: 1.45;
  color: #9298a2;
}

.uw-duplicate-results {
  max-height: 360px;
  overflow: auto;
}

.uw-infinity-address-box {
  padding: 12px;
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 12px;
  background: rgba(255,255,255,.025);
}

.uw-infinity-merge-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.uw-infinity-merge-info strong {
  color: #edf0f3;
  font-size: 11px;
}

.uw-infinity-merge-info span {
  color: #858b95;
  font-size: 9px;
}

.uw-infinity-merge-mode {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 800;
  white-space: nowrap;
}

.uw-infinity-merge-mode.matched {
  color: #82d99f;
  border: 1px solid rgba(25,135,84,.30);
  background: rgba(25,135,84,.09);
}

.uw-infinity-merge-mode.copy {
  color: #8fd3ff;
  border: 1px solid rgba(81,173,232,.30);
  background: rgba(81,173,232,.08);
}

.uw-infinity-empty-merge {
  padding: 18px;
  border-radius: 12px;
  text-align: center;
  color: #949aa4;
  border: 1px dashed rgba(255,255,255,.12);
  background: rgba(255,255,255,.02);
  font-size: 11px;
}

@media (max-width: 576px) {
  .uw-infinity-summary {
    grid-template-columns: 1fr;
  }

  .uw-infinity-merge-row {
    grid-template-columns: 1fr;
  }

  .uw-infinity-merge-mode {
    width: fit-content;
  }

  .uw-note-list-row {
    grid-template-columns: 72px minmax(0, 1fr) auto;
    gap: 6px;
  }

  .uw-note-list-date {
    padding: 7px 4px;
    font-size: 9px;
  }

  .uw-note-row-actions {
    gap: 4px;
    min-width: 60px;
  }

  .uw-note-row-action {
    width: 28px;
    height: 28px;
    border-radius: 8px;
  }
}



/* ==========================================================================
   MODALI — LIQUID GLASS FINALE
   Stile unificato per tutti i modal della Gestione Contatti.
   ========================================================================== */

.modal-backdrop.show {
  background:
    radial-gradient(circle at 18% 8%, rgba(235,181,27,.10), transparent 30%),
    radial-gradient(circle at 84% 18%, rgba(255,255,255,.055), transparent 28%),
    #05070a !important;
  opacity: .78 !important;
  backdrop-filter: blur(13px) saturate(125%);
  -webkit-backdrop-filter: blur(13px) saturate(125%);
}

.modal-dialog.uw-liquid-glass-dialog {
  width: 50vw !important;
  max-width: 720px !important;
  min-width: 420px;

  /* Centratura forzata: evita che CSS esterni di Bootstrap/progetto
     spostino il dialog a sinistra. */
  position: absolute !important;
  left: 50% !important;
  top: 50% !important;
  margin: 0 !important;
  transform: translate(-50%, -50%) !important;

  filter: drop-shadow(0 30px 70px rgba(0,0,0,.42));
}

/* React-Bootstrap aggiunge modal-dialog-centered: manteniamo comunque
   la centratura in modo esplicito e indipendente dagli stili globali. */
.modal.show .modal-dialog.uw-liquid-glass-dialog,
.modal.fade.show .modal-dialog.uw-liquid-glass-dialog,
.modal-dialog-centered.uw-liquid-glass-dialog {
  left: 50% !important;
  top: 50% !important;
  margin: 0 !important;
  transform: translate(-50%, -50%) !important;
}

/* Se il contenuto è alto, resta dentro lo schermo e scorre solo il body. */
.uw-liquid-glass-modal {
  max-height: calc(100vh - 32px);
}

.uw-liquid-glass-modal .modal-body {
  overflow-y: auto;
}

/* Modal compatti: note, fiera, modifica nota, dettaglio nota */
.modal-dialog.uw-postit-modal-dialog.uw-liquid-glass-dialog {
  width: 50vw !important;
  max-width: 680px !important;
  min-width: 420px;
}

/* I modal grandi restano più larghi perché contengono tabelle/form complessi,
   ma non occupano mai tutta la pagina. */
.modal-dialog.modal-lg.uw-liquid-glass-dialog {
  width: 68vw !important;
  max-width: 980px !important;
  min-width: 640px;
}

.uw-liquid-glass-modal {
  position: relative;
  isolation: isolate;
  color: #f5f6f8 !important;

  background:
    linear-gradient(
      145deg,
      rgba(43,47,56,.84) 0%,
      rgba(29,32,39,.78) 46%,
      rgba(19,22,28,.86) 100%
    ) !important;

  border: 1px solid rgba(255,255,255,.14) !important;
  border-radius: 24px !important;

  backdrop-filter: blur(34px) saturate(165%) !important;
  -webkit-backdrop-filter: blur(34px) saturate(165%) !important;

  box-shadow:
    0 28px 80px rgba(0,0,0,.48),
    0 10px 30px rgba(0,0,0,.25),
    inset 0 1px 0 rgba(255,255,255,.15),
    inset 0 -1px 0 rgba(255,255,255,.035) !important;

  overflow: hidden;
}

.uw-liquid-glass-modal::before {
  content: "";
  position: absolute;
  inset: 0;
  height: auto !important;
  display: block !important;
  z-index: -2;
  pointer-events: none;

  background:
    radial-gradient(circle at 10% 0%, rgba(235,181,27,.16), transparent 34%),
    radial-gradient(circle at 92% 8%, rgba(255,255,255,.10), transparent 28%),
    linear-gradient(180deg, rgba(255,255,255,.05), transparent 34%) !important;
}

.uw-liquid-glass-modal::after {
  content: "";
  position: absolute;
  inset: 1px;
  z-index: -1;
  pointer-events: none;

  border-radius: 23px;
  border: 1px solid rgba(255,255,255,.045);

  background:
    linear-gradient(
      115deg,
      rgba(255,255,255,.045),
      transparent 34%,
      transparent 70%,
      rgba(235,181,27,.028)
    );
}

/* Header */
.uw-liquid-glass-modal .modal-header,
.uw-liquid-glass-modal .uw-note-modal-header {
  position: relative;
  z-index: 1;

  min-height: 58px;
  padding: 14px 17px !important;

  background:
    linear-gradient(
      180deg,
      rgba(255,255,255,.065),
      rgba(255,255,255,.018)
    ) !important;

  border: 0 !important;
  border-bottom: 1px solid rgba(255,255,255,.08) !important;
}

.uw-liquid-glass-modal .modal-header::after {
  content: "";
  position: absolute;
  left: 17px;
  right: 17px;
  bottom: -1px;
  height: 1px;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(235,181,27,.46),
      rgba(255,255,255,.12),
      transparent
    );
}

/* Corpo */
.uw-liquid-glass-modal .modal-body {
  position: relative;
  z-index: 1;
  color: #e8ebef;

  background:
    linear-gradient(
      180deg,
      rgba(255,255,255,.012),
      rgba(0,0,0,.035)
    ) !important;
}

/* Footer */
.uw-liquid-glass-modal .modal-footer,
.uw-liquid-glass-modal .uw-note-modal-footer {
  position: relative;
  z-index: 1;

  gap: 8px;
  padding: 13px 16px !important;

  background:
    linear-gradient(
      0deg,
      rgba(0,0,0,.10),
      rgba(255,255,255,.018)
    ) !important;

  border: 0 !important;
  border-top: 1px solid rgba(255,255,255,.075) !important;
}

/* Titoli */
.uw-liquid-glass-modal .uw-note-modal-title,
.uw-liquid-glass-modal .modal-title {
  color: #f7f7f4 !important;
  font-size: .88rem !important;
  font-weight: 800 !important;
  letter-spacing: .48px;
  text-transform: uppercase;
  text-shadow: 0 1px 14px rgba(0,0,0,.25);
}

.uw-liquid-glass-modal .uw-section-icon {
  width: 28px;
  height: 28px;
  border-radius: 9px;

  color: #f3c43c !important;

  background:
    linear-gradient(
      145deg,
      rgba(235,181,27,.20),
      rgba(235,181,27,.065)
    ) !important;

  border: 1px solid rgba(235,181,27,.28);

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.10),
    0 8px 20px rgba(0,0,0,.15);
}

/* X chiusura */
.uw-liquid-glass-modal .btn-close {
  width: 32px;
  height: 32px;
  padding: 0 !important;
  margin: 0 !important;

  border-radius: 50%;

  opacity: .82;
  filter: invert(1) grayscale(1) brightness(1.65);

  background-size: 10px;
  background-color: rgba(255,255,255,.055);

  border: 1px solid rgba(255,255,255,.10);

  transition:
    transform .18s ease,
    background .18s ease,
    opacity .18s ease;
}

.uw-liquid-glass-modal .btn-close:hover {
  opacity: 1;
  transform: scale(1.06);
  background-color: rgba(255,255,255,.11);
}

/* Label */
.uw-liquid-glass-modal .uw-label,
.uw-liquid-glass-modal .form-label {
  color: #9fa5af !important;
  font-size: .67rem !important;
  font-weight: 800 !important;
  letter-spacing: .48px;
}

/* Input / Select / Textarea */
.uw-liquid-glass-modal .uw-input,
.uw-liquid-glass-modal .form-control,
.uw-liquid-glass-modal .form-select {
  color: #f3f5f7 !important;

  background:
    linear-gradient(
      180deg,
      rgba(255,255,255,.075),
      rgba(255,255,255,.035)
    ) !important;

  border: 1px solid rgba(255,255,255,.105) !important;
  border-radius: 12px !important;

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.055),
    0 5px 14px rgba(0,0,0,.08) !important;

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  transition:
    border-color .16s ease,
    background .16s ease,
    box-shadow .16s ease,
    transform .16s ease;
}

.uw-liquid-glass-modal .form-control::placeholder {
  color: rgba(217,221,227,.38) !important;
}

.uw-liquid-glass-modal .uw-input:hover:not(:disabled),
.uw-liquid-glass-modal .form-control:hover:not(:disabled),
.uw-liquid-glass-modal .form-select:hover:not(:disabled) {
  border-color: rgba(255,255,255,.18) !important;
  background: rgba(255,255,255,.075) !important;
}

.uw-liquid-glass-modal .uw-input:focus,
.uw-liquid-glass-modal .form-control:focus,
.uw-liquid-glass-modal .form-select:focus {
  color: #fff !important;
  background: rgba(255,255,255,.09) !important;
  border-color: rgba(235,181,27,.50) !important;

  box-shadow:
    0 0 0 3px rgba(235,181,27,.105),
    inset 0 1px 0 rgba(255,255,255,.075) !important;
}

.uw-liquid-glass-modal .form-select option {
  color: #f5f6f7;
  background: #22262e;
}

.uw-liquid-glass-modal .form-control:disabled,
.uw-liquid-glass-modal .form-select:disabled,
.uw-liquid-glass-modal .uw-input:disabled {
  opacity: .48;
  cursor: not-allowed;
}

/* Pulsanti */
.uw-liquid-glass-modal .uw-action-btn {
  min-height: 35px;
  color: #e8ebef !important;

  background:
    linear-gradient(
      180deg,
      rgba(255,255,255,.075),
      rgba(255,255,255,.032)
    ) !important;

  border: 1px solid rgba(255,255,255,.11) !important;
  border-radius: 11px !important;

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.07),
    0 7px 18px rgba(0,0,0,.12);

  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  transition:
    transform .16s ease,
    border-color .16s ease,
    background .16s ease,
    box-shadow .16s ease;
}

.uw-liquid-glass-modal .uw-action-btn:hover:not(:disabled) {
  color: #fff !important;
  transform: translateY(-1px);
  border-color: rgba(235,181,27,.30) !important;

  background:
    linear-gradient(
      180deg,
      rgba(235,181,27,.11),
      rgba(255,255,255,.045)
    ) !important;

  box-shadow:
    0 9px 22px rgba(0,0,0,.18),
    inset 0 1px 0 rgba(255,255,255,.09);
}

.uw-liquid-glass-modal .uw-action-btn.uw-action-solid {
  color: #111419 !important;

  background:
    linear-gradient(
      180deg,
      #f1c747 0%,
      #ebb51b 52%,
      #c99409 100%
    ) !important;

  border-color: rgba(255,222,113,.74) !important;

  box-shadow:
    0 9px 24px rgba(235,181,27,.18),
    inset 0 1px 0 rgba(255,255,255,.45) !important;
}

.uw-liquid-glass-modal .uw-action-btn.uw-action-solid:hover:not(:disabled) {
  color: #0c0e12 !important;

  background:
    linear-gradient(
      180deg,
      #f7d566 0%,
      #efbd2e 56%,
      #d6a20d 100%
    ) !important;
}

.uw-liquid-glass-modal .uw-action-btn.uw-action-danger {
  color: #ff9d9d !important;
  border-color: rgba(255,92,92,.18) !important;
  background: rgba(255,67,67,.055) !important;
}

.uw-liquid-glass-modal .uw-action-btn.uw-action-danger:hover:not(:disabled) {
  color: #ffc0c0 !important;
  border-color: rgba(255,92,92,.34) !important;
  background: rgba(255,67,67,.11) !important;
}

/* Box e riepiloghi interni */
.uw-liquid-glass-modal .uw-draft-hint,
.uw-liquid-glass-modal .uw-infinity-address-box,
.uw-liquid-glass-modal .uw-infinity-contact-check,
.uw-liquid-glass-modal .uw-infinity-summary > div,
.uw-liquid-glass-modal .uw-infinity-merge-row,
.uw-liquid-glass-modal .uw-duplicate-warning,
.uw-liquid-glass-modal .uw-note-detail-meta,
.uw-liquid-glass-modal .uw-note-detail-body {
  background:
    linear-gradient(
      145deg,
      rgba(255,255,255,.050),
      rgba(255,255,255,.020)
    ) !important;

  border-color: rgba(255,255,255,.09) !important;

  box-shadow:
    inset 0 1px 0 rgba(255,255,255,.035);
}

/* Tabelle dentro i modal */
.uw-liquid-glass-modal .uw-search-results,
.uw-liquid-glass-modal .uw-table-wrap {
  overflow: auto;
  background: rgba(8,10,14,.20) !important;
  border: 1px solid rgba(255,255,255,.075) !important;
  border-radius: 13px !important;
}

.uw-liquid-glass-modal .uw-table {
  color: #dde1e6 !important;
  background: transparent !important;
}

.uw-liquid-glass-modal .uw-table thead th {
  color: #9ea4ad !important;
  background: rgba(255,255,255,.035) !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}

.uw-liquid-glass-modal .uw-table td {
  color: #dde1e6 !important;
  background: transparent !important;
  border-color: rgba(255,255,255,.045) !important;
}

.uw-liquid-glass-modal .uw-table tbody tr {
  transition: background .15s ease;
}

.uw-liquid-glass-modal .uw-table tbody tr:hover {
  background: rgba(235,181,27,.065) !important;
}

.uw-liquid-glass-modal .text-muted {
  color: #8d939d !important;
}

.uw-liquid-glass-modal .text-light {
  color: #eef0f3 !important;
}

.uw-liquid-glass-modal .uw-search-empty {
  color: #9299a3;
}

/* Scrollbar */
.uw-liquid-glass-modal * {
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,.16) transparent;
}

.uw-liquid-glass-modal *::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

.uw-liquid-glass-modal *::-webkit-scrollbar-track {
  background: transparent;
}

.uw-liquid-glass-modal *::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,.15);
  border-radius: 999px;
}

.uw-liquid-glass-modal *::-webkit-scrollbar-thumb:hover {
  background: rgba(235,181,27,.30);
}

@media (max-width: 768px) {
  .modal-dialog.uw-liquid-glass-dialog,
  .modal-dialog.uw-postit-modal-dialog.uw-liquid-glass-dialog,
  .modal-dialog.modal-lg.uw-liquid-glass-dialog {
    width: calc(100% - 22px) !important;
    max-width: none !important;
    min-width: 0 !important;

    left: 50% !important;
    top: 50% !important;
    margin: 0 !important;
    transform: translate(-50%, -50%) !important;
  }

  .uw-liquid-glass-modal {
    border-radius: 20px !important;
  }

  .uw-liquid-glass-modal::after {
    border-radius: 19px;
  }

  .uw-liquid-glass-modal .modal-header,
  .uw-liquid-glass-modal .uw-note-modal-header {
    min-height: 54px;
    padding: 12px 13px !important;
  }

  .uw-liquid-glass-modal .modal-body {
    padding: 13px !important;
  }

  .uw-liquid-glass-modal .modal-footer,
  .uw-liquid-glass-modal .uw-note-modal-footer {
    padding: 11px 12px !important;
  }

  .uw-liquid-glass-modal .uw-action-btn {
    flex: 1 1 auto;
    justify-content: center;
  }
}


      `}</style>
    </div>
  );
};

export default AddContatti;